import React, { createContext, useContext, useState, ReactNode } from "react";

type Language = "en" | "nl" | "ar";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: <T = any>(key: string) => T;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

// Translation keys - you can expand this with all your text
export const translations = {
  en: {
    // Navigation
    nav: {
      home: "Home",
      about: "About us",
      insideus: "Inside Mini Adventurers",
      vision: "Growing and improving",
      whatWeOffer: "What we offer",

      // What We Offer
      services: "Childcare programs",
      transportationPolicy: "Transport & pick-up services",
      nutrition: "Healthy meals & nutrition",
      ipadSystem: "Digital tools & daily updates",

      ourDay: "Our day",
      learningGrowth: "Learning & growth",
      familyConnection: "Family connection",
      healthSafety: "Health & safety",

      // More - Column 1
      dailyRoutine: "Daily routines",
      emotionalSafety: "Emotional safety",
      activities: "Mealtimes & activities",

      // More - Column 2
      horizontalGroups: "Age group structure",
      educationalStaff: "Education staff",
      receivingPolicy: "Settling-in policy",

      // More - Column 3
      parentCollaboration: "Parent communication",
      faq: "FAQ",

      // More - Column 4
      safetyHygiene: "Hygiene",
      openingClosingChecklist: "Opening/Closing",

      // Other
      contact: "Contact us",
      register: "Register",
      registerChild: "Register child",
      registerTour: "Register tour",
      more: "More",
      login: "Login",
      dashboard: "Dashboard",
      logout: "Logout",
    },

    openingClosingChecklist: {
      title: "Opening and closing checklist",
      subtitle: "Structured daily procedures for safety and continuity",
      intro:
        "At Mini Avonturiers, we believe it's important that every day begins and ends in a careful, safe, and structured way. That's why we use a standard checklist for opening and closing, which every childcare worker follows. This checklist ensures that all necessary checks and preparations are completed before the children arrive and after the last one has been picked up.",
      intro2:
        "By systematically following this procedure, we guarantee safety, hygiene, peace and continuity in our care.",

      section1: {
        title: "Purpose of the checklist",
        description: "The checklist serves several purposes:",
        purposes: [
          "Ensure physical security of the space and materials.",
          "Ensuring a hygienic environment for a healthy start to the day.",
          "Predictable and calm start for children and employees.",
          "Clear handover between early and late shifts.",
          "Accountability instrument for managers and Municipal Health Service.",
        ],
        note: "Each employee signs off on what has been done, so that there is control, clarity and transferability within the team.",
      },

      section2: {
        title: "Opening checklist (morning)",
        description:
          "Before the start of childcare (before 7:00), the employee carries out the following steps:",
        subsections: {
          safety: {
            title: "General safety",
            points: [
              "Check that the building is properly closed (no open windows/doors).",
              "Activate indoor and outdoor lighting as needed.",
              "Checking the alarm system upon entry.",
            ],
          },
          firstAid: {
            title: "First aid and hygiene",
            points: [
              "Checking the first aid kit (presence, expiration date of materials).",
              "Check that there are sufficient diapers, cleaning wipes and gloves available.",
              "Check changing room: clean, tidy, sufficient materials.",
            ],
          },
          cleaning: {
            title: "Cleaning and ventilation",
            points: [
              "Ventilate rooms upon entering (open windows/doors on dry days).",
              "Visually check the kitchen, toilets and group for cleanliness.",
              "Check if trash bins have been emptied.",
              "Start up air circulation system if applicable.",
            ],
          },
          materials: {
            title: "Game materials & furnishings",
            points: [
              "Check play areas and tables for safety (no loose parts or sharp edges).",
              "Check age range: are suitable materials available?",
              "Replenish toys as needed and remove soiled materials.",
            ],
          },
          preparation: {
            title: "Preparing for transfer",
            points: [
              "Prepare tablet/iPad for parent communication.",
              "Review group lists and details from the previous day.",
              "Mentors identify any areas of concern.",
            ],
          },
        },
      },

      section3: {
        title: "Closing checklist (End of Day)",
        description:
          "After picking up the last child, the following checklist is completed:",
        subsections: {
          childControl: {
            title: "Child control and transfer",
            points: [
              "Check that all children have been unsubscribed through the system. Parent contacts have been completed or recorded.",
              "Any special details are noted for the following day.",
            ],
          },
          spacePreparation: {
            title: "Make the space ready for closing",
            points: [
              "Cleaning tables and floors, putting away toys.",
              "Clean up the kitchen, empty the trash cans.",
              "Check and replenish toilets and changing facilities.",
            ],
          },
          technology: {
            title: "Technology and building",
            points: [
              "Check windows: closed and locked.",
              "Check refrigerator: cover products, check temperature.",
              "Turn off appliances (lamps, tablet, coffee maker).",
              "Activate alarm system, lock front door.",
            ],
          },
        },
        note: "Every employee who closes the office signs off and notes any details (such as remaining inventory, forgotten items, or technical malfunctions). At the end of the week, there's a special focus on inventory management and ordering.",
      },

      section4: {
        title: "Responsibility and follow-up",
        points: [
          "Each employee is responsible for correctly and completely completing the checklist.",
          "Manager checks output on a random basis (e.g. first aid supplies).",
          "Deviations, damage or unsafe situations are immediately reported to the location manager.",
          "Checklist is kept digitally or on paper per half-day (archiving obligation due to GGD).",
        ],
      },
    },

    safetyHygiene: {
      hero: {
        title: "Safety, hygiene & atmosphere",
        subtitle:
          "Creating a secure, clean, and nurturing environment for every child",
      },
      intro: {
        title: "Your child's safety is our priority",
        text1:
          "At Mini Avonturiers, we maintain the highest standards of safety, hygiene, and atmosphere to ensure every child feels secure, healthy, and happy in our care.",
        text2:
          "Our comprehensive protocols and carefully designed spaces create an environment where children can thrive, learn, and grow with confidence.",
        commitmentTitle: "Our commitment",
        commitments: [
          "Strict safety protocols",
          "Regular hygiene practices",
          "Peaceful learning environment",
          "Professional staff training",
        ],
      },
      physicalSafety: {
        title: "Physical safety and GGD guidelines",
        subtitle:
          "We adhere to strict GGD (Municipal Health Service) guidelines to ensure the highest standards of physical safety for all children in our care.",
        complianceTitle: "GGD compliance",
        inspectionsTitle: "Regular inspections",
        inspections: [
          "Annual GGD safety inspections",
          "Monthly internal safety audits",
          "Continuous staff safety training",
          "Emergency procedure reviews",
        ],
        standardsTitle: "Safety standards",
        standards: [
          "Age-appropriate equipment and materials",
          "Secure building access and exits",
          "Proper staff-to-child ratios",
          "First aid certified staff",
        ],
      },
      safetyGuidelines: [
        {
          title: "Building safety",
          description:
            "All facilities meet strict GGD safety standards with regular inspections",
          icon: "🏢",
          color: "bg-blue-500",
        },
        {
          title: "Equipment safety",
          description:
            "All toys and equipment are age-appropriate and regularly checked",
          icon: "🧸",
          color: "bg-green-500",
        },
        {
          title: "Emergency procedures",
          description: "Comprehensive emergency protocols and trained staff",
          icon: "🚨",
          color: "bg-red-500",
        },
        {
          title: "Supervision",
          description:
            "Constant supervision with appropriate staff-to-child ratios",
          icon: "👁️",
          color: "bg-purple-500",
        },
      ],
      hygiene: {
        title: "Hygiene protocols",
        subtitle:
          "Comprehensive hygiene practices ensure a healthy environment for all children and staff.",
      },
      hygieneProtocols: [
        {
          title: "Daily cleaning",
          description:
            "Thorough daily cleaning of all surfaces, toys, and equipment",
          icon: "🧹",
          details: [
            "Disinfection of high-touch surfaces",
            "Regular toy cleaning and rotation",
            "Sanitization of eating areas",
          ],
          color: "bg-green-500",
        },
        {
          title: "Personal hygiene",
          description:
            "Teaching and maintaining proper handwashing and hygiene habits",
          icon: "🧼",
          details: [
            "Frequent handwashing routines",
            "Proper diaper changing procedures",
            "Illness prevention protocols",
          ],
          color: "bg-blue-500",
        },
        {
          title: "Food safety",
          description: "Strict food handling and preparation safety standards",
          icon: "🍽️",
          details: [
            "Safe food storage and preparation",
            "Allergy awareness and management",
            "Clean kitchen and dining areas",
          ],
          color: "bg-amber-500",
        },
        {
          title: "Illness management",
          description:
            "Clear protocols for managing illness and preventing spread",
          icon: "🏥",
          details: [
            "Sick child policies",
            "Communication with parents",
            "Return-to-care guidelines",
          ],
          color: "bg-purple-500",
        },
      ],
      spaces: {
        title: "Educationally equipped spaces",
        subtitle:
          "Our spaces are thoughtfully designed to support learning, development, and safety for children of all ages.",
      },
      educationalSpaces: [
        {
          title: "Age-appropriate design",
          description:
            "Spaces designed specifically for different developmental stages",
          icon: "🎯",
          color: "bg-amber-500",
          features: [
            "Safe play areas for each age group",
            "Appropriate furniture and equipment",
            "Accessible materials and resources",
          ],
        },
        {
          title: "Learning zones",
          description:
            "Dedicated areas for different types of learning activities",
          icon: "📚",
          color: "bg-blue-500",
          features: [
            "Quiet reading corners",
            "Creative art stations",
            "Active play areas",
            "Sensory exploration spaces",
          ],
        },
        {
          title: "Outdoor spaces",
          description:
            "Safe, stimulating outdoor environments for play and learning",
          icon: "🌳",
          color: "bg-green-500",
          features: [
            "Age-appropriate playground equipment",
            "Natural play elements",
            "Shaded areas and weather protection",
            "Secure fencing and supervision",
          ],
        },
        {
          title: "Technology integration",
          description: "Thoughtful use of technology to enhance learning",
          icon: "💻",
          color: "bg-purple-500",
          features: [
            "Educational apps and programs",
            "Digital documentation tools",
            "Parent communication platforms",
            "Screen time guidelines",
          ],
        },
      ],
      atmosphere: {
        title: "Peace and order in atmosphere",
        subtitle:
          "Creating a calm, orderly environment that promotes learning, emotional well-being, and positive behavior.",
        calmTitle: "Creating Calm",
        calmPoints: [
          "Soft lighting and natural materials",
          "Quiet zones for rest and reflection",
          "Gentle transitions between activities",
          "Positive behavior reinforcement",
        ],
        orderTitle: "Maintaining order",
        orderPoints: [
          "Clear routines and expectations",
          "Organized storage and play areas",
          "Consistent rules and boundaries",
          "Staff modeling of calm behavior",
        ],
      },
      atmosphereElements: [
        {
          title: "Calm environment",
          description:
            "Creating a peaceful, stress-free atmosphere for children",
          icon: "😌",
          color: "bg-blue-500",
        },
        {
          title: "Structured routines",
          description:
            "Predictable schedules that provide security and stability",
          icon: "📅",
          color: "bg-green-500",
        },
        {
          title: "Positive interactions",
          description:
            "Warm, supportive relationships between staff and children",
          icon: "🤗",
          color: "bg-amber-500",
        },
        {
          title: "Orderly spaces",
          description:
            "Well-organized environments that promote focus and calm",
          icon: "🗂️",
          color: "bg-purple-500",
        },
      ],
      cta: {
        title: "Experience our safe environment",
        subtitle:
          "See firsthand how we maintain the highest standards of safety, hygiene, and atmosphere. Schedule a visit to tour our facilities and meet our team.",
        button: "Learn more About Our Safety Standards",
      },
    },

    parentCollaboration: {
      hero: {
        title: "Collaboration with parents & trust",
        subtitle: "Building strong partnerships for your child's development",
      },
      intro: {
        title: "Our partnership vision",
        text1:
          "At Mini Avonturiers, we view parents as the first and most important educators in their child's life. We recognize that parents know their children better than anyone else.",
        text2:
          "Therefore, we strive for open, transparent, and equal collaboration, based on mutual trust, respect, and dialogue. For us, parents are partners in the child's development.",
        supportTitle: "Why partnership matters",
        supportText:
          "Effective collaboration between childcare professionals and parents not only strengthens a child's sense of security but also ensures richer coordination and continuity in parenting and guidance.",
      },
      pillars: {
        title: "Our vision: raising children is a joint effort",
        subtitle:
          "Our approach aligns with the principles of the pedagogical partnership, as outlined in the pedagogical frameworks of the NJi and the Childcare Act.",
      },
      partnershipPillars: [
        {
          title: "Equality",
          description:
            "Parents and professionals each have their own expertise.",
          icon: "🤝",
          color: "bg-amber-500",
        },
        {
          title: "Mutual trust",
          description:
            "An open attitude is the basis for constructive cooperation.",
          icon: "💝",
          color: "bg-rose-500",
        },
        {
          title: "Transparent communication",
          description: "Clear, timely and in understandable language.",
          icon: "💬",
          color: "bg-blue-500",
        },
        {
          title: "Participation & involvement",
          description:
            "Parents have influence, provide input and are actively involved.",
          icon: "👥",
          color: "bg-green-500",
        },
      ],
      practice: {
        title: "Practice design",
        subtitle:
          "Four key areas where we put our partnership vision into practice",
        featuresLabel: "What this includes:",
      },
      practiceAreas: [
        {
          title: "Daily transfer & iPad system",
          subtitle: "Warm handovers and digital transparency",
          icon: "📱",
          description:
            "Every day, we ensure a warm and welcoming handover when dropping off and picking up children. Parents are informed about the day's schedule, mealtimes, naps, any special circumstances, and their child's behavior.",
          features: [
            "Receive photos and updates about activities",
            "Gain insight into sleeping and eating moments",
            "Be able to exchange messages with the educational staff member",
            "Access to the schedule, observations and details",
          ],
        },
        {
          title: "Mentor-parent conversations",
          subtitle: "Personalized development tracking",
          icon: "👨‍👩‍👧‍👦",
          description:
            "Each child has a designated mentor: a childcare professional responsible for the child's development and well-being. Mentor meetings are held at least twice a year (or more often if desired).",
          features: [
            "The development on social, motor, cognitive and emotional levels is discussed",
            "Observations are shared",
            "There is room for questions, concerns or wishes from parents",
            "Any follow-up actions will be discussed",
          ],
        },
        {
          title: "Low-threshold communication",
          subtitle: "Open-door policy and accessible support",
          icon: "🚪",
          description:
            "We have an open-door policy. Parents are always welcome to drop in with questions, short conversations, or to share something.",
          features: [
            "Open-door policy for parents",
            "Trained staff in accessible communication",
            "Active listening and solution-oriented approach",
            "Dedicated contact persons on-site",
          ],
        },
        {
          title: "Parent meetings & brainstorming",
          subtitle: "Regular engagement and shared learning",
          icon: "📅",
          description:
            "We organize parent meetings at least twice a year. These meetings can be informative or social.",
          features: [
            "Theme evenings about education and development",
            "Viewing evenings in the group with explanations",
            "Brainstorming sessions about policy and methods",
            "Social events to build community",
          ],
        },
      ],
      feedback: {
        title: "Feedback culture",
        subtitle:
          "We are constantly developing and are open to feedback. Parents can share suggestions, concerns, or compliments in several ways:",
        note: "Feedback is not seen as criticism, but as valuable input to continuously improve our quality.",
      },
      feedbackMethods: [
        {
          title: "Personal conversation",
          description: "Direct communication with educational staff member",
          icon: "💭",
        },
        {
          title: "Digital parent portal",
          description: "Online feedback through our digital system",
          icon: "💻",
        },
        {
          title: "Evaluation moments",
          description: "Structured feedback during conversations",
          icon: "📊",
        },
        {
          title: "Parent questionnaires",
          description: "Regular satisfaction surveys and feedback forms",
          icon: "📝",
        },
      ],
      committee: {
        title: "Role of the parent committee",
        subtitle:
          "Our parent committee plays an active role within the childcare organization",
        activeRole: "Active advisory role",
        text1:
          "The committee functions as a sounding board and voice of parents, with whom we jointly strive for quality and involvement.",
        text2:
          "They advise on key areas that directly impact your child's experience and your family's needs.",
        areasTitle: "Areas of advisory:",
      },
      parentCommitteeAreas: [
        "The pedagogical policy",
        "Food, safety and health",
        "Opening hours and prices",
        "Complaints procedure and parent communication",
      ],
      cta: {
        title: "Ready to start our partnership",
        subtitle:
          "Join our community of parents and educators working together for your child's optimal development. Schedule a meeting to learn more about our collaborative approach.",
        button: "Schedule a meeting",
      },
    },

    ipadSystem: {
      hero: {
        title: "iPad system & parent communication",
        subtitle: "Stay connected with your child's daily adventures",
      },
      intro: {
        title: "Digital connection for peace of mind",
        text1:
          "At Mini Avonturiers, we value open, transparent, and engaged communication with parents. We understand that leaving their child behind isn't always easy for parents.",
        text2:
          "That's why we use a modern, secure, and user-friendly iPad system with a parent app, which keeps parents updated on their child's well-being in real time—even remotely.",
        supportTitle: "Digital support, not replacement",
        supportText:
          "This digital support is not a replacement for personal contact, but a valuable addition that contributes to trust, involvement and coordination between shelter and home.",
      },
      appFeatures: {
        title: "What can parents expect from the app?",
        subtitle:
          "The parent app gives parents daily access to comprehensive information about their child's day.",
        whatYouSee: "What you'll see:",
        items: [
          {
            title: "Photos and impressions",
            description:
              "Parents regularly receive photos of their child playing, doing crafts, spending time outdoors, or participating in themed activities.",
            icon: "📸",
            color: "bg-pink-500",
            benefits: [
              "Visual insight into group life",
              "Reinforces sense of involvement",
              "Captures special moments",
              "Shows daily activities and themes",
            ],
          },
          {
            title: "Daily reports",
            description:
              "At the end of the day, educational staff post a short report describing activities, atmosphere, and notable moments.",
            icon: "📝",
            color: "bg-indigo-500",
            benefits: [
              "What the children did (activities, theme)",
              "The atmosphere in the group",
              "Notable moments or observations",
              "Helps parents talk to their child about the day",
            ],
          },
          {
            title: "Mealtimes",
            description:
              "Insight into what and how much the child has eaten, including any special circumstances.",
            icon: "🍽️",
            color: "bg-green-500",
            benefits: [
              "What and how much the child has eaten",
              "Any special circumstances (lack of appetite, allergies)",
              "Dietary preferences and restrictions",
              "Nutritional tracking and coordination",
            ],
          },
          {
            title: "Sleep schedules",
            description:
              "Parents can see when their child slept, for how long and how it woke up.",
            icon: "😴",
            color: "bg-blue-400",
            benefits: [
              "When their child slept",
              "How long they slept",
              "How they woke up",
              "Valuable for coordinating home rhythms",
            ],
          },
          {
            title: "Message function",
            description:
              "Parents can send messages to the group and vice versa for questions, short messages, or comments.",
            icon: "💬",
            color: "bg-yellow-500",
            benefits: [
              "Direct communication with staff",
              "Questions and quick updates",
              "Not a replacement for conversation",
              "Handy addition for the little things",
            ],
          },
        ],
      },
      whyChoose: {
        title: "Why do we choose this?",
        subtitle:
          "Our digital communication system is designed with four key principles in mind",
        items: [
          {
            title: "Transparency and trust",
            description:
              "Parents don't have to guess how their day went. They're constantly informed, which contributes to trust in the care and peace of mind during working hours.",
            icon: "🛡️",
            color: "bg-amber-500",
          },
          {
            title: "Timely signaling and coordination",
            description:
              "We can inform parents immediately about any changes in behavior, appetite, or mood. Parents can also use the app to let us know important information.",
            icon: "⚡",
            color: "bg-blue-500",
          },
          {
            title: "Encourage engagement",
            description:
              "The app helps parents stay actively involved in their child's development. By monitoring, observing, and responding to the day's activities.",
            icon: "🤝",
            color: "bg-green-500",
          },
          {
            title: "Documentation and portability",
            description:
              "All reports, observations, and messages are available for review. This ensures continuity of guidance and provides valuable input for meetings.",
            icon: "📊",
            color: "bg-purple-500",
          },
        ],
      },
      privacy: {
        title: "Privacy and security",
        subtitle:
          "We work exclusively with systems that meet the highest standards for privacy, security, and data protection (GDPR-compliant).",
        items: [
          {
            title: "GDPR compliant",
            description:
              "We work exclusively with systems that meet the highest standards for privacy, security, and data protection.",
            icon: "🔒",
            color: "bg-red-500",
          },
          {
            title: "Secure access",
            description:
              "Only the parents of the child in question have access to the information.",
            icon: "👤",
            color: "bg-blue-600",
          },
          {
            title: "No public sharing",
            description:
              "Photos and data are not shared publicly and are retained for the legally required period.",
            icon: "🚫",
            color: "bg-gray-700",
          },
          {
            title: "Trained staff",
            description:
              "Our employees are trained in careful reporting and digital communication, with attention to respect, neutrality and professionalism.",
            icon: "👨‍🏫",
            color: "bg-teal-500",
          },
        ],
        note: "Only the parents of the child in question have access to the information. Photos and data are not shared publicly and are retained for the legally required period.",
      },
      technical: {
        title: "Professional training & standards",
        text1:
          "Our employees are trained in careful reporting and digital communication, with attention to respect, neutrality and professionalism.",
        text2:
          "All reports, observations, and messages are available for review. This ensures continuity of guidance and provides valuable input for parent-teacher meetings, evaluations, and mentor meetings.",
        benefitsTitle: "Key benefits:",
        benefits: [
          "Real-time updates and transparency",
          "Secure, GDPR-compliant platform",
          "Professional staff training",
          "Comprehensive documentation",
        ],
      },
      cta: {
        title: "Ready to stay connected?",
        subtitle:
          "Experience the peace of mind that comes with real-time updates about your child's daily adventures. Learn more about our digital communication system.",
        button: "Learn more About the App",
      },
    },

    // ENGLISH
    receivingPolicy: {
      hero: {
        title: "Policy: receiving & adjusting to new children",
        subtitle: "Creating a warm, safe foundation for every child's journey",
      },
      intro: {
        title: "A child-centered welcome",
        text1:
          "A child's first experiences at daycare have a significant impact on their sense of safety, trust, and attachment. At Mini Avonturiers, we therefore dedicate extra care and attention to welcoming and helping new children settle in.",
        text2:
          "We believe that every child is unique and should therefore be able to adjust to a new environment in their own way and at their own pace. That's why we use an individualized approach, tailored to the needs of both the child and the parents.",
        supportTitle: "Why this matters",
        supportText:
          "A good start creates a sense of calm for both the child and the parents, and lays the foundation for a positive relationship with the childcare staff and the group.",
      },
      warmIntro: {
        title: "Warm introduction: from the first contact",
        subtitle:
          "The adjustment process begins even before the first day of care. We build trust with parents and ensure the child can get acquainted with their new environment in a calm, loving atmosphere.",
        items: [
          {
            title: "Introduction & intake interview",
            description:
              "Before the first day of care, an intake interview will take place with parents.",
            features: [
              "Parents talk about the child: character, sleep pattern, nutrition",
              "Cuddles, preferences, language, allergies, attachment figures",
              "Special features and unique characteristics",
              "We discuss mutual expectations and our working methods",
            ],
            icon: "👨‍👩‍👧‍👦",
          },
          {
            title: "Guided tour",
            description:
              "During the tour we will show you the rooms, group and employees.",
            features: [
              "Show you the rooms, group and employees",
              "Parents and child can experience the atmosphere",
              "Meet the educational staff member(s) and any mentor",
              "This makes the location a little more familiar",
            ],
            icon: "🏠",
          },
        ],
      },
      phases: {
        title: "Getting used to things in phases: The child is central",
        subtitle:
          "A child starting with us is given the opportunity to gradually adjust. Depending on age, personality, home situation, and previous childcare experiences, we develop an adjustment plan.",
        items: [
          {
            day: "Day 1",
            duration: "Short",
            description: "Together with parent(s), max. 1 hour in the group",
            activities: "Quietly observing and getting to know each other",
            icon: "👀",
          },
          {
            day: "Day 2",
            duration: "Longer",
            description: "Parent stays briefly, child is left alone",
            activities: "Approximately 1.5–2 hours without parent",
            icon: "🤗",
          },
          {
            day: "Day 3",
            duration: "Half day",
            description: "Child remains alone, including activity",
            activities: "Possibly mealtime and structured activities",
            icon: "🎨",
          },
          {
            day: "Day 4+",
            duration: "Full day",
            description: "Build up to a full day",
            activities: "Depending on the child's needs and response",
            icon: "🌟",
          },
        ],
        note: "This phasing is not mandatory or rigid. Some children need more time, while others feel comfortable quickly. We coordinate this flexibly with parents and emphasize calm, safety, and emotional well-being.",
      },
      mentor: {
        title: "The role of the mentor: proximity and trust",
        subtitle:
          "Each child is assigned a permanent mentor from the start. This mentor provides focused presence and dedication that gives the child the opportunity to bond with an adult.",
        roles: [
          {
            title: "First point of contact",
            description:
              "The employee who actively observes, comforts, guides and instills confidence in the child.",
          },
          {
            title: "Safe relationship builder",
            description:
              "Responsible for building a safe relationship with the child.",
          },
          {
            title: "Process monitor",
            description:
              "The person who carefully monitors the adjustment process and communicates about this with the team and the parents.",
          },
          {
            title: "Extra closeness provider",
            description:
              "Offers the child extra closeness during the adjustment period.",
          },
        ],
        activitiesTitle: "Mentor activities include:",
        activities: [
          "Staying close during play",
          "Comforting on a lap",
          "Doing activities together",
          "Repeating familiar routines",
          "Using familiar objects",
        ],
        note: "This focused presence and dedication gives the child the opportunity to bond with an adult, which is essential for further development according to Bowlby's attachment theory.",
      },
      parents: {
        title: "Parents as partners in the adjustment period",
        subtitle:
          "We believe that parental involvement is crucial for the transition. We encourage parents to be trusting, but also to take their own emotions seriously.",
        items: [
          {
            title: "Active observation",
            description:
              "Parents are invited to actively observe and share their observations during the familiarisation moments.",
          },
          {
            title: "Easy communication",
            description:
              "Easy contact via the iPad system and verbally when picking up and dropping off.",
          },
          {
            title: "Open discussion",
            description:
              "We discuss openly how the child is doing, what signals we pick up and how we can make adjustments if necessary.",
          },
          {
            title: "Always available",
            description:
              "The mentor is always open to consultation, even outside of planned meetings.",
          },
        ],
        note: "Because when parents feel safe, their children feel safe too.",
      },
      aftercare: {
        title: "Aftercare and evaluation",
        subtitle:
          "The first few weeks are crucial. Everything is focused on ensuring the child feels safe, connected, and at home at Mini Avonturiers.",
        items: [
          {
            title: "Intensive monitoring",
            description:
              "The mentor continues to monitor the child intensively during the first few weeks.",
          },
          {
            title: "Informal evaluations",
            description:
              "Short, informal evaluation moments (verbal or via the app).",
          },
          {
            title: "6-week evaluation",
            description:
              "After approximately 6 weeks, a short evaluation and parent meeting will take place.",
          },
          {
            title: "Additional support",
            description:
              "If necessary, we provide additional support or extend the transition period.",
          },
        ],
        note: "After approximately 6 weeks, a short evaluation and parent meeting will take place to discuss the adjustment period, development and well-being.",
      },
      cta: {
        title: "Ready to start your child's journey",
        subtitle:
          "Experience our warm, child-centered approach to welcoming new families. Schedule an intake interview to begin your child's adjustment journey.",
        button: "Learn more About Our Settling Process",
      },
    },

    // Nutrition
    nutrition: {
      hero: {
        title: "Nutrition according to the wheel of five",
        subtitle:
          "Healthy, varied, and mindful nutrition for growing minds and bodies",
      },
      intro: {
        title: "More than just food",
        text1:
          "At Mini Avonturiers, we value healthy, varied, and mindful nutrition. Nutrition is essential not only for young children's physical development but also for their concentration, mood, energy balance, and for developing healthy eating habits for the future.",
        text2:
          "That's why we base our nutritional policy on the guidelines of the Netherlands Nutrition Centre and adhere to the principles of the Wheel of Five.",
        supportTitle: "Our approach",
        supportText:
          "We strive for a balanced diet, focusing on vegetables, fruit, whole grains, proteins, dairy, healthy fats, and sufficient fluids.",
      },
      wheelOfFive: {
        title: "The wheel of five",
        subtitle:
          "Our nutritional foundation based on the Netherlands Nutrition Centre guidelines",
        items: [
          {
            category: "Vegetables & fruit",
            description: "Fresh, colorful vegetables and fruits",
            icon: "🥬",
            color: "bg-green-500",
          },
          {
            category: "Whole grains",
            description: "Whole wheat bread, pasta, and brown rice",
            icon: "🌾",
            color: "bg-amber-500",
          },
          {
            category: "Proteins",
            description: "Legumes, eggs, fish, chicken, and alternatives",
            icon: "🥚",
            color: "bg-red-500",
          },
          {
            category: "Dairy",
            description: "Milk, cheese, and dairy products",
            icon: "🥛",
            color: "bg-blue-500",
          },
          {
            category: "Healthy fats",
            description: "Nuts, seeds, and healthy oils",
            icon: "🥑",
            color: "bg-purple-500",
          },
        ],
      },
      mealtime: {
        title: "Food is more than just nutrition: A social moment",
        subtitle:
          "At Mini Adventurers, mealtimes are more than just consuming food: they're also educational, social, and emotional learning opportunities.",
        note: "Our educational staff eat together at the table and serve as role models. They encourage children to try new flavors in a positive and non-coercive way, without pressure or reward.",
        items: [
          {
            title: "Social skills",
            description: "Eating together, sharing and waiting for each other",
            icon: "🤝",
          },
          {
            title: "Table manners",
            description: 'Learning to say "please" and "thank you"',
            icon: "🍽️",
          },
          {
            title: "Independence",
            description: "Learning to serve or choose for yourself",
            icon: "👶",
          },
          {
            title: "Food education",
            description: "Talking about food: colours, flavours, origins",
            icon: "🌍",
          },
        ],
      },
      baby: {
        title: "Babies: nutrition tailored to your needs",
        subtitle:
          "For babies, nutrition is fully tailored to their individual needs, in close consultation with the parents.",
        note: "We actively support parents in the transition to solid food, paying attention to allergies, preferences and dietary requirements.",
        items: [
          {
            title: "Following rhythm",
            description:
              "Following the child's own rhythm (no fixed mealtimes)",
            icon: "⏰",
          },
          {
            title: "Loving guidance",
            description:
              "Calm, loving guidance during feeding (on your lap, with eye contact)",
            icon: "💝",
          },
          {
            title: "Safe feeding",
            description:
              "Feed in a seated and safe manner, paying attention to satiety signals",
            icon: "🛡️",
          },
          {
            title: "Warm communication",
            description:
              "Warm communication about transition moments (e.g. first snacks)",
            icon: "💬",
          },
        ],
      },
      toddler: {
        title: "Toddlers & preschoolers: healthy, varied and independent",
        subtitle:
          "For older children (ages 1.5–4), we maintain a daily schedule with fixed mealtimes.",
        hotMealsTitle: "Hot Meals Include:",
        items: [
          {
            title: "Fruit moments",
            description:
              "Multiple varieties per day, cut and attractively presented",
            icon: "🍎",
            color: "bg-red-500",
          },
          {
            title: "Whole wheat options",
            description:
              "Whole wheat sandwiches or hot lunch (depending on the time of day)",
            icon: "🥪",
            color: "bg-amber-500",
          },
          {
            title: "Healthy snacks",
            description:
              "Bread sticks, cucumber, cherry tomatoes, whole wheat crackers",
            icon: "🥒",
            color: "bg-green-500",
          },
          {
            title: "Healthy drinks",
            description:
              "Water and lukewarm tea as standard, juice only on special occasions",
            icon: "💧",
            color: "bg-blue-500",
          },
        ],
        hotMealFeatures: [
          "Variety in vegetables (at least 2 types per meal)",
          "Whole wheat pasta, brown rice or potatoes",
          "Protein source (legumes, eggs, fish, chicken or vegetarian)",
          "No added sugars or artificial flavors",
          "Less salt (maximum 1 gram per year for young children)",
          "Sufficient fiber, proteins and healthy fats",
        ],
      },
      independence: {
        title: "Do it yourself, experience it yourself",
        subtitle:
          "We encourage children – in an age-appropriate way – to eat, choose, taste, and clean up after themselves.",
        note: "We make eating a moment of attention, rest and connection.",
        items: [
          {
            title: "Hold and pour",
            description: "Hold and pour their own cup (with help)",
            icon: "🥤",
          },
          {
            title: "Choose fruit",
            description: "Choose a piece of fruit from the plate yourself",
            icon: "🍓",
          },
          {
            title: "Clean up",
            description: "Learning to tidy away your own dishes",
            icon: "🧹",
          },
        ],
      },
      cultural: {
        title: "Culturally sensitive and inclusive",
        subtitle:
          "We recognize that eating habits and rituals can vary from family to family and culture to culture.",
        items: [
          {
            title: "Religious preferences",
            description:
              "Respect for vegetarian or religious dietary preferences",
            icon: "🕊️",
          },
          {
            title: "Cultural rituals",
            description: "Attention to fixed or symbolic mealtimes at home",
            icon: "🏠",
          },
          {
            title: "No pressure",
            description:
              "No general obligation to 'eat': children can indicate when they have had enough",
            icon: "🤗",
          },
        ],
      },
      educational: {
        title: "Educational value of nutrition",
        subtitle:
          "Nutrition is also a way to make children aware of where food comes from.",
        note: "With this we encourage understanding, involvement and healthy behaviour.",
        items: [
          {
            title: "Food preparation",
            description: "Cutting fruit or vegetables (under supervision)",
            icon: "🔪",
          },
          {
            title: "Learning numbers",
            description: "Counting pieces or naming colors",
            icon: "🔢",
          },
          {
            title: "Healthy choices",
            description:
              'Conversations about healthy choices: "What do you like? What\'s healthy?"',
            icon: "💭",
          },
          {
            title: "Garden activities",
            description:
              "Sometimes sowing and harvesting in a mini vegetable garden",
            icon: "🌱",
          },
        ],
      },
      cta: {
        title: "Ready to nourish your child's growth?",
        subtitle:
          "Experience our comprehensive approach to healthy nutrition and mealtime learning. Schedule a visit to see our nutritional program in action.",
        button: "Learn more About Our Nutrition",
      },
    },

    horizontalGroups: {
      hero: {
        title: "Horizontal groups",
        subtitle: "Age-appropriate care for optimal development",
        ageRange: "(0–4 years)",
      },

      intro: {
        title: "Our approach to age groups",
        text1:
          "At Mini Avonturiers, we've deliberately chosen to work with horizontal groups. This means that children of roughly the same age and developmental stage are grouped together.",
        text2:
          "Instead of a vertical group (where children aged 0 to 4 are mixed together), we divide the group into three stages, ensuring every child develops best when the environment is tailored to their specific needs, developmental stage and possibilities.",
        threeGroups: "Our Three Groups",
      },

      groupsDivisionTitle: "Our division group",
      groupsDivisionSubtitle:
        "Each group is carefully designed to meet the specific",
      whatChildrenReceive: "What children receive:",
      ourApproach: "Our approach:",
      groups: {
        baby: {
          title: "Baby group",
          subtitle: "± 0–1.5 years",
          description:
            "Trust, nurturing, and bonding are central. We follow each child's natural rhythm.",
          features: [
            "Individual attention and peace",
            "Lots of physical contact and responsive interaction",
            "Free space of movement (lying, rolling, crawling)",
            "Sensory material and soothing music",
          ],
          approach:
            "Our pedagogical staff are trained in sensitive responsiveness and the vision of Emmi Pikler, in which respect for the child and independent movement are central.",
        },
        toddler: {
          title: "Toddler group",
          subtitle: "± 1.5–2.5 years",
          description:
            "Exploration and autonomy development. Children want to do everything themselves.",
          features: [
            "Learning to express and regulate their emotions",
            "Developing motor skills (climbing, pushing, building)",
            "Getting started with language and simple communication",
            "Practice concepts such as waiting, sharing, saying 'no'",
          ],
          approach:
            "Our guidance focuses on affirming, guiding, and setting boundaries. We provide clear guidelines, address behavior, help with conflicts, and provide ample opportunity for repetition and independent learning.",
        },
        preschool: {
          title: "Preschool group",
          subtitle: "± 2.5–4 years",
          description:
            "Language explosion, social development, and symbolic play. Children crave challenge and interaction.",
          features: [
            "Playing together, learning to share, consult and role-play",
            "Discover cause-and-effect relationships and logic",
            "Are offered activities within themes",
            "Be prepared for the transition to primary school",
          ],
          approach:
            "Here, we incorporate language-rich interaction, circle time, structured play instruction, and role-playing. Our pedagogical staff encourage independence, vocabulary development, problem-solving skills, and self-confidence.",
        },
      },

      why: {
        title: "Why horizontal groups?",
        subtitle:
          "Our approach is based on solid pedagogical principles and developmental psychology",
        items: {
          safety: {
            title: "Safety & recognition",
            description:
              "Children in the same developmental stage have similar rhythms, needs, and abilities. We can fully tailor the environment to that stage.",
          },
          targeted: {
            title: "Targeted guidance",
            description:
              "Babies, toddlers, and preschoolers each have their own developmental goals. Educators can provide more targeted support.",
          },
          natural: {
            title: "Natural learning",
            description:
              "According to Jean Piaget, cognitive development occurs in stages. We align play and support with these natural learning processes.",
          },
        },
      },

      benefits: {
        title: "Benefits of our approach",
        subtitle:
          "Horizontal groups provide advantages for both children and staff",
        children: {
          title: "Benefits for children",
          subtitle: "Optimal development in age-appropriate environments",
          items: [
            "More peace and less stimuli, because there are no major age differences",
            "Activities are better tailored to developmental level",
            "Children feel competent and confident more quickly",
            "Space for development of independence, appropriate to the phase",
            "Better observation and guidance of behaviour, play and emotional development",
          ],
        },
        staff: {
          title: "Benefits for staff",
          subtitle: "Focused expertise and professional development",
          items: [
            "Focusing on one developmental phase leads to more pedagogical depth",
            "Easier structuring of the daily program",
            "Higher quality in game guidance and observation",
            "More peace and clarity in group dynamics",
            "Improved team collaboration through clear division of roles",
          ],
        },
      },

      transitions: {
        title: "Coherence and transitions",
        subtitle:
          "Although the children are in horizontal groups, we don't lose sight of continuity in development. Transitions to the next group are carefully prepared:",
        items: [
          "There is a warm transfer between pedagogical staff",
          "Parents are involved in the transition",
          "Children gradually get used to the new group",
          "The mentor remains involved throughout the transition",
        ],
        conclusion:
          "In this way we ensure that the child can progress to the next phase with confidence and without stress.",
      },

      cta: {
        title: "Ready to learn more?",
        subtitle:
          "Discover how our age-appropriate approach can benefit your child's development. Schedule a tour to see our horizontal groups in action.",
        button: "Learn more About Our Groups",
      },
    },

    // Brand
    brand: {
      name: "Mini Avonturiers",
      tagline: "Adventures start with love and learning.",
    },

    // Home page
    home: {
      welcome: "Welcome to Mini Adventurers!",
      subtitle:
        "At Mini Adventurers, everything revolves around discovery, growth, and fun. We offer a safe and trusted place where children feel comfortable and can develop at their own pace. For the little ones from 0 to 4 years, we have our daycare, where playing and learning go hand in hand. For children from 4 to 13 years, we provide our after-school care—a welcoming environment to relax after school, discover new things, and enjoy together. Our team is ready every day with care, warmth, and structure. This way, we ensure that every child has the space to explore their talents and grow up with confidence and joy.",
      registerChild: "Register Child",
      registerTour: "Register Tour",

      aboutUs: {
        title: "About us",
        welcomeCenter: {
          title: "Welcome to our center",
          description:
            "At Mini Avonturiers, our mission is to provide a safe, nurturing, and inspiring environment where every child is seen, heard, and supported in their own unique development. Through play, curiosity, and connection with nature, we empower children to grow into confident, happy individuals.",
        },
        growingImproving: {
          title: "Growing and improving",
          description:
            "We believe every child develops in their own way and at their own pace. Our vision is to guide this growth through emotional safety, structure, and freedom of expression. By creating a space full of discovery, warmth, and connection, we ensure children feel secure to explore the world around them, make choices, and become who they are meant to be.",
        },
      },

      services: {
        title: "Our services",
        subtitle:
          "Discover our comprehensive range of childcare services designed to support your child's growth and development.",
        items: {
          childcarePrograms: {
            title: "Childcare programs",
            features: [
              "Daily care",
              "Structured activities",
              "Development areas",
            ],
          },
          transportPickup: {
            title: "Transport & pick-up services",
            features: ["Safe routes", "GPS tracked", "Reliable timing"],
          },
          healthyMeals: {
            title: "Healthy meals & nutrition",
            features: [
              "Balanced diet",
              "Customizable menu",
              "Guided by nutritionists",
            ],
          },
          digitalTools: {
            title: "Digital tools & daily updates",
            features: [
              "Real-time updates",
              "Parent portal",
              "Seamless communication",
            ],
          },
          dailyRoutines: {
            title: "Daily routines",
            features: [
              "Structured schedule",
              "Playtime balance",
              "Rest periods",
            ],
          },
          emotionalSafety: {
            title: "Emotional safety",
            features: [
              "Safe environment",
              "Professional guidance",
              "Supportive staff",
            ],
          },
          mealtimesActivities: {
            title: "Mealtimes & activities",
            features: [
              "Engaging activities",
              "Mealtime variety",
              "Nutritional education",
            ],
          },
          ageGroupStructure: {
            title: "Age group structure",
            features: [
              "Tailored care",
              "Age-appropriate activities",
              "Specialist staff",
            ],
          },
          transitions: {
            title: "Educational staff",
            features: [
              "Educated staff",
              "Supportive transitions",
              "Continuous feedback",
            ],
          },
          settlingPolicy: {
            title: "Settling-in policy",
            features: [
              "Welcoming environment",
              "Settling assistance",
              "Parental support",
            ],
          },
          parentCommunication: {
            title: "Parent communication",
            features: [
              "Two-way communication",
              "Regular updates",
              "Community building",
            ],
          },
          hygiene: {
            title: "Hygiene",
            features: [
              "Clean facilities",
              "Regular checkups",
              "Health protocols",
            ],
          },
          openingClosing: {
            title: "Opening/Closing",
            features: [
              "Structured schedule",
              "Safety checks",
              "Efficient processes",
            ],
          },
        },
      },

      contactUs: "Contact us",
      learnMore: "Learn more",
      whyChooseUs: "Why choose us?",
      whyChooseUsSubtitle:
        "We provide a nurturing environment where children can grow, learn, and thrive",
      activities: "Our activities",
      activitiesSubtitle:
        "Engaging activities designed to develop your child's skills and creativity",
      viewDailyRoutine: "View daily routine",
      parentTestimonials: "What parents say",
      testimonialsSubtitle:
        "Hear from families who trust us with their children",
      readyToStart: "Ready to start your child's journey?",
      ctaSubtitle: "Join our family and give your child the best start in life",
      registerNow: "Register now",
      scheduleVisit: "Schedule a visit",
      ctaReady: "Ready to get started?",
      ctaDescription:
        "Contact us today to learn more about our programs and schedule a visit to our facility.",
      ctaScheduleVisit: "Schedule a visit",
      ctaDownloadBrochure: "Download brochure",
    },

    // About page
    about: {
      title: "About Mini Avonturiers",
      subtitle:
        "Learn more about our mission, values, and the team behind Mini Avonturiers",
      mission: "Our mission",
      missionText:
        "To provide a nurturing, safe, and stimulating environment where every child can grow, learn, and develop to their full potential. We believe in fostering curiosity, creativity, and confidence in every child.",
      vision: "Our vision",
      visionText:
        "To be the leading childcare provider in Utrecht, known for our innovative approach to early childhood education and our commitment to creating happy, healthy, and well-rounded children.",
      experience: "Years of experience",
      children: "Happy children",
      staff: "Qualified staff",
      satisfaction: "Parent satisfaction",
      values: "Our values",
      valuesList: {
        safety: "Safety first",
        learning: "Learning through play",
        respect: "Respect for every child",
        community: "Building community",
      },
      team: "Our team",
      teamText:
        "Meet our dedicated team of early childhood professionals who are passionate about your child's development and well-being.",
      ourImpact: "Our impact",
      principlesThatGuide: "The principles that guide everything we do",
      valuesDescriptions: {
        safety:
          "We prioritize the safety and well-being of every child in our care",
        learning:
          "Children learn best through play, exploration, and hands-on experiences",
        respect:
          "Every child is unique and deserves respect, love, and individual attention",
        community:
          "We build strong partnerships with families and the local community",
      },
      teamMembers: {
        sarahJohnson: {
          name: "Sarah Johnson",
          role: "Director & Founder",
          experience: "20+ years in early childhood education",
          description:
            "Passionate about creating nurturing environments where children can thrive and grow.",
        },
        mariaRodriguez: {
          name: "Maria Rodriguez",
          role: "Lead Teacher",
          experience: "15+ years teaching experience",
          description:
            "Specializes in creative arts and language development for young children.",
        },
        davidChen: {
          name: "David Chen",
          role: "Physical Education Specialist",
          experience: "12+ years in child development",
          description:
            "Focuses on motor skills development and healthy physical activities.",
        },
        lisaThompson: {
          name: "Lisa Thompson",
          role: "Nutrition Coordinator",
          experience: "10+ years in child nutrition",
          description:
            "Ensures healthy, balanced meals that support children's growth and development.",
        },
      },
      parentTestimonials: {
        emmaAndTom: {
          names: "Emma & Tom Wilson",
          child: "Lucas (4 years)",
          text: "Mini Avonturiers has been an amazing experience for our son. The staff truly care about each child's development and create such a warm, loving environment.",
        },
        annaAndMark: {
          names: "Anna & Mark Davis",
          child: "Sophie (3 years)",
          text: "We love how the center focuses on both learning and play. Sophie has grown so much in confidence and skills since joining.",
        },
        lisaAndJames: {
          names: "Lisa & James Brown",
          child: "Oliver (5 years)",
          text: "The communication with parents is excellent, and we always feel informed about our child's progress and daily activities.",
        },
      },
      ctaJoinFamily: "Join our family",
      ctaExperience:
        "Experience the difference that our dedicated team and proven approach can make for your child's development.",
      meetTeam: "Meet our dedicated team of early childhood professionals",
      whatParentsSayTitle: "What parents say",
      whatParentsSaySubtitle:
        "Hear from families who trust us with their children",
      scheduleVisit: "Schedule a visit",
      contactUs: "Contact us",
    },

    // Services page
    services: {
      title: "Our services",
      subtitle:
        "Comprehensive childcare solutions designed to meet your family's needs",
      daycare: "Full-day care",
      daycareDesc:
        "Complete childcare solution for working parents with flexible scheduling options.",
      afterSchool: "After-school care",
      afterSchoolDesc:
        "Safe and engaging after-school environment with homework support and activities.",
      holiday: "Holiday programs",
      holidayDesc:
        "Special programs during school holidays with themed activities and adventures.",
      flexible: "Flexible care",
      flexibleDesc:
        "Customizable care options to fit your family's unique schedule and needs.",
      learnMore: "Learn more",
      contactUs: "Contact us",
    },

    // Transportation Policy page
    transportationPolicy: {
      title: "Transportation : pick-up service by bus",
      subtitle:
        "Safe and reliable transportation service for school-aged children",
      intro:
        "At Mini Avonturiers, we offer a safe, reliable, and child-friendly pickup service for school-aged children using our own recognizable vans. This service is available for children attending our after-school care program (BSO) and enhances the continuity of care and convenience for parents.",
      intro2:
        "Transporting young children requires careful preparation, clear agreements, and constant attention to safety, comfort, and involvement. That's why we've developed a clear transport policy that meets all legal requirements and focuses on the child's well-being during transport.",

      section1: {
        title: "The Mini Adventurer bus: safe, recognizable and familiar",
        description: "Our vans are equipped with:",
        features: [
          "Recognisable corporate identity (logo and name Mini Adventurers), so that children and schools know who will be picking them up.",
          "Child seats and belts that meet the latest safety requirements.",
          "Approved vehicles that undergo annual technical inspection.",
          "Navigation and driver assistance systems for safe and efficient transportation.",
        ],
        note: "During transport, the applicable regulations for childcare transport are always complied with (in accordance with the Passenger Transport Act 2000 and GGD guidelines).",
      },

      section2: {
        title: "Qualified drivers with a child-oriented approach",
        description:
          "Our drivers are more than just transporters – they're the first point of contact after school and play a vital role in the transition from school to after-school care. That's why we set high standards:",
        requirements: [
          "Valid driving license B and certificate of good conduct (VOG).",
          "Basic training in child safety and pedagogical etiquette.",
          "Instruction on safe boarding, conducting conversations, and maintaining calm during transitions.",
          "Able to recognize signs of tension or fatigue in children.",
        ],
        note: "Our drivers have a calm, child-friendly attitude and, over time, build recognition and trust with the children.",
      },

      section3: {
        title: "On-site guidance and transfer",
        arrival: {
          title: "Upon arrival at school:",
          points: [
            "The driver reports to the school and waits at an agreed location.",
            "Children are checked against a list.",
            "There is contact with school staff in case of special circumstances (e.g. if a child is ill or something has happened).",
          ],
        },
        shelter: {
          title: "Upon arrival at the shelter:",
          points: [
            "The driver personally hands over the children to a childcare worker.",
            "The arrival time is recorded in the system.",
            "Any special details during the ride will be shared immediately.",
          ],
        },
        note: "This transfer ensures continuity, safety and information transfer between school, transport and childcare.",
      },

      section4: {
        title: "Real-time communication with parents",
        description:
          "Our parent app keeps parents informed about transport times. Parents receive:",
        features: [
          "Push notifications on departure and arrival.",
          "Ability to share questions or cancellations via the app.",
          "Direct contact with the daycare in case of delays or changes.",
        ],
        note: "This way we create transparency and trust, and parents can have their child transported with peace of mind.",
      },

      section5: {
        title: "Flexible collection routes and planning",
        description: "Our routes are efficient and child-friendly:",
        features: [
          "Short travel times, with preference for routes close to the reception location.",
          "Small groups per trip, so that there is peace and quiet on the bus.",
          "Adjustment for seasons or traffic congestion, if necessary.",
          "Fixed schedules with clear time blocks for each school location.",
        ],
        note: "When registering, we agree with parents which school the child will be picked up from, at what time and on which days.",
      },

      section6: {
        title: "Emergencies and backup",
        description: "For exceptional situations we apply a strict protocol:",
        features: [
          "In case of delay, the daycare center and the parent will be informed immediately.",
          "If a child does not appear at the agreed location, the school and parents will be contacted within 2 minutes.",
          "The driver is in contact with the shelter and has a list of emergency numbers.",
        ],
        note: "Our backup service ensures that there is always someone available to assist in the event of a breakdown, incident or unforeseen circumstance.",
      },
    },

    // Gallery page
    gallery: {
      title: "Photo Gallery",
      subtitle: "Take a peek into our daily activities and happy moments",
      featuredPhotos: "Featured Photos",
      carouselSubtitle: "Swipe through our highlights",
      photoGrid: "Photo Collection",
      gridSubtitle: "Browse through our complete collection of memories",
      ctaTitle: "Want to See More?",
      ctaSubtitle:
        "Schedule a visit to experience our warm and welcoming environment in person",
      scheduleVisit: "Schedule a visit",
      contactUs: "Contact Us",
      images: {
        creativePainting: {
          src: "/images/creative-1.jpg",
          title: "Creative Painting",
          alt: "Children painting together",
        },
        artActivities: {
          src: "/images/creative-2.jpg",
          title: "Art Activities",
          alt: "Kids doing art activities",
        },
        drawingCrafting: {
          src: "/images/creative-3.jpg",
          title: "Drawing & Crafting",
          alt: "Children drawing and crafting",
        },
        outdoorFun: {
          src: "/images/outdoor-1.jpg",
          title: "Outdoor Fun",
          alt: "Kids playing outdoors",
        },
        parkPlay: {
          src: "/images/outdoor-2.jpg",
          title: "Park Play",
          alt: "Children running in the park",
        },
        playgroundTime: {
          src: "/images/outdoor-3.jpg",
          title: "Playground Time",
          alt: "Group of kids on playground",
        },
        healthyMeals: {
          src: "/images/meals-1.jpg",
          title: "Healthy Meals",
          alt: "Children eating healthy meals",
        },
        lunchTime: {
          src: "/images/meals-2.jpg",
          title: "Lunch Time",
          alt: "Kids enjoying lunch together",
        },
        snackSharing: {
          src: "/images/meals-3.jpg",
          title: "Snack Sharing",
          alt: "Children sharing snacks",
        },
        napTime: {
          src: "/images/rest-1.jpg",
          title: "Nap Time",
          alt: "Child napping peacefully",
        },
        restingMats: {
          src: "/images/rest-2.jpg",
          title: "Resting Mats",
          alt: "Kids resting on mats",
        },
        daycareSleep: {
          src: "/images/rest-3.jpg",
          title: "Daycare Sleep",
          alt: "Children sleeping in daycare",
        },
        groupLearning: {
          src: "/images/learning-1.jpg",
          title: "Group Learning",
          alt: "Kids learning together",
        },
        readingTime: {
          src: "/images/learning-2.jpg",
          title: "Reading Time",
          alt: "Children reading books",
        },
        teacherHelp: {
          src: "/images/learning-3.jpg",
          title: "Teacher Help",
          alt: "Teacher helping students",
        },
        musicClass: {
          src: "/images/music-1.jpg",
          title: "Music Class",
          alt: "Children playing music",
        },
        instrumentsFun: {
          src: "/images/music-2.jpg",
          title: "Instruments Fun",
          alt: "Kids with musical instruments",
        },
        singingGroup: {
          src: "/images/music-3.jpg",
          title: "Singing Group",
          alt: "Group singing together",
        },
      },
    },

    dailyRoutine: {
      title: "Daily routine",
      subtitle: "A typical day at Mini Avonturiers",

      // Morning Section
      morningSchedule: "Morning schedule",
      morningDesc:
        "Children start the day with structured play and learning experiences.",
      morning: {
        dropOff: {
          time: "7:00 – 9:00",
          title: "Morning arrival & free play",
          description:
            "Children are welcomed in a relaxed atmosphere. Parents are given the opportunity to share information with the childcare professional. Children choose their own activity or corner, allowing them to start the day at their own pace. This phase promotes safety and attachment: the transition from home to daycare is calm and child-centered.",
        },
        circle: {
          time: "9:00",
          title: "Fruit, drinks & circle time",
          description:
            "We gather in a circle for a fun and educational time: singing, talking about the weather, reading a book, and checking in with others. This promotes language skills, team spirit, and social-emotional development. Mealtime is intentionally kept together and calm.",
        },
        activity: {
          time: "10:00",
          title: "Targeted activity",
          description:
            "Activities vary daily, but are aligned with the theme and developmental area (e.g., crafts, language games, making sequences, sensory materials). They take place in small groups or individually and are tailored to each child's developmental needs.",
        },
        outdoor: {
          time: "11:00",
          title: "Outdoor play or themed play",
          description:
            "Outdoor movement is essential for motor skills and health. Outdoors, there's space for climbing, running, exploring, and free play. In case of inclement weather, a similar play session with a themed theme takes place indoors.",
        },
        lunch: {
          time: "11:30",
          title: "Lunch according to the wheel of five",
          description:
            "Healthy eating is an essential part of our developmental vision. We eat lunch together, focusing on healthy eating habits, self-reliance, and engaging in conversation at the table. Children are encouraged to try new flavors and, where possible, we involve them in preparing them.",
        },
      },

      // Afternoon Section
      afternoonSchedule: "Afternoon schedule",
      afternoonDesc:
        "In the afternoon we balance rest with creative and physical activities.",
      afternoon: {
        rest: {
          time: "12:30",
          title: "Rest time or afternoon nap",
          description:
            "Younger children sleep in a quiet, darkened room under supervision. Older children who are no longer sleeping are given a rest time: reading, lying down, or playing a quiet game. This promotes recovery, concentration, and emotional regulation.",
        },
        snack: {
          time: "2:30",
          title: "Fruit or vegetable snack",
          description:
            "A healthy snack provides a short break. We also use this time to observe how children are feeling and what they need for the rest of the day.",
        },
        activity: {
          time: "3:00",
          title: "Activity: Role-playing, motor skills, afternoon gym",
          description:
            "In the afternoon, we focus on expressive and physical activities. Afternoon gym, dancing, building, role-playing, or acting out a story together. The emphasis is on fun, expression, and teamwork.",
        },
        meal: {
          time: "4:00",
          title: "Bread, breadsticks, or a hot meal",
          description:
            "Depending on the child and the length of care, we offer a nutritious snack or a light hot meal. Eating remains a social occasion, but is not forced. Children determine their dietary needs within the available options.",
        },
        pickup: {
          time: "5:00 – 6:30",
          title: "Pickup, free play & quiet corners",
          description:
            "The day ends in a relaxed atmosphere. Children play freely in a calm atmosphere. We offer soft lighting and book corners to make the transition home predictable and peaceful. Staff members take ample time to hand over to parents during the handover.",
        },
      },

      // Principles Section
      principles: "Guiding principles",
      principlesDesc:
        "Our daily routine is designed with balance, rhythm, and children’s wellbeing in mind.",
      principleItems: {
        predictability: {
          title: "Predictability and repetition",
          description:
            "give children structure and make transitions between activities easier.",
        },
        sharedMoments: {
          title: "Shared moments",
          description:
            "(circle, meal times) children learn social skills and respect for others.",
        },
        freePlay: {
          title: "Free play moments",
          description:
            "stimulate creativity, self-direction and social interaction.",
        },
        targeted: {
          title: "Targeted activities",
          description:
            "provide developmental challenges within the Zone of Proximal Development (Vygotsky).",
        },
        rest: {
          title: "Rest and sleep",
          description:
            "are crucial for processing impressions and promote self-regulation.",
        },
        attentionSpan: {
          title: "Natural attention span",
          description:
            "The daily rhythm is tailored to the natural attention span of young children, so that they can learn and play optimally without overstimulation.",
        },
      },
    },

    // Contact page
    contact: {
      title: "Contact",
      subtitle: "Get in touch with our team",
      address: "Address",
      addressText: "Kinderstraat 123, 3511 AB Utrecht",
      phone: "Phone",
      phoneText: "+31 (0)30 123 4567",
      email: "Email",
      emailText: "info@mini-avonturiers.nl",
      hours: "Opening Hours",
      hoursText: "Monday - Friday: 07:00 - 18:30",
      message: "Send us a message",
      name: "Full name",
      emailField: "Email address",
      subject: "Subject",
      messageField: "Your message",
      send: "Send message",
      mondayFriday: "Monday to Friday",
      saturday: "Saturday",
      sunday: "Sunday",
      closed: "Closed",
      saturdayOpeningHours: "Different opening hours",
      faqItems: {
        q1: "What age groups do you care for?",
        a1: "We care for children from 0 to 12 years old, with age-appropriate programs for each group.",
        q2: "What are your opening hours?",
        a2: "We are open Monday to Friday from 7:00 to 18:30.",
        q3: "Do you provide meals?",
        a3: "Yes, we provide healthy breakfast, lunch, and snacks throughout the day.",
        q4: "What is your staff-to-child ratio?",
        a4: "We maintain a ratio of 1:4 for babies, 1:6 for toddlers, and 1:8 for preschoolers.",
        q5: "Do you offer flexible scheduling?",
        a5: "Yes, we offer flexible scheduling options to accommodate different family needs.",
        q6: "What activities do you offer?",
        a6: "We offer a variety of activities including crafts, music, outdoor play, educational games, and more.",
        q7: "How do you handle emergencies?",
        a7: "We have comprehensive emergency procedures and all staff are trained in first aid.",
        q8: "Can parents visit during the day?",
        a8: "Yes, parents are welcome to visit during opening hours, although we ask for prior notice.",
      },
      transport: {
        byCar: "By car",
        byCarDescription:
          "Free parking on site. Located near the A12, exit 18.",
        byPublicTransport: "By public transport",
        byPublicTransportDescription:
          "Bus lines 2, 8, and 12 stop within a 5-minute walk.",
        byBicycle: "By bicycle",
        byBicycleDescription:
          "Secure bicycle parking available. Located on the main bicycle route.",
      },
    },
    // Auth
    auth: {
      welcome: "Welcome back",
      loginSubtitle: "Log in to access your account",
      email: "Email address",
      password: "Password",
      login: "Login",
      loggingIn: "Logging in...",
      loginFailed: "Login failed. Please check your credentials.",
      noAccount: "Don't have an account?",
      registerHere: "Register here",
      forgotPassword: "Forgot password?",
      registerTitle: "Join our family",
      registerSubtitle: "Create an account to register your child",
      fullName: "Full name",
      phone: "Phone number",
      confirmPassword: "Confirm password",
      parentInfo: "Parent information",
      children: "Children",
      addChild: "+ Add child",
      removeChild: "Remove",
      childNumber: "Child",
      name: "Child's name",
      dateOfBirth: "Date of birth",
      group: "Group (optional)",
      register: "Register",
      registering: "Registering...",
      registrationFailed: "Registration failed. Please try again.",
      passwordMismatch: "Passwords do not match.",
      passwordTooShort: "Password must be at least 6 characters long.",
      haveAccount: "Already have an account?",
      loginHere: "Login here",
      childname: "Child name",
      childBirthDate: "Child birth date",
      childGroup: "Child group",
      selectGroup: "Select group",
      babies: "Babies",
      toddlers: "Toddlers",
      preschoolers: "Preschoolers",
      kindergarten: "Kindergarten",
      schoolAge: "School age",
    },

    // Common UI elements
    common: {
      scheduleVisit: "Schedule a visit",
      contactUs: "Contact us",
      learnMore: "Learn more",
      registerNow: "Register now",
      downloadBrochure: "Download brochure",
      readyToStart: "Ready to get started?",
      joinOurFamily: "Join our family",
      whatParentsSay: "What parents say",
      frequentlyAskedQuestions: "Frequently asked questions",
      quickAnswers: "Quick answers to common questions",
      getInTouch: "Get in touch",
      sendMessage: "Send message",
      findUs: "Find us",
      byCar: "By car",
      byPublicTransport: "By public transport",
      byBicycle: "By bicycle",
      openingHours: "Opening hours",
      address: "Address",
      phone: "Phone",
      email: "Email",
      faq: "Frequently asked questions",
      faqDescription: "Quick answers to common questions",
      locationDesc:
        "Visit our location and discover what Mini Adventurers has to offer.",
      ctaReady: "Ready to get started?",
      ctaDescription:
        "Contact us today to learn more about our programs and schedule a visit to our facility.",
      ctaScheduleVisit: "Schedule a visit",
      ctaDownloadBrochure: "Download brochure",
    },

    // Form options
    form: {
      firstname: "First name",
      lastname: "Last name",
      firstnamePlaceholder: "John",
      lastnamePlaceholder: "Doe",
      selectSubject: "Select a subject",
      generalInquiry: "General inquiry",
      registration: "Registration",
      scheduleVisit: "Schedule a visit",
      pricingInfo: "Pricing information",
      other: "Other",
      fullName: "Full name *",
      emailAddress: "Email address *",
      phoneNumber: "Phone number",
      subject: "Subject *",
      message: "Message *",
      yourFullName: "Your full name",
      emailPlaceholder: "your.email@example.com",
      phonePlaceholder: "+31 (0)6 12345678",
      messagePlaceholder: "Tell us how we can help you...",
      aanmeldenIntro:
        "Register your child or have a question? Fill in the form below.",
      rondleidingIntro:
        "Curious about our location? Fill in the contact form below to make an appointment.",
      aanmeldenTitle: "Register child or ask a question",
      rondleidingTitle: "Request a tour",
      send: "Send",
      sending: "Sending",
      sendSuccess:
        "Thank you! your message has been sent successfully. We’ll get back to you shortly.",
      sendError:
        "Oops! Something went wrong while sending your message. Please try again later or contact us directly.",
    },

    // FAQ items
    faq: {
      openingHours: "What are your opening hours?",
      openingHoursAnswer:
        "We are open Monday through Friday from 7:00 to 6:30. We also offer flexible scheduling options to meet your family's needs.",
      ageGroups: "What age groups do you serve?",
      ageGroupsAnswer:
        "We provide care for children from 0 to 12 years old, with age-appropriate programs and activities for each developmental stage.",
      meals: "Do you provide meals?",
      mealsAnswer:
        "Yes, we provide healthy, balanced meals and snacks throughout the day. We can accommodate special dietary requirements.",
      scheduleVisit: "How can I schedule a visit?",
      scheduleVisitAnswer:
        "You can schedule a visit by calling us, sending an email, or filling out the contact form above.",
    },

    // Activities
    activities: {
      hero: {
        title: "Activities offered",
        subtitle: "Learning through play in a natural, stimulating environment",
      },
      intro: {
        title: "Learning through play",
        p1: "At Mini Avonturiers, learning through play is central. Our activities are carefully designed to stimulate children's development in a natural, playful way.",
        p2: "The activities are tailored to the children's developmental stage, interests, and talents, and contribute to growth in language, motor skills, creativity, social-emotional skills, and world orientation.",
        ourApproachTitle: "Our approach",
        ourApproachText:
          "We offer a varied daily program, incorporating both free play and supervised activities. Our approach is based on Vygotsky's Zone of Proximal Development: we offer activities that are slightly above the child's current level, but are achievable with support.",
      },
      sensory: {
        title: "Sensory activities",
        subtitle:
          "Sensory play—also known as playing with materials that stimulate the senses—is essential for young children.",
        helpsWithTitle: "Sensory play helps with:",
        reggioNote:
          "Educational staff guide this type of play from the Reggio Emilia perspective: the child is the researcher, the space is the third pedagogue, and we support with open questions, words and observations.",
      },
      language: {
        title: "Language development",
        subtitle:
          "Language forms the foundation for communication, expression, and later school skills.",
        note: "Our staff uses language-rich interactions: they describe actions, ask open-ended questions, expand on statements, and encourage children to talk. This approach aligns with the Interactive Reading Method and early childhood education principles.",
      },
      creative: {
        title: "Creative expression",
        subtitle:
          "Creative expression helps children express themselves, process feelings, and develop their imaginations.",
        note: "We encourage children to make their own choices and create an atmosphere in which experimenting, making mistakes and fantasizing are the norm.",
      },
      thematic: {
        title: "Thematic work & development-oriented offering",
        subtitle:
          "We work with themes that connect with the child's world, such as seasons, family, animals, professions, emotions, traffic, or holidays.",
        note: "Within these themes, we link activities to multiple developmental areas. Our staff guides the game and encourages deepening through targeted questions, expanding the game, and integrating learning objectives.",
      },
      bso: {
        title: "After-school care program",
        subtitle:
          "Challenge and fun for older children. BSO children are often eager for challenge, independence, and relaxation after school.",
        note: "After-school care activities are tailored to the children's own wishes and ideas: children have a voice and can choose from several options. This creates space for autonomy, friendship, and relaxation.",
      },
      cta: {
        title: "Ready to explore our activities?",
        subtitle:
          "Experience our diverse range of activities designed to stimulate your child's development through play. Schedule a visit to see our programs in action.",
        button: "Learn more about our activities",
      },
      sensoryActivities: {
        naturalMaterials: {
          title: "Natural materials (sand, water, clay)",
          description:
            "Children explore textures, temperatures, and shapes while building creativity.",
        },
        outdoorElements: {
          title: "Outdoor elements (mud kitchen, garden)",
          description: "Opportunities for sensory-rich experiences in nature.",
        },
        creativeMaterials: {
          title: "Creative materials (paints, dough, fabrics)",
          description: "Encouraging imagination and fine motor development.",
        },
      },
      sensoryBenefits: {
        exploringTextures: "Exploring different textures and sensations",
        developMotorSkills: "Developing fine and gross motor skills",
        emotionRegulation: "Improving emotional regulation",
        problemSolving: "Encouraging problem-solving skills",
      },
      languageActivities: {
        dailyReading: {
          title: "Daily reading",
          description: "Story time that expands vocabulary and comprehension.",
        },
        visualLearning: {
          title: "Visual learning",
          description:
            "Using images and objects to support language development.",
        },
        musicRhymes: {
          title: "Music and rhymes",
          description:
            "Singing songs and rhymes to strengthen rhythm and language.",
        },
        rolePlay: {
          title: "Role play",
          description:
            "Children act out scenarios, boosting expression and creativity.",
        },
      },
      creativeActivities: {
        artsCrafts: {
          title: "Arts and crafts",
          description: "Painting, cutting, and gluing to express creativity.",
        },
        buildingConstruction: {
          title: "Building and construction",
          description: "Using blocks and materials to develop spatial skills.",
        },
        musicDance: {
          title: "Music and dance",
          description:
            "Moving and creating to music, developing rhythm and coordination.",
        },
        drawingColoring: {
          title: "Drawing and coloring",
          description: "Exploring colors and shapes with pencils and markers.",
        },
      },
      thematicAreas: {
        arithmetic: {
          title: "Arithmetic",
          description: "Fun counting and number recognition activities.",
        },
        language: {
          title: "Language",
          description: "Storytelling, reading, and vocabulary games.",
        },
        motorSkills: {
          title: "Motor skills",
          description: "Games that promote movement and coordination.",
        },
        socialBehavior: {
          title: "Social behavior",
          description: "Activities to encourage teamwork and empathy.",
        },
      },
      bsoActivities: {
        sportsExercise: {
          category: "Sports and exercise",
          activities: [
            "Football and team games",
            "Outdoor play and running challenges",
          ],
        },
        technologyExperiments: {
          category: "Technology and experiments",
          activities: [
            "Simple science experiments",
            "Building with LEGO and tech toys",
          ],
        },
        cookingBaking: {
          category: "Cooking and baking",
          activities: [
            "Baking cookies and simple recipes",
            "Preparing healthy snacks",
            "Exploring tastes and textures",
          ],
        },
        excursionsTrips: {
          category: "Excursions and trips",
          activities: [
            "Nature walks and park visits",
            "Visiting local attractions",
          ],
        },
      },
    },

    // Testimonials
    testimonials: {
      title: "What parents say",
      subtitle: "Hear from families who trust us with their children",
      sarah: {
        name: "Sarah Johnson",
        role: "Parent of Emma (4 years)",
        text: "Mini Avonturiers has been amazing for our daughter. She loves coming here every day and has learned so much. The staff is caring and professional.",
      },
      michael: {
        name: "Michael Chen",
        role: "Parent of Lucas (3 years)",
        text: "The daily routine is perfect for our son. He's developed great social skills and is always excited to tell us about his day.",
      },
      anna: {
        name: "Anna Rodriguez",
        role: "Parent of Sofia (5 years)",
        text: "We couldn't be happier with the care our daughter receives. The activities are engaging and educational, and the staff truly cares about each child.",
      },
    },

    // Service Features
    serviceFeatures: {
      safety: "Safety first",
      safetyDesc: "Secure environment with 24/7 monitoring and trained staff",
      nutrition: "Healthy nutrition",
      nutritionDesc:
        "Balanced meals and snacks prepared with fresh ingredients",
      education: "Early education",
      educationDesc: "Age-appropriate learning activities and curriculum",
      flexibility: "Flexible scheduling",
      flexibilityDesc: "Customizable care options to fit your family's needs",
      communication: "Parent communication",
      communicationDesc: "Regular updates and open communication with parents",
      activities: "Rich activities",
      activitiesDesc:
        "Diverse activities including arts, music, outdoor play, and learning games",
      qualifiedStaff: "Qualified staff",
      qualifiedStaffDesc:
        "Our team consists of certified early childhood educators with years of experience",
      safeEnvironment: "Safe environment",
      safeEnvironmentDesc:
        "Secure facilities with modern safety equipment and protocols",
      healthyMeals: "Healthy meals",
      healthyMealsDesc:
        "Nutritious, balanced meals prepared with fresh, local ingredients",
      learningProgram: "Learning program",
      learningProgramDesc:
        "Comprehensive curriculum designed for early childhood development",
      flexibleHours: "Flexible hours",
      flexibleHoursDesc:
        "Extended hours and flexible scheduling to accommodate working parents",
      parentInvolvement: "Parent involvement",
      parentInvolvementDesc:
        "Regular communication and opportunities for parent participation",
    },

    // Additional Services
    additionalServices: {
      title: "Additional services",
      subtitle: "Enhance your child's experience with our optional services",
      transportation: "Transportation",
      transportationDesc:
        "Safe and reliable transportation to and from school or home",
      extraActivities: "Extra activities",
      extraActivitiesDesc:
        "Specialized classes in music, art, sports, or languages",
      specialDiet: "Special diet",
      specialDietDesc: "Accommodation for special dietary requirements",
      tutoring: "Tutoring",
      tutoringDesc: "Individual or small group tutoring sessions",
    },

    // Service Features Lists
    serviceFeaturesList: {
      daycare: [
        "Full-day care from 7:00 to 6:30",
        "Age-appropriate activities and learning",
        "Healthy meals and snacks included",
        "Regular progress reports",
        "Parent communication app",
      ],
      afterSchool: [
        "After-school pickup from local schools",
        "Homework assistance and support",
        "Recreational activities and games",
        "Healthy afternoon snack",
        "Flexible pickup times",
      ],
      holiday: [
        "Full-day programs during school holidays",
        "Themed activities and special events",
        "Field trips and excursions",
        "Arts, crafts, and creative projects",
        "Physical activities and sports",
      ],
      flexible: [
        "Customizable schedules",
        "Drop-in care when needed",
        "Part-time options available",
        "Emergency care services",
        "Weekend care (by arrangement)",
      ],
    },

    // Common CTAs and Section Headings
    cta: {
      readyToGetStarted: "Ready to get started?",
      contactToLearnMore:
        "Contact us to learn more about our services and schedule a visit to our facility.",
      registerNow: "Register now",
      ourCoreServices: "Our core services",
      whyChooseOurServices: "Why choose our services?",
      moreThanChildcare:
        "We provide more than just childcare - we create an environment where children thrive",
      additionalServices: "Additional services",
      additionalServicesSubtitle:
        "Enhance your child's experience with our optional services",
      whatsIncluded: "What's included:",
      meetOurTeam:
        "Meet our dedicated team of early childhood professionals who are passionate about your child's development and well-being.",
      yearsOfExperience: "Years of experience",
      happyChildren: "Happy children",
      qualifiedStaff: "Qualified staff",
      parentSatisfaction: "Parent satisfaction",
      parentOf: "Parent of",
      safeTransport:
        "Safe and reliable transportation to and from school or home",
      extraActivities:
        "Specialized classes in music, art, sports, or languages",
      specialDiet: "Accommodation for special dietary requirements",
      tutoring: "Individual or small group tutoring sessions",
      fullDayCare: "Full-day care from 7:00 to 6:30",
      ageAppropriate: "Age-appropriate activities and learning",
      healthyMealsIncluded: "Healthy meals and snacks included",
      progressReports: "Regular progress reports",
      parentApp: "Parent communication app",
      afterSchoolPickup: "After-school pickup from local schools",
      homeworkSupport: "Homework assistance and support",
      recreation: "Recreatieve activiteiten en spelletjes",
      afternoonSnack: "Healthy afternoon snack",
      flexiblePickup: "Flexible pickup times",
      holidayPrograms: "Full-day programs during school holidays",
      themedActivities: "Themed activities and special events",
      fieldTrips: "Field trips and excursions",
      artsCrafts: "Arts, crafts, and creative projects",
      physicalActivities: "Physical activities and sports",
      customizableSchedules: "Customizable schedules",
      dropInCare: "Drop-in care when needed",
      partTime: "Part-time options available",
      emergencyCare: "Emergency care services",
      weekendCare: "Weekend care (by arrangement)",
    },

    // Daily Routine CTA and Features
    dailyRoutineCta: {
      joinAdventures: "Ready to join our daily adventures?",
      experienceText:
        "Experience our structured yet flexible daily routine that promotes learning, creativity, and fun for your child.",
      flexibleScheduleTitle: "Flexible schedule",
      flexibleScheduleDesc:
        "We understand that every family has different needs. Our flexible scheduling allows you to choose the hours that work best for your family.",
      fullDayCare: "Full-day care (7:00 - 18:30)",
      halfDayOptions: "Half-day options available",
      dropInCare: "Drop-in care when needed",
      specialActivitiesTitle: "Special activities",
      specialActivitiesDesc:
        "In addition to our daily routine, we offer special activities and events throughout the week.",
      artWorkshops: "Art workshops on Tuesdays",
      musicClasses: "Music classes on Wednesdays",
      gardenTime: "Garden time on Thursdays",
      fridayFun: "Friday fun day activities",
      scheduleVisit: "Schedule a visit",
      contactUs: "Contact us",
    },

    // Proper structure for Vision page translations
    vision: {
      title: "Pedagogical vision",
      subtitle:
        "Our approach to childcare is based on scientific research and proven pedagogical principles",

      section21: {
        title: "Our vision on childcare",
        content:
          "At Mini Avonturiers, we believe that every child is unique and thrives in an environment that fosters wonder, educational play, and clear structure. We offer children a safe space where, under expert guidance, they can grow into curious, social, and independent individuals. We focus on Riksen-Walraven's four core values: emotional safety, personal and social skills, and norms and values.",
      },

      section22: {
        title: "Our view of children",
        content:
          "At Mini Avonturiers, the child is central, in all their individuality. We embrace the belief that every child is unique, with their own character, pace, developmental trajectory, and inner motivation to learn. This vision aligns with the image of the competent child, as emphasized in the Reggio Emilia approach. In this pedagogy, the child is not seen as an empty vessel to be filled, but as an active, powerful explorer who explores the world based on their own interests.",
        piaget: {
          title: "Jean Piaget's theory",
          content:
            "Jean Piaget also endorses this idea in his theory of cognitive development. According to him, children are naturally inclined to experiment, test hypotheses, and give meaning to experiences. Therefore, we offer children space not only to play but also to make mistakes, try new things, and gain insights on their own.",
        },
        bowlby: {
          title: "John Bowlby's attachment theory",
          content:
            "A secure base is essential. As John Bowlby's attachment theory demonstrates, a secure attachment is a fundamental prerequisite for healthy social-emotional development. At Mini Avonturiers, we therefore provide a warm, stable, and loving environment where children feel seen, heard, and valued. Only then can they open up to the world around them.",
        },
      },

      section23: {
        title: "Our vision on development",
        content:
          "We view development as a holistic process in which the child simultaneously develops in multiple domains: social, emotional, motor, cognitive, and creative. This perspective is inspired by the theory of Lev Vygotsky, who emphasizes that development occurs within social interaction and that the environment, particularly adults and more skilled peers, plays a crucial role.",
        playTool: {
          title: "Play as development tool",
          content:
            'Play is the most important development tool we use. Not as a pastime, but as a means of learning and growth. Play promotes creativity, problem-solving skills, language development, cooperation, motor skills, and much more. This idea is supported by Maria Montessori, who emphasized that "play is the work of the child." Her concept guides our approach.',
        },
        thematicApproach: {
          title: "Thematic approach",
          content:
            'Within our thematic approach, based on current experiences, we create rich learning environments where children can discover, experiment, create, and reflect for themselves. These themes aren\'t presented top-down, but often arise from within the child. This aligns with the Reggio Emilia vision, which views children as possessing "a hundred languages"—in other words, there are countless ways children express themselves, all of which must be taken seriously.',
        },
        staffRole: {
          title: "Educational staff role",
          content:
            "Our educational staff take on a guiding and observant role: they create the conditions, ask questions, reflect on behavior, and support without directing. They apply the principles of the Gordon Method (by Thomas Gordon): listening without judgment, communicating clearly, and solving problems through mutual respect.",
        },
      },

      section24: {
        title: "Correcting and confirming",
        behaviorCommunication: {
          title: "Behavior as communication",
          content:
            "Behavior is communication. In line with the Emmi Pikler approach and non-directive pedagogy, we believe it's important to always consider what a child is trying to express through their behavior. Undesirable behavior isn't a sign of intent or unwillingness, but often a signal of frustration, powerlessness, or an unfulfilled need.",
        },
        respectfulApproach: {
          title: "Respectful approach",
          content:
            "When challenging behavior occurs, we employ a respectful and supportive approach. We engage in conversation with the child, address their feelings, and put into words what they may be experiencing. This helps children develop self-regulation, a key aspect of social-emotional growth.",
        },
        positiveReinforcement: {
          title: "Positive reinforcement",
          content:
            "We set boundaries consistently and lovingly, with clear rules, a consistent rhythm, and predictability. We believe in the power of positive reinforcement, as advocated in Alfie Kohn's theory. By genuinely identifying and appreciating desired behavior, we increase the child's intrinsic motivation and strengthen their self-esteem.",
        },
      },

      section25: {
        title: "Basic principles In practice",
        content:
          "Our daily pedagogical practice is built on five solid pillars, which are constantly interacting with each other. These basic principles are inspired by scientifically substantiated pedagogical visions and are reflected in our actions, the design of the space, and the organization of the day.",
        principles: {
          sensitiveResponsiveness: {
            title: "1. Sensitive responsiveness",
            content:
              "Based on Bowlby's attachment theory and further developed by Mary Ainsworth, sensitive responsiveness means that caregivers perceive the child's signals promptly, accurately, and lovingly. We are emotionally available and responsive to the child's needs, whether for comfort, attention, food, or rest. This responsiveness forms the foundation for a secure attachment, which in turn forms the basis for further development.",
          },
          respectAutonomy: {
            title: "2. Respect for autonomy",
            content:
              "We want to give children the confidence and space to act independently. Whether it's eating independently, making choices, or resolving conflicts, we support, but don't take over. This not only promotes autonomy, but also a sense of responsibility and self-confidence in children.",
          },
          structureRhythm: {
            title: "3. Structure and rhythm",
            content:
              "Children thrive in a predictable and organized environment. A consistent daily structure contributes to inner peace and balance. Rhythm provides stability, and recurring rituals (such as songs, circles, and transitions) reinforce feelings of safety and cohesion. Therefore, we maintain a well-thought-out daily schedule with sufficient balance between activity and rest.",
          },
          activeInvolvement: {
            title: "4. Active involvement of educational staff",
            content:
              "The strength of our educational staff lies in their presence, attention, and reflection. They are not spectators, but co-discoverers. With an open mind and constant observation, they guide the child's development process. We invest in professional development and self-reflection, ensuring our staff continuously develop their skills.",
          },
          positiveInteraction: {
            title: "5. Positive interaction",
            content:
              "Language matters. The way we speak to children shapes how they experience themselves and the world. We use positive, inviting, and respectful language. Conflicts are discussed, not punished. Compliments focus on effort and process. This form of communication strengthens the child's relationship, competence, and autonomy—three basic psychological needs according to Deci & Ryan's self-determination theory.",
          },
        },
      },

      section26: {
        title: "Evaluation and update",
        content:
          "Our pedagogical policy plan is evaluated annually in collaboration with the team, the pedagogical coach, and the parent committee. We gather feedback from the field, discuss new insights, and current developments. We adjust the policy where necessary to ensure it remains current, workable, and aligned with our vision.",
      },
      documentsSection: {
        documents: "Documents & Policy Plans",
        kdv: "KDV Pedagogical Policy Plan",
        bso: "BSO Pedagogical Policy Plan",
        safety: "Safety & Health Policy",
      },
    },

    // Educational Staff page translations
    educationalStaff: {
      title: "Educational staff role",
      subtitle:
        "The heart of our vision and quality - active parental partners, emotional anchors and growth coaches",

      sectionTitle: "Role of educational staff",
      introText1:
        "At Mini Avonturiers, our educational staff are at the heart of our vision and quality. They are the living embodiment of our educational policy in daily practice. Our staff are not just supervisors, but active parenting partners, emotional anchors, and growth coaches.",
      introText2:
        "We believe that the quality of interaction between children and adults determines the quality of care. Therefore, our staff are sensitive, responsive, professional, and reflective, passionate about their work and attentive to the needs of children.",

      coreValues: {
        title: "Our core values",
        sensitiveResponsive: {
          title: "Sensitive and responsive attitude",
          description:
            "Our staff are trained in sensitive responsiveness, one of the four core pedagogical objectives of the Childcare Act. They truly see the child, identify subtle cues (e.g., tension, the need for rest or attention), and respond quickly and appropriately.",
          practices: [
            "Children are called by name and greeted personally",
            "Teaching staff communicate at eye level, with a calm voice and open body language",
            "Emotions are named and acknowledged",
            "Children are never ignored, rejected or interrupted in their experience",
          ],
        },
        guidingGroupProcesses: {
          title: "Guiding group processes",
          description:
            "In addition to individual attention, the group process plays a vital role in development. Educational staff actively guide the group and foster an atmosphere of safety, cooperation, and respect.",
          practices: [
            "Help children connect and resolve conflicts",
            "Foster sharing and cooperation",
            "Develop empathy and social skills",
            "Balance between giving space and setting boundaries",
          ],
        },
        providingStructure: {
          title: "Providing structure without rigidity",
          description:
            "A clear daily structure is essential for peace and security. Our staff ensure predictability, clear transitions, and a clear rhythm, while also being flexible enough to adapt to the needs of the moment.",
          practices: [
            "Ensure predictability and clear transitions",
            "Provide clarity about what happens, why, and when",
            "Help children learn to plan and assess",
            "Adapt to the needs of the moment",
          ],
        },
        allowingAutonomy: {
          title: "Allowing space for autonomy",
          description:
            "Our educational staff encourage independence. They allow children to make their own choices, discover their limits, and make mistakes, all under safe supervision.",
          practices: [
            "Children can choose what they want to play or where they want to sit",
            "They are allowed to help with daily routines",
            "Activities are open and inviting, no mandatory outcomes",
            "Staff offer proximity and support where necessary",
          ],
        },
        individualDevelopment: {
          title: "Support for individual development",
          description:
            "Every child develops at their own pace and in their own way. Our teaching staff systematically monitor children's development through observations, conversations, and parent-teacher meetings.",
          practices: [
            "Systematic monitoring through observations",
            "Consider overall development: language, motor, social-emotional, cognitive",
            "Tailor activities to child's needs and interests",
            "Use child monitoring systems and reflective tools",
          ],
        },
        everyEmployeeMentor: {
          title: "Every employee as a mentor",
          description:
            "Every child at Mini Avonturiers has a designated mentor: a childcare professional who serves as a point of contact for both the child and the parents.",
          practices: [
            "Closely monitor the child's development",
            "Conduct mentor conversations with parents",
            "Ensure a warm, individual bond with the child",
            "Keep an overview of the child's well-being and growth trajectory",
          ],
        },
      },

      professionalDevelopment: {
        title: "Professional development of our team",
        intro:
          "To ensure quality, we invest consistently in the professional and personal development of our teaching staff. We expect our employees not only to act based on routine, but also to make conscious pedagogical choices, with respect for the child as an individual.",
        areas: {
          intervision: {
            title: "Regular intervisions and team reflection",
            description:
              "Structured team meetings for professional growth and shared learning",
          },
          education: {
            title: "Further education and training",
            description:
              "Continuous learning in emotional development, play guidance, and communication",
          },
          coaching: {
            title: "Coaching in the workplace",
            description:
              "On-the-job guidance and support for professional development",
          },
          documentation: {
            title: "Time for documentation and preparation",
            description:
              "Dedicated time for planning, documentation, and consultation",
          },
        },
      },

      sensitiveResponsiveness: {
        title: "Sensitive responsiveness",
        intro:
          "This attitude is based on the attachment theory of Bowlby and Ainsworth: responding consistently and warmly to the child promotes secure attachment, which is crucial for healthy social-emotional development.",
        secureAttachment: {
          title: "Secure attachment",
          description:
            "Consistent and warm responses promote secure attachment, creating a foundation for healthy social-emotional development and exploration.",
        },
        subtleCues: {
          title: "Subtle cues",
          description:
            "Staff identify and respond to subtle cues like tension, need for rest, or attention, ensuring every child feels seen and understood.",
        },
      },

      teamConsultation: {
        title: "Team consultation and further training",
        teamCollaboration: {
          title: "Team collaboration",
          description:
            "Regular team meetings and consultations ensure consistent quality and shared best practices across all staff members.",
          practices: [
            "Regular intervisions and team reflection",
            "Shared learning and best practices",
            "Collaborative problem-solving",
          ],
        },
        continuousLearning: {
          title: "Continuous learning",
          description:
            "Ongoing education and training ensure our staff stay current with the latest pedagogical approaches and research.",
          practices: [
            "Further education and specialized training",
            "Coaching and mentoring programs",
            "Professional development opportunities",
          ],
        },
      },

      staffInAction: {
        title: "Our staff in action",
        professionalMentors: {
          title: "Professional mentors",
          description:
            "Every child has a designated mentor who closely monitors their development and serves as a trusted point of contact for parents.",
        },
        emotionalAnchors: {
          title: "Emotional anchors",
          description:
            "Our staff provide emotional stability and support, helping children feel secure and confident in their environment.",
        },
        growthCoaches: {
          title: "Growth coaches",
          description:
            "Staff guide children's development through play, learning activities, and supportive interactions that foster growth.",
        },
      },

      cta: {
        title: "Meet our professional team",
        subtitle:
          "Experience the dedication and expertise of our educational staff who make every day special for your child",
        scheduleVisit: "Schedule a visit",
        learnMore: "Learn more",
      },

      inPractice: "In practice:",
    },

    // Dashboard page translations
    dashboard: {
      welcome: "Welcome",
      subtitle:
        "Manage your children's information and stay updated with their activities",
      birthDate: "Birth date",
      group: "Group",
      notAssigned: "Not assigned",
      registeredSince: "Registered since",
      viewDetails: "View details",
      editInfo: "Edit information",
      quickActions: "Quick actions",
      scheduleVisit: "Schedule visit",
      contact: "Contact",
      registration: "Registration",
      reports: "Reports",
      recentUpdates: "Recent updates",
      newActivity: "New activities have been added to the program",
      photosAdded: "New photos from today's activities",
      menuUpdate: "This week's menu has been updated",
      daysAgo: "days ago",
      weekAgo: "week ago",
    },

    // Emotional Safety page translations
    emotionalSafety: {
      title: "Emotional safety & trust",
      subtitle:
        "The foundation of our pedagogical vision where every child feels loved, seen, and heard",
      sectionTitle: "4. Emotional safety & trust",
      introText1:
        "At Mini Avonturiers, emotional safety is the foundation of our pedagogical vision. Every child needs to feel loved, seen, and heard to develop optimally. We create an environment where children can form secure attachments, learn to understand and process their emotions, and experience the confidence that they can be themselves.",
      introText2:
        "Our approach is based on, among other things, the attachment theory of John Bowlby and Mary Ainsworth, who argue that children need a secure base from which to explore the world. Emmi Pikler and the Gordon Method (by Thomas Gordon) are also important sources of inspiration for our respectful communication and the centrality of the relationship.",

      whyEssential: "Why emotional safety is essential",
      benefits: {
        dareExplore: {
          title: "Dare to explore",
          description: "Children feel safe to try new things and take risks",
        },
        emotionalRegulation: {
          title: "Better emotional regulation",
          description:
            "Learn to deal with emotions, frustration and disappointments",
        },
        positiveSelf: {
          title: "Positive self-image",
          description: "Develop strong social skills and confidence",
        },
        lastingRelationships: {
          title: "Lasting relationships",
          description: "Build secure attachments with adults and peers",
        },
      },

      inPractice: "In practice",
      practices: {
        familiarFaces: {
          title: "Familiar faces and predictability",
          description:
            "We work with a permanent team of childcare professionals for each group. This creates familiarity and trust. Children know who is caring for them and don't have to constantly adjust.",
          dailyStructure: "Daily structure",
          dailyStructureDesc:
            "The daily structure is clear and predictable. Transitions are announced well in advance and accompanied by rituals (such as songs or short explanations), so children can prepare and aren't caught off guard. This prevents unnecessary stress.",
        },
        individualAttention: {
          title: "Attention to the individual child",
          description:
            "We provide individual attention, including within the group. By carefully observing, we identify each child's needs. Sometimes that means a hug, sometimes a moment of rest, sometimes a helping hand or a listening ear.",
          staffApproach: "Educational staff approach",
          approaches: {
            eyeLevel: "Communicate at eye level, with open body language",
            acknowledgeFeelings: "Actively naming and acknowledging feelings",
            neverReject: "Never reject or minimize emotions",
            clearBoundaries: "Set clear boundaries in a respectful, loving way",
          },
        },
        parentalTrust: {
          title: "Parental trust",
          description:
            "Emotional safety isn't just created within childcare; it starts with trust between parents and childcare staff. We consciously invest in the relationship with parents.",
          parentPartnership: "Parent partnership",
          partnerships: {
            warmHandover: "Warm handover at drop-off and pick-up moments",
            openCommunication: "Open, honest communication without judgement",
            takeConcerns: "Take parents' concerns seriously",
            involveParents: "Involve parents in their child's development",
          },
        },
      },

      trainingDevelopment: "Training and development of educational staff",
      trainingIntro:
        "To achieve this, we expect our educational staff to develop specific competencies and continuously improve their skills in emotional safety practices.",
      staffExpectations: {
        selfReflective: {
          title: "Self-reflective work",
          description:
            "Regularly reflecting on your own actions and communication",
        },
        emotionalTraining: {
          title: "Emotional communication training",
          description:
            "Trained in emotional communication with children through coaching and training",
        },
        developmentalKnowledge: {
          title: "Developmental psychology knowledge",
          description:
            "Understanding children's behavior and emotions in context",
        },
        timeToConnect: {
          title: "Taking time to connect",
          description: "Making space to connect, even during busy times",
        },
        visiblySensitive: {
          title: "Being visibly sensitive",
          description: "Seeing, reflecting on and supporting children",
        },
      },
      continuousDevelopment: "Continuous development",
      continuousDevDesc:
        "We facilitate this through regular pedagogical consultations, training sessions, and further education, where this topic receives structural attention. Emotional safety is not an afterthought—it is the core of our pedagogical quality.",

      creatingSafeSpaces: "Creating safe spaces",
      secureEnvironment: {
        title: "Secure environment",
        description:
          "Our spaces are designed to feel like a second home, with cozy corners, soft lighting, and familiar faces that create a sense of belonging and security for every child.",
      },
      emotionalConnection: {
        title: "Emotional connection",
        description:
          "Every interaction is an opportunity to build trust. We listen, validate feelings, and respond with empathy, helping children develop emotional intelligence and resilience.",
      },

      experienceEnvironment: "Experience our caring environment",
      experienceSubtitle:
        "See how we create emotional safety and build trusting relationships with every child",
      cta: {
        scheduleVisit: "Schedule a visit",
        learnMore: "Learn more",
      },
    },
    footer: {
      connectWith: "Connect with Mini Avonturiers",
      email: "info@mini-avonturiers.nl",
      aboutUs: "About us",
      services: "Services",
      dailyRoutine: "Daily routine",
      registerNow: "Register now",
      address: "Address",
      addressText: "Lindenhof 3,",
      addressText2: "3442 GT Woerden",
      telephone: "Telephone",
      phoneNumber: "+31 6 16504171",
      copyright: "© 2025 Mini Avonturiers. All rights reserved.",
    },
  },

  nl: {
    // Vision page
    vision: {
      title: "Pedagogische visie",
      subtitle:
        "Onze benadering van kinderopvang is gebaseerd op wetenschappelijk onderzoek en beproefde pedagogische principes",
      section21: {
        title: "Onze visie op kinderopvang",
        content:
          "Bij Mini Avonturiers geloven we dat elk kind uniek is en floreert in een omgeving die verwondering, educatief spel en een duidelijke structuur bevordert. We bieden kinderen een veilige ruimte waar zij, onder professionele begeleiding, kunnen uitgroeien tot nieuwsgierige, sociale en zelfstandige individuen. We richten ons op de vier kernwaarden van Riksen-Walraven: emotionele veiligheid, persoonlijke en sociale vaardigheden, en normen en waarden.",
      },
      section22: {
        title: "Onze kijk op kinderen",
        content:
          "Bij Mini Avonturiers staat het kind centraal, in al zijn individualiteit. We omarmen de overtuiging dat elk kind uniek is, met zijn eigen karakter, tempo, ontwikkelingsverloop en innerlijke motivatie om te leren. Deze visie sluit aan bij het beeld van het competente kind, zoals benadrukt in de Reggio Emilia-benadering. In deze pedagogiek wordt het kind niet gezien als een leeg vat dat gevuld moet worden, maar als een actieve, krachtige onderzoeker die de wereld verkent op basis van zijn eigen interesses.",
        piaget: {
          title: "Jean Piaget's theorie",
          content:
            "Jean Piaget onderschrijft dit idee ook in zijn theorie van cognitieve ontwikkeling. Volgens hem zijn kinderen van nature geneigd om te experimenteren, hypothesen te toetsen en ervaringen betekenis te geven. Daarom bieden we kinderen ruimte niet alleen om te spelen, maar ook om fouten te maken, nieuwe dingen uit te proberen en zelf inzichten op te doen.",
        },
        bowlby: {
          title: "John Bowlby's hechtingstheorie",
          content:
            "Een veilige basis is essentieel. Zoals John Bowlby's hechtingstheorie aantoont, is een veilige hechting een fundamentele voorwaarde voor gezonde sociaal-emotionele ontwikkeling. Bij Mini Avonturiers bieden we daarom een warme, stabiele en liefdevolle omgeving waar kinderen zich gezien, gehoord en gewaardeerd voelen. Alleen dan kunnen ze zich openen voor de wereld om hen heen.",
        },
      },
      section23: {
        title: "Onze visie op ontwikkeling",
        content:
          "We beschouwen ontwikkeling als een holistisch proces waarin het kind zich gelijktijdig ontwikkelt in meerdere domeinen: sociaal, emotioneel, motorisch, cognitief en creatief. Deze visie is geïnspireerd op de theorie van Lev Vygotsky, die benadrukt dat ontwikkeling plaatsvindt binnen sociale interactie en dat de omgeving, met name volwassenen en meer ervaren leeftijdsgenoten, een cruciale rol speelt.",
        playTool: {
          title: "Spel als ontwikkelingshulpmiddel",
          content:
            'Spel is het belangrijkste ontwikkelingsmiddel dat we gebruiken. Niet als tijdverdrijf, maar als middel voor leren en groei. Spel bevordert creativiteit, probleemoplossende vaardigheden, taalontwikkeling, samenwerking, motorische vaardigheden en nog veel meer. Dit idee wordt ondersteund door Maria Montessori, die benadrukte dat "spel het werk is van het kind." Haar concept leidt onze benadering.',
        },
        thematicApproach: {
          title: "Thematische benadering",
          content:
            'Binnen onze thematische benadering, gebaseerd op actuele ervaringen, creëren we rijke leeromgevingen waarin kinderen zelf kunnen ontdekken, experimenteren, creëren en reflecteren. Deze thema\'s worden niet top-down gepresenteerd, maar ontstaan vaak vanuit het kind zelf. Dit sluit aan bij de Reggio Emilia-visie, die kinderen ziet als "een honderd talen"—met andere woorden, er zijn talloze manieren waarop kinderen zich uiten, die allemaal serieus genomen moeten worden.',
        },
        staffRole: {
          title: "Rol van onderwijzend personeel",
          content:
            "Ons onderwijzend personeel speelt een begeleidende en observerende rol: zij creëren de voorwaarden, stellen vragen, reflecteren op gedrag en ondersteunen zonder te sturen. Ze passen de principes van de Gordon Methode toe (door Thomas Gordon): luisteren zonder oordeel, duidelijk communiceren en problemen oplossen door wederzijds respect.",
        },
      },
      section24: {
        title: "Corrigeren en Bevestigen",
        behaviorCommunication: {
          title: "Gedrag als communicatie",
          content:
            "Gedrag is communicatie. In overeenstemming met de Emmi Pikler-aanpak en niet-directieve pedagogiek, vinden we het belangrijk om altijd te overwegen wat een kind probeert uit te drukken door zijn gedrag. Ongewenst gedrag is geen teken van opzet of onwil, maar vaak een signaal van frustratie, machteloosheid of een onvervulde behoefte.",
        },
        respectfulApproach: {
          title: "Respectvolle benadering",
          content:
            "Wanneer uitdagend gedrag zich voordoet, hanteren we een respectvolle en ondersteunende benadering. We gaan in gesprek met het kind, adresseren hun gevoelens en verwoorden wat ze mogelijk ervaren. Dit helpt kinderen zelfregulatie te ontwikkelen, een belangrijk aspect van sociaal-emotionele groei.",
        },
        positiveReinforcement: {
          title: "Positieve bekrachtiging",
          content:
            "We stellen grenzen consequent en liefdevol, met duidelijke regels, een consistent ritme en voorspelbaarheid. We geloven in de kracht van positieve bekrachtiging, zoals bepleit in Alfie Kohn's theorie. Door gewenst gedrag oprecht te identificeren en te waarderen, vergroten we de intrinsieke motivatie van het kind en versterken we hun zelfvertrouwen.",
        },
      },
      section25: {
        title: "Basisprincipes in de praktijk",
        content:
          "Onze dagelijkse pedagogische praktijk is opgebouwd uit vijf stevige pijlers, die voortdurend met elkaar in wisselwerking staan. Deze basisprincipes zijn geïnspireerd op wetenschappelijk onderbouwde pedagogische visies en komen tot uiting in ons handelen, de inrichting van de ruimte en de organisatie van de dag.",
        principles: {
          sensitiveResponsiveness: {
            title: "1. Gevoelige responsiviteit",
            content:
              "Gebaseerd op Bowlby's hechtingstheorie en verder ontwikkeld door Mary Ainsworth, betekent gevoelige responsiviteit dat verzorgers de signalen van het kind tijdig, nauwkeurig en liefdevol waarnemen. We zijn emotioneel beschikbaar en responsief voor de behoeften van het kind, of het nu gaat om troost, aandacht, voedsel of rust. Deze responsiviteit vormt de basis voor een veilige hechting, die op zijn beurt de basis vormt voor verdere ontwikkeling.",
          },
          respectAutonomy: {
            title: "2. Respect voor autonomie",
            content:
              "We willen kinderen het vertrouwen en de ruimte geven om zelfstandig te handelen. Of het nu gaat om zelfstandig eten, keuzes maken of conflicten oplossen, we ondersteunen, maar nemen het niet over. Dit bevordert niet alleen autonomie, maar ook een gevoel van verantwoordelijkheid en zelfvertrouwen bij kinderen.",
          },
          structureRhythm: {
            title: "3. Structuur en ritme",
            content:
              "Kinderen gedijen in een voorspelbare en georganiseerde omgeving. Een consistente dagelijkse structuur draagt bij aan innerlijke rust en balans. Ritme biedt stabiliteit, en terugkerende rituelen (zoals liedjes, kringen en overgangen) versterken gevoelens van veiligheid en samenhang. Daarom hanteren we een weloverwogen dagschema met voldoende balans tussen activiteit en rust.",
          },
          activeInvolvement: {
            title: "4. Actieve betrokkenheid van onderwijzend personeel",
            content:
              "De kracht van ons onderwijzend personeel ligt in hun aanwezigheid, aandacht en reflectie. Ze zijn geen toeschouwers, maar mede-ontdekkers. Met een open geest en constante observatie begeleiden ze het ontwikkelingsproces van het kind. We investeren in professionele ontwikkeling en zelfreflectie, zodat ons personeel voortdurend hun vaardigheden ontwikkelt.",
          },
          positiveInteraction: {
            title: "5. Positieve interactie",
            content:
              "Taal is belangrijk. De manier waarop we met kinderen praten, bepaalt hoe ze zichzelf en de wereld ervaren. We gebruiken positieve, uitnodigende en respectvolle taal. Conflicten worden besproken, niet bestraft. Complimenten richten zich op inspanning en proces. Deze vorm van communicatie versterkt de relatie van het kind, competentie en autonomie—drie basispsychologische behoeften volgens de zelfdeterminatietheorie van Deci 's en Ryan.",
          },
        },
      },
      section26: {
        title: "Evaluatie en update",
        content:
          "Ons pedagogische beleidsplan wordt jaarlijks geëvalueerd in samenwerking met het team, de pedagogische coach en de oudercommissie. We verzamelen feedback uit het veld, bespreken nieuwe inzichten en actuele ontwikkelingen. We passen het beleid daar waar nodig aan om ervoor te zorgen dat het actueel, werkbaar en in lijn met onze visie blijft.",
      },
      documentsSection: {
        documents: "Documenten & Beleidsplannen",
        kdv: "KDV Pedagogisch Beleidsplan",
        bso: "BSO Pedagogisch Beleidsplan",
        safety: "Beleid Veiligheid & Gezondheid",
      },
    },

    nav: {
      home: "Home",
      about: "Over ons",
      insideus: "Binnen Mini Avonturiers",
      vision: "Groei en verbetering",
      whatWeOffer: "Wat Wij Bieden",
      // Wat Wij Bieden
      services: "Kinderopvangprogramma’s",
      transportationPolicy: "Vervoer & ophalen",
      nutrition: "Gezonde maaltijden & voeding ",
      ipadSystem: "Digitale hulpmiddelen",

      ourDay: "Onze dag",
      learningGrowth: "Leren & groeien",
      familyConnection: "Familie verbinding",
      healthSafety: "Gezondheid & veiligheid",

      // Meer - Kolom 1
      dailyRoutine: "Dagelijkse routines",
      emotionalSafety: "Emotionele veiligheid",
      activities: "Maaltijden & activiteiten",

      // Meer - Kolom 2
      horizontalGroups: "Leeftijdsgroep structuur",
      educationalStaff: "Onderwijzend personeel",
      receivingPolicy: "Inwerkbeleid",

      // Meer - Kolom 3
      parentCollaboration: "Oudercommunicatie",
      faq: "Veelgestelde vragen",

      // Meer - Kolom 4
      safetyHygiene: "Hygiëne",
      openingClosingChecklist: "Opening/Sluiting",

      // Overig
      contact: "Contact",
      register: "Registreren",
      registerChild: "Kind registreren",
      registerTour: "Rondleiding",
      more: "Meer",
      login: "Inloggen",
      logout: "Uitloggen",
    },

    openingClosingChecklist: {
      title: "Opening en Sluiting Checklist",
      subtitle:
        "Gestructureerde dagelijkse procedures voor veiligheid en continuïteit",
      intro:
        "Bij Mini Avonturiers vinden we het belangrijk dat elke dag zorgvuldig, veilig en gestructureerd begint en eindigt. Daarom gebruiken we een standaard checklist voor opening en sluiting, die elke kinderopvangmedewerker volgt. Deze checklist zorgt ervoor dat alle noodzakelijke controles en voorbereidingen zijn voltooid voordat de kinderen arriveren en nadat de laatste is opgehaald.",
      intro2:
        "Door systematisch deze procedure te volgen, garanderen we veiligheid, hygiëne, rust en continuïteit in onze zorg.",

      section1: {
        title: "Doel van de checklist",
        description: "De checklist dient verschillende doelen:",
        purposes: [
          "Zorgen voor fysieke veiligheid van de ruimte en materialen.",
          "Zorgen voor een hygiënische omgeving voor een gezonde start van de dag.",
          "Voorspelbare en rustige start voor kinderen en medewerkers.",
          "Duidelijke overdracht tussen vroege en late diensten.",
          "Verantwoordingsinstrument voor managers en GGD.",
        ],
        note: "Elke medewerker tekent af op wat er is gedaan, zodat er controle, duidelijkheid en overdraagbaarheid is binnen het team.",
      },

      section2: {
        title: "Opening Checklist (ochtend)",
        description:
          "Voor aanvang van de kinderopvang (voor 7:00 uur) voert de medewerker de volgende stappen uit:",
        subsections: {
          safety: {
            title: "Algemene veiligheid",
            points: [
              "Controleren dat het gebouw goed is afgesloten (geen open ramen/deuren).",
              "Indoor en outdoor verlichting activeren indien nodig.",
              "Controle van het alarmsysteem bij binnenkomst.",
            ],
          },
          firstAid: {
            title: "Eerste hulp en hygiëne",
            points: [
              "Controle van de EHBO-kit (aanwezigheid, vervaldatum van materialen).",
              "Controleren dat er voldoende luiers, schoonmaakdoekjes en handschoenen beschikbaar zijn.",
              "Controle van de verschoonruimte: schoon, opgeruimd, voldoende materialen.",
            ],
          },
          cleaning: {
            title: "Schoonmaken en ventileren",
            points: [
              "Ventileren van ruimtes bij binnenkomst (ramen/deuren openen op droge dagen).",
              "Visueel controleren van keuken, toiletten en groep op netheid.",
              "Controleren of vuilnisbakken zijn geleegd.",
              "Opstarten van luchtcirculatiesysteem indien van toepassing.",
            ],
          },
          materials: {
            title: "Speelmaterialen & inrichting",
            points: [
              "Controleren van speelplekken en tafels op veiligheid (geen losse onderdelen of scherpe randen).",
              "Controle leeftijdsbereik: zijn geschikte materialen beschikbaar?",
              "Aanvullen van speelgoed indien nodig en verwijderen van vuile materialen.",
            ],
          },
          preparation: {
            title: "Voorbereiden voor overdracht",
            points: [
              "Voorbereiden van tablet/iPad voor oudercommunicatie.",
              "Doornemen van groepsbezetting en bijzonderheden van de vorige dag.",
              "Mentoren identificeren eventuele aandachtspunten.",
            ],
          },
        },
      },

      section3: {
        title: "Sluiting Checklist (einde dag)",
        description:
          "Na het ophalen van het laatste kind wordt de volgende checklist voltooid:",
        subsections: {
          childControl: {
            title: "Kindcontrole en Overdracht",
            points: [
              "Controleren dat alle kinderen zijn uitgeschreven via het systeem. Oudercontacten zijn voltooid of geregistreerd.",
              "Eventuele bijzonderheden worden genoteerd voor de volgende dag.",
            ],
          },
          spacePreparation: {
            title: "Ruimte gereedmaken voor sluiting",
            points: [
              "Schoonmaken van tafels en vloeren, opruimen van speelgoed.",
              "Opruimen van de keuken, legen van de vuilnisbakken.",
              "Controleren en aanvullen van toiletten en verschoonfaciliteiten.",
            ],
          },
          technology: {
            title: "Techniek en gebouw",
            points: [
              "Controleren van ramen: gesloten en vergrendeld.",
              "Controleren van koelkast: afdekken van producten, controleren van temperatuur.",
              "Uitschakelen van apparaten (lampen, tablet, koffiezetapparaat).",
              "Activeren van alarmsysteem, vergrendelen van voordeur.",
            ],
          },
        },
        note: "Elke medewerker die de opvang sluit tekent af en noteert eventuele bijzonderheden (zoals resterende voorraad, vergeten spullen, of technische storingen). Aan het einde van de week is er speciale aandacht voor voorraadbeheer en bestellingen.",
      },

      section4: {
        title: "Verantwoordelijkheid en follow-up",
        points: [
          "Elke medewerker is verantwoordelijk voor het correct en volledig invullen van de checklist.",
          "Manager controleert output op steekproefbasis (bijv. EHBO-voorraad).",
          "Afwijkingen, schade of onveilige situaties worden direct gemeld aan de locatiemanager.",
          "Checklist wordt digitaal of op papier bewaard per halve dag (archiveringsplicht vanwege GGD).",
        ],
      },
    },

    safetyHygiene: {
      hero: {
        title: "Veiligheid, hygiëne & sfeer",
        subtitle:
          "Een veilige, schone en koesterende omgeving creëren voor ieder kind",
      },
      intro: {
        title: "De veiligheid van uw kind is onze prioriteit",
        text1:
          "Bij Mini Avonturiers hanteren we de hoogste normen op het gebied van veiligheid, hygiëne en sfeer, zodat elk kind zich veilig, gezond en gelukkig voelt in onze zorg.",
        text2:
          "Onze uitgebreide protocollen en zorgvuldig ontworpen ruimtes creëren een omgeving waarin kinderen zich vol vertrouwen kunnen ontwikkelen, leren en groeien.",
        commitmentTitle: "Onze toewijding",
        commitments: [
          "Strikte veiligheidsprotocollen",
          "Regelmatige hygiënepraktijken",
          "Vreedzame leeromgeving",
          "Professionele training van medewerkers",
        ],
      },
      physicalSafety: {
        title: "Fysieke veiligheid en GGD-richtlijnen",
        subtitle:
          "Wij houden ons aan strikte GGD-richtlijnen om de hoogste normen van fysieke veiligheid voor alle kinderen te waarborgen.",
        complianceTitle: "GGD-naleving",
        inspectionsTitle: "Regelmatige inspecties",
        inspections: [
          "Jaarlijkse GGD-veiligheidsinspecties",
          "Maandelijkse interne veiligheidsaudits",
          "Voortdurende veiligheidstraining voor personeel",
          "Evaluatie van noodprocedures",
        ],
        standardsTitle: "Veiligheidsnormen",
        standards: [
          "Leeftijdsgerichte materialen en apparatuur",
          "Beveiligde toegang en uitgangen van het gebouw",
          "Correcte beroepskracht-kindratio’s",
          "EHBO-gediplomeerd personeel",
        ],
      },
      safetyGuidelines: [
        {
          title: "Gebouwveiligheid",
          description:
            "Alle faciliteiten voldoen aan strikte GGD-veiligheidsnormen met regelmatige inspecties",
          icon: "🏢",
          color: "bg-blue-500",
        },
        {
          title: "Apparatuurveiligheid",
          description:
            "Alle speelgoed en materialen zijn leeftijdsgericht en worden regelmatig gecontroleerd",
          icon: "🧸",
          color: "bg-green-500",
        },
        {
          title: "Noodprocedures",
          description: "Uitgebreide noodprotocollen en getraind personeel",
          icon: "🚨",
          color: "bg-red-500",
        },
        {
          title: "Toezicht",
          description:
            "Constante supervisie met passende beroepskracht-kindratio’s",
          icon: "👁️",
          color: "bg-purple-500",
        },
      ],
      hygiene: {
        title: "Hygiëneprotocollen",
        subtitle:
          "Uitgebreide hygiënepraktijken zorgen voor een gezonde omgeving voor alle kinderen en medewerkers.",
      },
      hygieneProtocols: [
        {
          title: "Dagelijkse schoonmaak",
          description:
            "Grondige dagelijkse schoonmaak van alle oppervlakken, speelgoed en apparatuur",
          icon: "🧹",
          details: [
            "Desinfectie van veelgebruikte oppervlakken",
            "Regelmatige speelgoedreiniging en rotatie",
            "Sanitisatie van eetruimtes",
          ],
          color: "bg-green-500",
        },
        {
          title: "Persoonlijke hygiëne",
          description:
            "Aanleren en onderhouden van goede handwas- en hygiënegewoonten",
          icon: "🧼",
          details: [
            "Regelmatige handwasroutines",
            "Correcte luierwisselprocedures",
            "Ziektepreventieprotocollen",
          ],
          color: "bg-blue-500",
        },
        {
          title: "Voedselveiligheid",
          description:
            "Strikte veiligheidsnormen voor voedselbereiding en -hantering",
          icon: "🍽️",
          details: [
            "Veilige opslag en bereiding van voedsel",
            "Allergiebewustzijn en -beheer",
            "Schone keuken- en eetruimtes",
          ],
          color: "bg-amber-500",
        },
        {
          title: "Ziektebeheer",
          description:
            "Duidelijke protocollen voor het omgaan met ziekte en het voorkomen van verspreiding",
          icon: "🏥",
          details: [
            "Beleid voor zieke kinderen",
            "Communicatie met ouders",
            "Richtlijnen voor terugkeer naar opvang",
          ],
          color: "bg-purple-500",
        },
      ],
      spaces: {
        title: "Educatief ingerichte ruimtes",
        subtitle:
          "Onze ruimtes zijn zorgvuldig ontworpen om leren, ontwikkeling en veiligheid voor kinderen van alle leeftijden te ondersteunen.",
      },
      educationalSpaces: [
        {
          title: "Leeftijdsgericht ontwerp",
          description:
            "Ruimtes speciaal ontworpen voor verschillende ontwikkelingsfasen",
          icon: "🎯",
          color: "bg-amber-500",
          features: [
            "Veilige speelzones per leeftijdsgroep",
            "Passend meubilair en materialen",
            "Toegankelijke middelen en bronnen",
          ],
        },
        {
          title: "Leerzones",
          description:
            "Toegewijde gebieden voor verschillende soorten leeractiviteiten",
          icon: "📚",
          color: "bg-blue-500",
          features: [
            "Rustige leeshoekjes",
            "Creatieve kunststations",
            "Actieve speelruimtes",
            "Zintuiglijke ontdekkingsplekken",
          ],
        },
        {
          title: "Buitenruimtes",
          description:
            "Veilige, stimulerende buitenomgevingen voor spel en leren",
          icon: "🌳",
          color: "bg-green-500",
          features: [
            "Leeftijdsgericht speeltoestellen",
            "Natuurlijke speelelementen",
            "Schaduwplekken en weerbescherming",
            "Veilig hekwerk en toezicht",
          ],
        },
        {
          title: "Technologie-integratie",
          description:
            "Doordacht gebruik van technologie ter ondersteuning van leren",
          icon: "💻",
          color: "bg-purple-500",
          features: [
            "Educatieve apps en programma’s",
            "Digitale documentatietools",
            "Oudercommunicatieplatforms",
            "Richtlijnen voor schermtijd",
          ],
        },
      ],
      atmosphere: {
        title: "Rust en orde in de sfeer",
        subtitle:
          "Een rustige, ordelijke omgeving creëren die leren, welzijn en positief gedrag bevordert.",
        calmTitle: "Rust creëren",
        calmPoints: [
          "Zachte verlichting en natuurlijke materialen",
          "Rustige zones voor ontspanning",
          "Zachte overgangen tussen activiteiten",
          "Positieve gedragsbekrachtiging",
        ],
        orderTitle: "Orde handhaven",
        orderPoints: [
          "Duidelijke routines en verwachtingen",
          "Georganiseerde opslag- en speelruimtes",
          "Consistente regels en grenzen",
          "Personeel als voorbeeld van kalm gedrag",
        ],
      },
      atmosphereElements: [
        {
          title: "Rustige omgeving",
          description: "Een vredige, stressvrije sfeer creëren voor kinderen",
          icon: "😌",
          color: "bg-blue-500",
        },
        {
          title: "Gestructureerde routines",
          description:
            "Voorspelbare schema’s die veiligheid en stabiliteit bieden",
          icon: "📅",
          color: "bg-green-500",
        },
        {
          title: "Positieve interacties",
          description:
            "Warme, ondersteunende relaties tussen personeel en kinderen",
          icon: "🤗",
          color: "bg-amber-500",
        },
        {
          title: "Geordende ruimtes",
          description:
            "Goed georganiseerde omgevingen die concentratie en rust bevorderen",
          icon: "🗂️",
          color: "bg-purple-500",
        },
      ],
      cta: {
        title: "Ervaar onze Veilige omgeving",
        subtitle:
          "Zie met eigen ogen hoe wij de hoogste normen van veiligheid, hygiëne en sfeer hanteren. Plan een bezoek om onze faciliteiten te bekijken en ons team te ontmoeten.",
        button: "Meer weten over onze veiligheidsnormen",
      },
    },

    parentCollaboration: {
      hero: {
        title: "Samenwerking met ouders en vertrouwen",
        subtitle:
          "Sterke partnerschappen bouwen voor de ontwikkeling van uw kind",
      },
      intro: {
        title: "Onze visie op partnerschap",
        text1:
          "Bij Mini Avonturiers zien we ouders als de eerste en belangrijkste opvoeders in het leven van hun kind. We erkennen dat ouders hun kinderen beter kennen dan wie dan ook.",
        text2:
          "Daarom streven wij naar een open, transparante en gelijkwaardige samenwerking, gebaseerd op wederzijds vertrouwen, respect en dialoog. Voor ons zijn ouders partners in de ontwikkeling van het kind.",
        supportTitle: "Waarom partnerschap belangrijk Is",
        supportText:
          "Een goede samenwerking tussen pedagogische medewerkers en ouders versterkt niet alleen het gevoel van veiligheid van een kind, maar zorgt ook voor betere afstemming en continuïteit in opvoeding en begeleiding.",
      },
      pillars: {
        title: "Onze visie: kinderen opvoeden is samenwerken",
        subtitle:
          "Onze aanpak sluit aan bij de principes van het pedagogische partnerschap, zoals omschreven in de pedagogische kaders van het NJi en de Wet Kinderopvang.",
      },
      partnershipPillars: [
        {
          title: "Gelijkheid",
          description:
            "Ouders en professionals hebben ieder hun eigen expertise.",
          icon: "🤝",
          color: "bg-amber-500",
        },
        {
          title: "Wederzijds vertrouwen",
          description:
            "Een open houding is de basis voor constructieve samenwerking.",
          icon: "💝",
          color: "bg-rose-500",
        },
        {
          title: "Transparante communicatie",
          description: "Duidelijk, tijdig en in begrijpelijke taal.",
          icon: "💬",
          color: "bg-blue-500",
        },
        {
          title: "Participatie & betrokkenheid",
          description:
            "Ouders hebben invloed, geven input en zijn actief betrokken.",
          icon: "👥",
          color: "bg-green-500",
        },
      ],
      practice: {
        title: "Praktijkontwerp",
        subtitle:
          "Vier belangrijke gebieden waar wij onze visie op partnerschap in praktijk brengen",
        featuresLabel: "Dit houdt in:",
      },
      practiceAreas: [
        {
          title: "Dagelijkse overdracht & iPad-systeem",
          subtitle: "Warme overdracht en digitale transparantie",
          icon: "📱",
          description:
            "Elke dag zorgen wij voor een warme en gastvrije overdracht bij het brengen en halen. Ouders worden geïnformeerd over het dagschema, maaltijden, dutjes, eventuele bijzonderheden en het gedrag van hun kind.",
          features: [
            "Ontvang foto’s en updates over activiteiten",
            "Inzicht in slaap- en eetmomenten",
            "Berichten uitwisselen met de pedagogische medewerker",
            "Toegang tot het rooster, observaties en details",
          ],
        },
        {
          title: "Mentor-oudergesprekken",
          subtitle: "Persoonlijke ontwikkelingsbegeleiding",
          icon: "👨‍👩‍👧‍👦",
          description:
            "Elk kind heeft een vaste mentor: een pedagogische medewerker verantwoordelijk voor de ontwikkeling en het welzijn van het kind. Er vinden minstens twee keer per jaar mentorgesprekken plaats (vaker indien gewenst).",
          features: [
            "De ontwikkeling op sociaal, motorisch, cognitief en emotioneel vlak wordt besproken",
            "Observaties worden gedeeld",
            "Ruimte voor vragen, zorgen of wensen van ouders",
            "Bespreking van eventuele vervolgacties",
          ],
        },
        {
          title: "Laagdrempelige communicatie",
          subtitle: "Open-deurbeleid en toegankelijke ondersteuning",
          icon: "🚪",
          description:
            "Wij hanteren een open-deurbeleid. Ouders zijn altijd welkom met vragen, korte gesprekken of om iets te delen.",
          features: [
            "Open-deurbeleid voor ouders",
            "Getraind personeel in toegankelijke communicatie",
            "Actief luisteren en oplossingsgerichte aanpak",
            "Vaste contactpersonen op locatie",
          ],
        },
        {
          title: "Ouderbijeenkomsten & brainstormen",
          subtitle: "Regelmatige betrokkenheid en samen leren",
          icon: "📅",
          description:
            "Wij organiseren minstens twee keer per jaar ouderbijeenkomsten. Deze bijeenkomsten kunnen informatief of sociaal van aard zijn.",
          features: [
            "Thema-avonden over opvoeding en ontwikkeling",
            "Kijkavonden in de groep met uitleg",
            "Brainstormsessies over beleid en methoden",
            "Sociale evenementen voor gemeenschapsvorming",
          ],
        },
      ],
      feedback: {
        title: "Feedbackcultuur",
        subtitle:
          "Wij zijn voortdurend in ontwikkeling en staan open voor feedback. Ouders kunnen suggesties, zorgen of complimenten op verschillende manieren delen:",
        note: "Feedback wordt niet gezien als kritiek, maar als waardevolle input om onze kwaliteit continu te verbeteren.",
      },
      feedbackMethods: [
        {
          title: "Persoonlijk gesprek",
          description: "Directe communicatie met pedagogische medewerker",
          icon: "💭",
        },
        {
          title: "Digitaal ouderportaal",
          description: "Online feedback via ons digitale systeem",
          icon: "💻",
        },
        {
          title: "Evaluatiemomenten",
          description: "Gestructureerde feedback tijdens gesprekken",
          icon: "📊",
        },
        {
          title: "Ouderenquêtes",
          description:
            "Regelmatige tevredenheidsenquêtes en feedbackformulieren",
          icon: "📝",
        },
      ],
      committee: {
        title: "Rol van de oudercommissie",
        subtitle:
          "Onze oudercommissie speelt een actieve rol binnen de kinderopvangorganisatie",
        activeRole: "Actieve adviesrol",
        text1:
          "De commissie fungeert als klankbord en stem van ouders, met wie wij samen streven naar kwaliteit en betrokkenheid.",
        text2:
          "Zij adviseren over belangrijke onderwerpen die direct invloed hebben op de ervaring van uw kind en de behoeften van uw gezin.",
        areasTitle: "Adviesgebieden:",
      },
      parentCommitteeAreas: [
        "Het pedagogische beleid",
        "Voeding, veiligheid en gezondheid",
        "Openingstijden en prijzen",
        "Klachtenprocedure en oudercommunicatie",
      ],
      cta: {
        title: "Klaar om ons partnerschap te starten",
        subtitle:
          "Word lid van onze gemeenschap van ouders en pedagogische medewerkers die samenwerken voor de optimale ontwikkeling van uw kind. Plan een gesprek om meer te leren over onze samenwerking.",
        button: "Plan een gesprek",
      },
    },

    // DUTCH (NL)

    ipadSystem: {
      hero: {
        title: "iPad-systeem & oudercommunicatie",
        subtitle: "Blijf verbonden met de dagelijkse avonturen van uw kind",
      },
      intro: {
        title: "Digitale verbinding voor gemoedsrust",
        text1:
          "Bij Mini Avonturiers hechten we veel waarde aan open, transparante en betrokken communicatie met ouders. We begrijpen dat het achterlaten van hun kind niet altijd gemakkelijk is voor ouders.",
        text2:
          "Daarom gebruiken we een modern, veilig en gebruiksvriendelijk iPad-systeem met een ouder-app, waarmee ouders in realtime op de hoogte blijven van het welzijn van hun kind — zelfs op afstand.",
        supportTitle: "Digitale steun, geen vervanging",
        supportText:
          "Deze digitale ondersteuning vervangt geen persoonlijk contact, maar is een waardevolle aanvulling die bijdraagt aan vertrouwen, betrokkenheid en afstemming tussen opvang en thuis.",
      },
      appFeatures: {
        title: "Wat kunnen ouders verwachten van de app?",
        subtitle:
          "De ouder-app geeft ouders dagelijks toegang tot uitgebreide informatie over de dag van hun kind.",
        whatYouSee: "Wat u zult zien:",
        items: [
          {
            title: "Foto’s en impressies",
            description:
              "Ouders ontvangen regelmatig foto’s van hun kind tijdens het spelen, knutselen, buitenspelen of deelnemen aan thema-activiteiten.",
            icon: "📸",
            color: "bg-pink-500",
            benefits: [
              "Visueel inzicht in het groepsleven",
              "Versterkt gevoel van betrokkenheid",
              "Legt speciale momenten vast",
              "Toont dagelijkse activiteiten en thema’s",
            ],
          },
          {
            title: "Dagrapporten",
            description:
              "Aan het einde van de dag plaatsen pedagogische medewerkers een kort verslag met beschrijving van activiteiten, sfeer en opvallende momenten.",
            icon: "📝",
            color: "bg-indigo-500",
            benefits: [
              "Wat de kinderen deden (activiteiten, thema)",
              "De sfeer in de groep",
              "Opvallende momenten of observaties",
              "Helpt ouders om met hun kind over de dag te praten",
            ],
          },
          {
            title: "Maaltijden",
            description:
              "Inzicht in wat en hoeveel het kind heeft gegeten, inclusief eventuele bijzonderheden.",
            icon: "🍽️",
            color: "bg-green-500",
            benefits: [
              "Wat en hoeveel het kind heeft gegeten",
              "Eventuele bijzonderheden (gebrek aan eetlust, allergieën)",
              "Dieetvoorkeuren en beperkingen",
              "Voedingsregistratie en afstemming",
            ],
          },
          {
            title: "Slaapschema’s",
            description:
              "Ouders kunnen zien wanneer hun kind sliep, hoe lang en hoe het wakker werd.",
            icon: "😴",
            color: "bg-blue-400",
            benefits: [
              "Wanneer hun kind sliep",
              "Hoe lang ze sliepen",
              "Hoe ze wakker werden",
              "Waardevol voor afstemming van ritmes thuis",
            ],
          },
          {
            title: "Berichtenfunctie",
            description:
              "Ouders kunnen berichten sturen naar de groep en vice versa voor vragen, korte berichten of opmerkingen.",
            icon: "💬",
            color: "bg-yellow-500",
            benefits: [
              "Directe communicatie met medewerkers",
              "Vragen en snelle updates",
              "Geen vervanging voor een gesprek",
              "Handige toevoeging voor kleine zaken",
            ],
          },
        ],
      },
      whyChoose: {
        title: "waarom kiezen wij hiervoor?",
        subtitle:
          "Ons digitale communicatiesysteem is ontworpen met vier belangrijke principes",
        items: [
          {
            title: "Transparantie en vertrouwen",
            description:
              "Ouders hoeven niet te gissen hoe de dag is verlopen. Ze worden voortdurend geïnformeerd, wat bijdraagt aan vertrouwen in de opvang en gemoedsrust tijdens werktijd.",
            icon: "🛡️",
            color: "bg-amber-500",
          },
          {
            title: "Tijdige signalering en afstemming",
            description:
              "Wij kunnen ouders direct informeren over veranderingen in gedrag, eetlust of stemming. Ouders kunnen via de app ook belangrijke informatie met ons delen.",
            icon: "⚡",
            color: "bg-blue-500",
          },
          {
            title: "Stimuleren van betrokkenheid",
            description:
              "De app helpt ouders actief betrokken te blijven bij de ontwikkeling van hun kind, door te volgen, observeren en reageren op de dagelijkse activiteiten.",
            icon: "🤝",
            color: "bg-green-500",
          },
          {
            title: "Documentatie en draagbaarheid",
            description:
              "Alle rapporten, observaties en berichten zijn beschikbaar om terug te kijken. Dit zorgt voor continuïteit en biedt waardevolle input voor gesprekken.",
            icon: "📊",
            color: "bg-purple-500",
          },
        ],
      },
      privacy: {
        title: "Privacy en veiligheid",
        subtitle:
          "Wij werken uitsluitend met systemen die voldoen aan de hoogste normen voor privacy, veiligheid en gegevensbescherming (AVG-conform).",
        items: [
          {
            title: "AVG-conform",
            description:
              "Wij werken uitsluitend met systemen die voldoen aan de hoogste normen voor privacy, veiligheid en gegevensbescherming.",
            icon: "🔒",
            color: "bg-red-500",
          },
          {
            title: "Beveiligde toegang",
            description:
              "Alleen de ouders van het betreffende kind hebben toegang tot de informatie.",
            icon: "👤",
            color: "bg-blue-600",
          },
          {
            title: "Geen publieke delen",
            description:
              "Foto’s en gegevens worden niet openbaar gedeeld en worden alleen bewaard voor de wettelijk vereiste periode.",
            icon: "🚫",
            color: "bg-gray-700",
          },
          {
            title: "Getraind personeel",
            description:
              "Onze medewerkers zijn getraind in zorgvuldig rapporteren en digitale communicatie, met aandacht voor respect, neutraliteit en professionaliteit.",
            icon: "👨‍🏫",
            color: "bg-teal-500",
          },
        ],
        note: "Alleen de ouders van het betreffende kind hebben toegang. Foto’s en gegevens worden niet openbaar gedeeld en alleen voor de wettelijk vereiste periode bewaard.",
      },
      technical: {
        title: "Professionele training & normen",
        text1:
          "Onze medewerkers zijn getraind in zorgvuldig rapporteren en digitale communicatie, met aandacht voor respect, neutraliteit en professionaliteit.",
        text2:
          "Alle rapporten, observaties en berichten zijn beschikbaar voor inzage. Dit waarborgt continuïteit en biedt waardevolle input voor oudergesprekken en evaluaties.",
        benefitsTitle: "Belangrijkste voordelen:",
        benefits: [
          "Realtime updates en transparantie",
          "Veilig, AVG-conform platform",
          "Professionele training van personeel",
          "Uitgebreide documentatie",
        ],
      },
      cta: {
        title: "Klaar om verbonden te blijven?",
        subtitle:
          "Ervaar de gemoedsrust die komt met realtime updates over de dagelijkse avonturen van uw kind. Ontdek meer over ons digitale communicatiesysteem.",
        button: "Lees meer over de app",
      },
    },

    // DUTCH
    receivingPolicy: {
      hero: {
        title: "Beleid: ontvangen & wennen van nieuwe kinderen",
        subtitle: "Een warme, veilige basis voor de reis van ieder kind",
      },
      intro: {
        title: "Een kindgerichte start",
        text1:
          "De eerste ervaringen van een kind op de opvang hebben een grote invloed op hun gevoel van veiligheid, vertrouwen en hechting. Bij Mini Avonturiers besteden we daarom extra zorg en aandacht aan het ontvangen en laten wennen van nieuwe kinderen.",
        text2:
          "Wij geloven dat ieder kind uniek is en zich op zijn eigen manier en tempo moet kunnen aanpassen aan een nieuwe omgeving. Daarom werken wij met een individuele aanpak, afgestemd op de behoeften van zowel het kind als de ouders.",
        supportTitle: "Waarom dit belangrijk is",
        supportText:
          "Een goede start zorgt voor rust bij zowel het kind als de ouders en vormt de basis voor een positieve relatie met de pedagogische medewerkers en de groep.",
      },
      warmIntro: {
        title: "Warme introductie: vanaf het eerste contact",
        subtitle:
          "Het wenproces begint al voor de eerste opvangdag. We bouwen vertrouwen op met ouders en zorgen ervoor dat het kind in een rustige, liefdevolle sfeer kan kennismaken met de nieuwe omgeving.",
        items: [
          {
            title: "Kennismaking & intakegesprek",
            description:
              "Voor de eerste opvangdag vindt er een intakegesprek met de ouders plaats.",
            features: [
              "Ouders vertellen over het kind: karakter, slaappatroon, voeding",
              "Knuffels, voorkeuren, taal, allergieën, hechtingsfiguren",
              "Bijzondere eigenschappen en unieke kenmerken",
              "We bespreken wederzijdse verwachtingen en onze werkwijze",
            ],
            icon: "👨‍👩‍👧‍👦",
          },
          {
            title: "Rondleiding",
            description:
              "Tijdens de rondleiding laten we de ruimtes, groep en medewerkers zien.",
            features: [
              "Laten zien van de ruimtes, groep en medewerkers",
              "Ouders en kind kunnen de sfeer ervaren",
              "Ontmoeten van de pedagogische medewerker(s) en eventuele mentor",
              "De locatie wordt zo wat vertrouwder",
            ],
            icon: "🏠",
          },
        ],
      },
      phases: {
        title: "Wennen in fasen: het kind staat centraal",
        subtitle:
          "Een kind dat bij ons start krijgt de kans om geleidelijk te wennen. Afhankelijk van leeftijd, persoonlijkheid, thuissituatie en eerdere opvangervaringen maken we een wenplan.",
        items: [
          {
            day: "Dag 1",
            duration: "Kort",
            description: "Samen met ouder(s), max. 1 uur in de groep",
            activities: "Rustig observeren en kennismaken",
            icon: "👀",
          },
          {
            day: "Dag 2",
            duration: "Langer",
            description: "Ouder blijft kort, kind blijft alleen achter",
            activities: "Ongeveer 1,5–2 uur zonder ouder",
            icon: "🤗",
          },
          {
            day: "Dag 3",
            duration: "Halve dag",
            description: "Kind blijft alleen, inclusief activiteit",
            activities:
              "Bijvoorbeeld eetmoment en gestructureerde activiteiten",
            icon: "🎨",
          },
          {
            day: "Dag 4+",
            duration: "Volle dag",
            description: "Opbouwen naar een hele dag",
            activities: "Afhankelijk van de behoefte en reactie van het kind",
            icon: "🌟",
          },
        ],
        note: "Dit faseren is niet verplicht of strak. Sommige kinderen hebben meer tijd nodig, anderen voelen zich snel op hun gemak. We stemmen dit flexibel af met ouders en leggen de nadruk op rust, veiligheid en emotioneel welzijn.",
      },
      mentor: {
        title: "De rol van de mentor: nabijheid en vertrouwen",
        subtitle:
          "Ieder kind krijgt vanaf de start een vaste mentor toegewezen. Deze mentor biedt gerichte aanwezigheid en toewijding, waardoor het kind de kans krijgt zich te hechten aan een volwassene.",
        roles: [
          {
            title: "Eerste aanspreekpunt",
            description:
              "De medewerker die actief observeert, troost, begeleidt en vertrouwen geeft aan het kind.",
          },
          {
            title: "Veilige relatieopbouwer",
            description:
              "Verantwoordelijk voor het opbouwen van een veilige relatie met het kind.",
          },
          {
            title: "Procesbewaker",
            description:
              "Degene die het wenproces zorgvuldig bewaakt en hierover communiceert met het team en de ouders.",
          },
          {
            title: "Extra nabijheid",
            description:
              "Biedt het kind extra nabijheid tijdens de wenperiode.",
          },
        ],
        activitiesTitle: "Mentoractiviteiten zijn onder andere:",
        activities: [
          "Dichtbij blijven tijdens het spelen",
          "Troosten op schoot",
          "Samen activiteiten doen",
          "Bekende routines herhalen",
          "Gebruik van vertrouwde voorwerpen",
        ],
        note: "Deze gerichte aanwezigheid en toewijding geeft het kind de kans zich te hechten aan een volwassene, wat essentieel is voor verdere ontwikkeling volgens de hechtingstheorie van Bowlby.",
      },
      parents: {
        title: "Ouders als partners in de wenperiode",
        subtitle:
          "Wij geloven dat ouderbetrokkenheid cruciaal is voor de overgang. We moedigen ouders aan om vertrouwen te hebben, maar ook hun eigen emoties serieus te nemen.",
        items: [
          {
            title: "Actieve observatie",
            description:
              "Ouders worden uitgenodigd om actief te observeren en hun observaties te delen tijdens de wenmomenten.",
          },
          {
            title: "Eenvoudige communicatie",
            description:
              "Eenvoudig contact via het iPad-systeem en mondeling bij halen en brengen.",
          },
          {
            title: "Open gesprek",
            description:
              "We bespreken open hoe het met het kind gaat, welke signalen we oppikken en hoe we zo nodig bijsturen.",
          },
          {
            title: "Altijd beschikbaar",
            description:
              "De mentor staat altijd open voor overleg, ook buiten geplande gesprekken om.",
          },
        ],
        note: "Want als ouders zich veilig voelen, voelen kinderen zich ook veilig.",
      },
      aftercare: {
        title: "Nazorg en evaluatie",
        subtitle:
          "De eerste weken zijn cruciaal. Alles is gericht op het kind zich veilig, verbonden en thuis laten voelen bij Mini Avonturiers.",
        items: [
          {
            title: "Intensieve observatie",
            description:
              "De mentor blijft het kind intensief volgen tijdens de eerste weken.",
          },
          {
            title: "Informele evaluaties",
            description:
              "Korte, informele evaluatiemomenten (mondeling of via de app).",
          },
          {
            title: "6-weekse evaluatie",
            description:
              "Na ongeveer 6 weken vindt er een korte evaluatie en oudergesprek plaats.",
          },
          {
            title: "Extra ondersteuning",
            description:
              "Indien nodig bieden we extra ondersteuning of verlengen we de wenperiode.",
          },
        ],
        note: "Na ongeveer 6 weken vindt er een korte evaluatie en oudergesprek plaats om de wenperiode, ontwikkeling en het welzijn te bespreken.",
      },
      cta: {
        title: "Klaar om de reis van uw kind te starten?",
        subtitle:
          "Ervaar onze warme, kindgerichte aanpak voor het ontvangen van nieuwe gezinnen. Plan een intakegesprek om de wenperiode van uw kind te beginnen.",
        button: "Meer over ons Wenproces",
      },
    },

    // nl.ts
    nutrition: {
      hero: {
        title: "Voeding volgens de schijf van vijf",
        subtitle:
          "Gezonde, gevarieerde en bewuste voeding voor groeiende geesten en lichamen",
      },
      intro: {
        title: "Meer dan alleen eten",
        text1:
          "Bij Mini Avonturiers hechten we veel waarde aan gezonde, gevarieerde en bewuste voeding. Voeding is niet alleen essentieel voor de lichamelijke ontwikkeling van jonge kinderen, maar ook voor hun concentratie, stemming, energiebalans en het ontwikkelen van gezonde eetgewoonten voor de toekomst.",
        text2:
          "Daarom baseren we ons voedingsbeleid op de richtlijnen van het Voedingscentrum en houden we ons aan de principes van de Schijf van Vijf.",
        supportTitle: "Onze aanpak",
        supportText:
          "We streven naar een evenwichtige voeding, met de nadruk op groenten, fruit, volkorenproducten, eiwitten, zuivel, gezonde vetten en voldoende vocht.",
      },
      wheelOfFive: {
        title: "De schijf van vijf",
        subtitle:
          "Onze voedingsbasis gebaseerd op de richtlijnen van het Voedingscentrum",
        items: [
          {
            category: "Groenten & fruit",
            description: "Verse, kleurrijke groenten en fruit",
            icon: "🥬",
            color: "bg-green-500",
          },
          {
            category: "Volkoren",
            description: "Volkoren brood, pasta en zilvervliesrijst",
            icon: "🌾",
            color: "bg-amber-500",
          },
          {
            category: "Eiwitten",
            description: "Peulvruchten, eieren, vis, kip en alternatieven",
            icon: "🥚",
            color: "bg-red-500",
          },
          {
            category: "Zuivel",
            description: "Melk, kaas en zuivelproducten",
            icon: "🥛",
            color: "bg-blue-500",
          },
          {
            category: "Gezonde vetten",
            description: "Noten, zaden en gezonde oliën",
            icon: "🥑",
            color: "bg-purple-500",
          },
        ],
      },
      mealtime: {
        title: "Eten is meer dan voeding: een sociaal moment",
        subtitle:
          "Bij Mini Avonturiers zijn maaltijden meer dan alleen eten: ze zijn ook leerzame, sociale en emotionele momenten.",
        note: "Onze pedagogische medewerkers eten samen met de kinderen aan tafel en zijn rolmodellen. Ze moedigen kinderen aan om nieuwe smaken te proberen op een positieve en niet-dwingende manier, zonder druk of beloning.",
        items: [
          {
            title: "Sociale vaardigheden",
            description: "Samen eten, delen en op elkaar wachten",
            icon: "🤝",
          },
          {
            title: "Tafelmanieren",
            description: 'Leren "alsjeblieft" en "dank je wel" te zeggen',
            icon: "🍽️",
          },
          {
            title: "Onafhankelijkheid",
            description: "Leren zelf te kiezen of te serveren",
            icon: "👶",
          },
          {
            title: "Voedseleducatie",
            description: "Praten over eten: kleuren, smaken, herkomst",
            icon: "🌍",
          },
        ],
      },
      baby: {
        title: "Baby’s: voeding op maat",
        subtitle:
          "Voor baby’s wordt voeding volledig afgestemd op hun individuele behoeften, in nauw overleg met de ouders.",
        note: "We ondersteunen ouders actief bij de overgang naar vaste voeding, met aandacht voor allergieën, voorkeuren en dieetwensen.",
        items: [
          {
            title: "Ritme volgen",
            description:
              "Volgen van het eigen ritme van het kind (geen vaste eetmomenten)",
            icon: "⏰",
          },
          {
            title: "Liefdevolle begeleiding",
            description:
              "Rustige, liefdevolle begeleiding tijdens het voeden (op schoot, met oogcontact)",
            icon: "💝",
          },
          {
            title: "Veilig voeden",
            description:
              "Zittend en veilig voeden, met aandacht voor verzadigingssignalen",
            icon: "🛡️",
          },
          {
            title: "Warme communicatie",
            description:
              "Warme communicatie over overgangsmomenten (bijv. eerste hapjes)",
            icon: "💬",
          },
        ],
      },
      toddler: {
        title: "Peuters & kleuters: gezond, gevarieerd en zelfstandig",
        subtitle:
          "Voor oudere kinderen (1,5–4 jaar) hanteren we een dagelijks schema met vaste eetmomenten.",
        hotMealsTitle: "Warme maaltijden bestaan uit:",
        items: [
          {
            title: "Fruitmomenten",
            description:
              "Meerdere soorten per dag, gesneden en aantrekkelijk gepresenteerd",
            icon: "🍎",
            color: "bg-red-500",
          },
          {
            title: "Volkoren opties",
            description:
              "Volkoren boterhammen of warme lunch (afhankelijk van het dagdeel)",
            icon: "🥪",
            color: "bg-amber-500",
          },
          {
            title: "Gezonde snacks",
            description:
              "Soepstengels, komkommer, cherrytomaten, volkoren crackers",
            icon: "🥒",
            color: "bg-green-500",
          },
          {
            title: "Gezonde drankjes",
            description:
              "Water en lauwe thee standaard, sap alleen bij speciale gelegenheden",
            icon: "💧",
            color: "bg-blue-500",
          },
        ],
        hotMealFeatures: [
          "Variatie in groenten (minstens 2 soorten per maaltijd)",
          "Volkoren pasta, zilvervliesrijst of aardappelen",
          "Eiwitbron (peulvruchten, eieren, vis, kip of vegetarisch)",
          "Geen toegevoegde suikers of kunstmatige smaakstoffen",
          "Minder zout (maximaal 1 gram per jaar voor jonge kinderen)",
          "Voldoende vezels, eiwitten en gezonde vetten",
        ],
      },
      independence: {
        title: "Zelf doen, zelf ervaren",
        subtitle:
          "We moedigen kinderen – op een leeftijdsgerichte manier – aan om zelf te eten, kiezen, proeven en opruimen.",
        note: "We maken van eten een moment van aandacht, rust en verbondenheid.",
        items: [
          {
            title: "Vasthouden en schenken",
            description: "Zelf een beker vasthouden en inschenken (met hulp)",
            icon: "🥤",
          },
          {
            title: "Fruit kiezen",
            description: "Zelf een stuk fruit van het bord kiezen",
            icon: "🍓",
          },
          {
            title: "Opruimen",
            description: "Leren je eigen servies opruimen",
            icon: "🧹",
          },
        ],
      },
      cultural: {
        title: "Cultureel sensitief en inclusief",
        subtitle:
          "We erkennen dat eetgewoonten en rituelen kunnen verschillen per gezin en per cultuur.",
        items: [
          {
            title: "Religieuze voorkeuren",
            description:
              "Respect voor vegetarische of religieuze dieetvoorkeuren",
            icon: "🕊️",
          },
          {
            title: "Culturele rituelen",
            description: "Aandacht voor vaste of symbolische eetmomenten thuis",
            icon: "🏠",
          },
          {
            title: "Geen druk",
            description:
              "Geen algemene verplichting om te 'eten': kinderen mogen aangeven wanneer ze genoeg hebben",
            icon: "🤗",
          },
        ],
      },
      educational: {
        title: "Educatieve waarde van voeding",
        subtitle:
          "Voeding is ook een manier om kinderen bewust te maken van waar eten vandaan komt.",
        note: "Hiermee stimuleren we begrip, betrokkenheid en gezond gedrag.",
        items: [
          {
            title: "Voedselbereiding",
            description: "Fruit of groenten snijden (onder begeleiding)",
            icon: "🔪",
          },
          {
            title: "Leren tellen",
            description: "Stukjes tellen of kleuren benoemen",
            icon: "🔢",
          },
          {
            title: "Gezonde keuzes",
            description:
              'Gesprekken over gezonde keuzes: "Wat vind je lekker? Wat is gezond?"',
            icon: "💭",
          },
          {
            title: "Tuinactiviteiten",
            description: "Soms zaaien en oogsten in een mini moestuin",
            icon: "🌱",
          },
        ],
      },
      cta: {
        title: "Klaar om de groei van uw kind te voeden?",
        subtitle:
          "Ervaar onze uitgebreide aanpak van gezonde voeding en leermomenten tijdens de maaltijden. Plan een bezoek om ons voedingsprogramma in actie te zien.",
        button: "Meer weten over onze voeding",
      },
    },

    horizontalGroups: {
      hero: {
        title: "Horizontale groepen",
        subtitle: "Leeftijdsgerichte zorg voor optimale ontwikkeling",
        ageRange: "(0–4 jaar)",
      },

      intro: {
        title: "Onze aanpak van leeftijdsgroepen",
        text1:
          "Bij Mini Avonturiers hebben we er bewust voor gekozen om te werken met horizontale groepen. Dit betekent dat kinderen van ongeveer dezelfde leeftijd en ontwikkelingsfase samen in een groep zitten.",
        text2:
          "In plaats van een verticale groep (waar kinderen van 0 tot 4 jaar gemengd zijn), verdelen we de groep in drie fasen. Zo krijgt elk kind de beste kans om zich te ontwikkelen in een omgeving die aansluit bij zijn of haar specifieke behoeften en mogelijkheden.",
        threeGroups: "Onze Drie Groepen",
      },

      groupsDivisionTitle: "Onze Groepsindeling",
      groupsDivisionSubtitle:
        "Elke groep is zorgvuldig ontworpen om te voldoen aan de specifieke behoeften",
      whatChildrenReceive: "Wat kinderen ontvangen:",
      ourApproach: "Onze aanpak:",

      groups: {
        baby: {
          title: "Babygroep",
          subtitle: "± 0–1,5 jaar",
          description:
            "Vertrouwen, zorg en hechting staan centraal. We volgen het natuurlijke ritme van elk kind.",
          features: [
            "Individuele aandacht en rust",
            "Veel lichamelijk contact en responsieve interactie",
            "Vrije bewegingsruimte (liggen, rollen, kruipen)",
            "Sensorisch materiaal en rustgevende muziek",
          ],
          approach:
            "Ons pedagogische personeel is getraind in sensitieve responsiviteit en de visie van Emmi Pikler, waarin respect voor het kind en zelfstandige beweging centraal staan.",
        },
        toddler: {
          title: "Peutergroep",
          subtitle: "± 1,5–2,5 jaar",
          description:
            "Ontdekken en het ontwikkelen van autonomie. Kinderen willen alles zelf doen.",
          features: [
            "Leren uiten en reguleren van emoties",
            "Ontwikkelen van motorische vaardigheden (klimmen, duwen, bouwen)",
            "Begin maken met taal en eenvoudige communicatie",
            "Oefenen met wachten, delen, ‘nee’ zeggen",
          ],
          approach:
            "Onze begeleiding richt zich op bevestigen, sturen en grenzen stellen. We bieden duidelijke richtlijnen, helpen bij conflicten en geven kinderen veel gelegenheid tot herhaling en zelfstandig leren.",
        },
        preschool: {
          title: "Kleutergroep",
          subtitle: "± 2,5–4 jaar",
          description:
            "Taalexplosie, sociale ontwikkeling en symbolisch spel. Kinderen zoeken uitdaging en interactie.",
          features: [
            "Samen spelen, leren delen, overleggen en rollenspellen",
            "Ontdekken van oorzaak-gevolg relaties en logica",
            "Activiteiten aangeboden binnen thema’s",
            "Voorbereiden op de overgang naar de basisschool",
          ],
          approach:
            "Hier gebruiken we taalrijke interactie, kringactiviteiten, gestructureerd spel en rollenspel. Ons pedagogische personeel stimuleert zelfstandigheid, woordenschat, probleemoplossend vermogen en zelfvertrouwen.",
        },
      },

      why: {
        title: "Waarom horizontale groepen?",
        subtitle:
          "Onze aanpak is gebaseerd op pedagogische principes en ontwikkelingspsychologie",
        items: {
          safety: {
            title: "Veiligheid & herkenning",
            description:
              "Kinderen in dezelfde ontwikkelingsfase hebben vergelijkbare ritmes, behoeften en vaardigheden. We kunnen de omgeving volledig afstemmen op die fase.",
          },
          targeted: {
            title: "Gerichte begeleiding",
            description:
              "Baby’s, peuters en kleuters hebben ieder hun eigen ontwikkelingsdoelen. Pedagogisch medewerkers kunnen gerichter ondersteunen.",
          },
          natural: {
            title: "Natuurlijk leren",
            description:
              "Volgens Jean Piaget verloopt cognitieve ontwikkeling in fasen. Wij stemmen spel en begeleiding af op deze natuurlijke leerprocessen.",
          },
        },
      },

      benefits: {
        title: "Voordelen van onze aanpak",
        subtitle:
          "Horizontale groepen bieden voordelen voor zowel kinderen als medewerkers",
        children: {
          title: "Voordelen voor Kinderen",
          subtitle: "Optimale ontwikkeling in leeftijdsgeschikte omgevingen",
          items: [
            "Meer rust en minder prikkels door het ontbreken van grote leeftijdsverschillen",
            "Activiteiten sluiten beter aan bij het ontwikkelingsniveau",
            "Kinderen voelen zich sneller competent en zelfverzekerd",
            "Ruimte voor zelfstandigheid passend bij de fase",
            "Betere observatie en begeleiding van gedrag, spel en emotionele ontwikkeling",
          ],
        },
        staff: {
          title: "Voordelen voor Medewerkers",
          subtitle: "Gerichte expertise en professionele ontwikkeling",
          items: [
            "Focus op één ontwikkelingsfase leidt tot meer pedagogische diepgang",
            "Gemakkelijker structureren van het dagprogramma",
            "Hogere kwaliteit in spelbegeleiding en observatie",
            "Meer rust en duidelijkheid in groepsdynamiek",
            "Betere team samenwerking door duidelijke rolverdeling",
          ],
        },
      },

      transitions: {
        title: "Samenhang en Overgangen",
        subtitle:
          "Hoewel kinderen in horizontale groepen zitten, verliezen we de continuïteit niet uit het oog. Overgangen naar de volgende groep worden zorgvuldig voorbereid:",
        items: [
          "Er vindt een warme overdracht plaats tussen pedagogische medewerkers",
          "Ouders worden betrokken bij de overgang",
          "Kinderen wennen geleidelijk aan de nieuwe groep",
          "De mentor blijft betrokken tijdens de hele overgang",
        ],
        conclusion:
          "Zo zorgen we ervoor dat het kind met vertrouwen en zonder stress naar de volgende fase kan doorgroeien.",
      },

      cta: {
        title: "Klaar om meer te ontdekken?",
        subtitle:
          "Ontdek hoe onze leeftijdsgerichte aanpak de ontwikkeling van uw kind kan ondersteunen. Plan een rondleiding om onze horizontale groepen in actie te zien.",
        button: "Meer over onze groepen",
      },
    },

    // Brand
    brand: {
      name: "Mini Avonturiers",
      tagline: "Avonturen beginnen met liefde en leren.",
    },

    // Home page
    home: {
      welcome: "Welkom bij Mini Avonturiers!",
      subtitle:
        "Bij Mini Avonturiers draait alles om ontdekken, groeien en plezier maken. We bieden een veilige en vertrouwde plek waar kinderen zich op hun gemak voelen en in hun eigen tempo kunnen ontwikkelen. Voor de allerkleinsten van 0 tot 4 jaar hebben we ons kinderdagverblijf, waar spelen en leren hand in hand gaan. Voor kinderen van 4 tot 13 jaar is er onze buitenschoolse opvang, een fijne omgeving om na school te ontspannen, nieuwe dingen te ontdekken en samen te genieten. Ons team staat elke dag klaar met aandacht, warmte en structuur. Zo zorgen we ervoor dat ieder kind de ruimte krijgt om zijn of haar talenten te ontdekken en met vertrouwen en plezier op te groeien.",
      registerChild: "Kind registreren",
      registerTour: "Rondleiding",

      aboutUs: {
        title: "Over ons",
        welcomeCenter: {
          title: "Welkom bij ons centrum",
          description:
            "Bij Mini Avonturiers is het onze missie om een veilige, voedende en inspirerende omgeving te bieden waar elk kind wordt gezien, gehoord en ondersteund in hun unieke ontwikkeling. Door middel van spel, nieuwsgierigheid en verbinding met de natuur stellen we kinderen in staat om zelfverzekerde, gelukkige individuen te worden.",
        },
        growingImproving: {
          title: "Groei en verbetering",
          description:
            "Wij geloven dat elk kind zich op zijn eigen manier en in zijn eigen tempo ontwikkelt. Onze visie is om deze groei te leiden door emotionele veiligheid, structuur en vrijheid van expressie. Door een ruimte vol ontdekking, warmte en verbinding te creëren, zorgen we ervoor dat kinderen zich veilig voelen om de wereld om hen heen te verkennen, keuzes te maken, en te worden wie ze moeten zijn.",
        },
      },

      services: {
        title: "Onze diensten",
        subtitle:
          "Ontdek ons uitgebreide aanbod aan kinderopvangdiensten, ontworpen om de groei en ontwikkeling van uw kind te ondersteunen.",
        items: {
          childcarePrograms: {
            title: "Kinderopvang programma's",
            features: [
              "Dagelijkse zorg",
              "Gestructureerde activiteiten",
              "Ontwikkelingsgebieden",
            ],
          },
          transportPickup: {
            title: "Transport- en ophaalservices",
            features: ["Veilige routes", "GPS-tracking", "Betrouwbare timing"],
          },
          healthyMeals: {
            title: "Voeding: gezonde maaltijden",
            features: [
              "Gebalanceerd dieet",
              "Aanpasbaar menu",
              "Geleid door voedingsdeskundigen",
            ],
          },
          digitalTools: {
            title: "Digitale hulpmiddelen en dagelijkse updates",
            features: [
              "Realtime updates",
              "Ouderportal",
              "Naadloze communicatie",
            ],
          },
          dailyRoutines: {
            title: "Dagelijkse routines",
            features: [
              "Gestructureerd schema",
              "Balans speeltijd",
              "Rustperiodes",
            ],
          },
          emotionalSafety: {
            title: "Emotionele veiligheid",
            features: [
              "Veilige omgeving",
              "Professionele begeleiding",
              "Ondersteunend personeel",
            ],
          },
          mealtimesActivities: {
            title: "Eettijden en activiteiten",
            features: [
              "Boeiende activiteiten",
              "Variatie in maaltijden",
              "Voedingseducatie",
            ],
          },
          ageGroupStructure: {
            title: "Leeftijdsgroep structuur",
            features: [
              "Aangepaste zorg",
              "Leeftijdsgeschikte activiteiten",
              "Specialistisch personeel",
            ],
          },
          transitions: {
            title: "Onderwijzend personeel",
            features: [
              "Opgeleid personeel",
              "Ondersteunende overgangen",
              "Continue feedback",
            ],
          },
          settlingPolicy: {
            title: "Inburgeringsbeleid",
            features: [
              "Welkomstomgeving",
              "Inburgeringshulp",
              "Ouderondersteuning",
            ],
          },
          parentCommunication: {
            title: "Oudercommunicatie",
            features: [
              "Twee-weg communicatie",
              "Regelmatige updates",
              "Gemeenschapsopbouw",
            ],
          },
          hygiene: {
            title: "Hygiëne",
            features: [
              "Schone faciliteiten",
              "Reguliere controles",
              "Gezondheidsprotocollen",
            ],
          },
          openingClosing: {
            title: "Opening/Sluiting",
            features: [
              "Gestructureerd schema",
              "Veiligheidscontroles",
              "Efficiënte processen",
            ],
          },
        },
      },

      contactUs: "Neem contact met ons op",
      learnMore: "Leer meer",
      whyChooseUs: "Waarom voor ons kiezen?",
      whyChooseUsSubtitle:
        "We bieden een voedende omgeving waarin kinderen kunnen groeien, leren en bloeien",
      activities: "Onze activiteiten",
      activitiesSubtitle:
        "Boeiende activiteiten ontworpen om de vaardigheden en creativiteit van uw kind te ontwikkelen",
      viewDailyRoutine: "Bekijk dagelijkse routine",
      parentTestimonials: "Wat zeggen ouders",
      testimonialsSubtitle:
        "Luister naar families die ons met hun kinderen vertrouwen",
      readyToStart: "Klaar om de reis van uw kind te starten?",
      ctaSubtitle:
        "Word lid van onze kindcentrum en geef je kind de beste start in het leven",
      registerNow: "Nu registreren",
      scheduleVisit: "Bezoek plannen",
      ctaReady: "Klaar om te beginnen?",
      ctaDescription:
        "Neem vandaag nog contact met ons op om meer te weten over onze programma's en een bezoek aan onze faciliteit in te plannen.",
      ctaScheduleVisit: "Bezoek plannen",
      ctaDownloadBrochure: "Brochure downloaden",
    },

    // About page
    about: {
      title: "Over Mini Avonturiers",
      subtitle: "Ons verhaal, missie en het team achter de zorg voor uw kind",
      mission: "Onze missie",
      missionText:
        "Het bieden van een veilige, voedende en stimulerende omgeving waar elk kind zijn volledige potentieel kan ontwikkelen door spel, leren en sociale interactie.",
      vision: "Onze visie",
      visionText:
        "De toonaangevende kinderopvang in Utrecht te zijn, bekend om excellentie in vroegkinderlijke educatie en zorg.",
      values: "Onze waarden",
      valuesList: {
        safety: "Veiligheid eerst",
        learning: "Leren door spel",
        respect: "Respect voor elk kind",
        community: "Gemeenschap bouwen",
      },
      team: "Ons team",
      teamText:
        "Onze ervaren en gekwalificeerde medewerkers zijn gepassioneerd over vroegkinderlijke ontwikkeling en toegewijd aan het bieden van de beste zorg voor uw kind.",
      experience: "Jaren ervaring",
      children: "Blije kinderen",
      staff: "Gekwalificeerde medewerkers",
      satisfaction: "Oudertevredenheid",
      ourImpact: "Onze impact",
      principlesThatGuide: "De principes die alles wat we doen leiden",
      valuesDescriptions: {
        safety:
          "We geven prioriteit aan de veiligheid en het welzijn van elk kind onder onze zorg",
        learning:
          "Kinderen leren het beste door spel, verkenning en hands-on ervaringen",
        respect:
          "Elk kind is uniek en verdient respect, liefde en individuele aandacht",
        community:
          "We bouwen sterke partnerschappen met families en de lokale gemeenschap",
      },
      teamMembers: {
        sarahJohnson: {
          name: "Sarah Johnson",
          role: "Directeur & Oprichter",
          experience: "20+ jaar in vroegkinderlijke educatie",
          description:
            "Gepassioneerd over het creëren van koesterende omgevingen waar kinderen kunnen gedijen en groeien.",
        },
        mariaRodriguez: {
          name: "Maria Rodriguez",
          role: "Hoofddocent",
          experience: "15+ jaar onderwijservaring",
          description:
            "Gespecialiseerd in creatieve kunsten en taalontwikkeling voor jonge kinderen.",
        },
        davidChen: {
          name: "David Chen",
          role: "Lichamelijke Opvoeding Specialist",
          experience: "12+ jaar in kinderontwikkeling",
          description:
            "Richt zich op motorische vaardighedenstwikkeling en gezonde fysieke activiteiten.",
        },
        lisaThompson: {
          name: "Lisa Thompson",
          role: "Voedingscoördinator",
          experience: "10+ jaar in kindervoeding",
          description:
            "Zorgt voor gezonde, evenwichtige maaltijden die de groei en ontwikkeling van kinderen ondersteunen.",
        },
      },
      parentTestimonials: {
        emmaAndTom: {
          names: "Emma & Tom Wilson",
          child: "Lucas (4 jaar)",
          text: "Mini Avonturiers is een geweldige ervaring geweest voor onze zoon. Het personeel geeft echt om de ontwikkeling van elk kind en creëert zo'n warme, liefdevolle omgeving.",
        },
        annaAndMark: {
          names: "Anna & Mark Davis",
          child: "Sophie (3 jaar)",
          text: "We houden ervan hoe het centrum zich richt op zowel leren als spelen. Sophie is zo gegroeid in vertrouwen en vaardigheden sinds ze zich heeft aangesloten.",
        },
        lisaAndJames: {
          names: "Lisa & James Brown",
          child: "Oliver (5 jaar)",
          text: "De communicatie met ouders is uitstekend, en we voelen ons altijd geïnformeerd over de vooruitgang en dagelijkse activiteiten van ons kind.",
        },
      },
      ctaJoinFamily: "Word lid van onze kindcentrum",
      ctaExperience:
        "Ervaar het verschil dat ons toegewijde team en bewezen aanpak kan maken voor de ontwikkeling van uw kind.",
      meetTeam:
        "Ontmoet ons toegewijde team van vroegkinderlijke professionals",
      whatParentsSayTitle: "Wat ouders zeggen",
      whatParentsSaySubtitle:
        "Hoor van families die ons vertrouwen met hun kinderen",
      scheduleVisit: "Bezoek plannen",
      contactUs: "Contact opnemen",
    },

    // Services page
    services: {
      title: "Onze diensten",
      subtitle: "Uitgebreide kinderopvang oplossingen voor elk gezin",
      daycare: "Volledige dagopvang",
      daycareDesc:
        "Complete kinderopvang van ochtend tot avond met educatieve activiteiten",
      afterSchool: "Buitenschoolse opvang",
      afterSchoolDesc: "Veilige en boeiende zorg voor schoolgaande kinderen",
      holiday: "Vakantieopvang",
      holidayDesc: "Speciale programma's tijdens schoolvakanties",
      flexible: "Flexibele uren",
      flexibleDesc:
        "Aanpasbare schema's die passen bij de behoeften van uw gezin",
      learnMore: "Meer informatie",
      contactUs: "Contact opnemen",
    },

    // Transportation Policy page
    transportationPolicy: {
      title: "Vervoer: Ophaalservice met de bus",
      subtitle:
        "Veilige en betrouwbare vervoersdienst voor schoolgaande kinderen",
      intro:
        "Bij Mini Avonturiers bieden we een veilige, betrouwbare en kindvriendelijke ophaalservice voor schoolgaande kinderen met onze eigen herkenbare bussen. Deze dienst is beschikbaar voor kinderen die deelnemen aan het buitenschoolse opvangprogramma (BSO) en bevordert de continuïteit van de zorg en het gemak voor ouders.",
      intro2:
        "Het vervoeren van jonge kinderen vereist zorgvuldige voorbereiding, duidelijke afspraken en voortdurende aandacht voor veiligheid, comfort en betrokkenheid. Daarom hebben we een helder vervoersbeleid ontwikkeld dat aan alle wettelijke vereisten voldoet en zich richt op het welzijn van het kind tijdens het vervoer.",

      section1: {
        title: "De Mini Avonturiers-bus: veilig, herkenbaar en vertrouwd",
        description: "Onze bussen zijn uitgerust met:",
        features: [
          "Herkenbare huisstijl (logo en naam Mini Avonturiers), zodat kinderen en scholen weten wie hen komt ophalen.",
          "Kinderzitjes en gordels die voldoen aan de nieuwste veiligheidseisen.",
          "Goedgekeurde voertuigen die jaarlijks technisch gekeurd worden.",
          "Navigatie- en rijhulpsystemen voor veilig en efficiënt vervoer.",
        ],
        note: "Tijdens het vervoer wordt altijd voldaan aan de geldende regelgeving voor kinderopvangvervoer (conform de Wet personenvervoer 2000 en de richtlijnen van de GGD).",
      },

      section2: {
        title: "Gekwalificeerde chauffeurs met een kindgerichte aanpak",
        description:
          "Onze chauffeurs zijn meer dan alleen vervoerders – zij zijn het eerste aanspreekpunt na school en spelen een belangrijke rol in de overgang van school naar de naschoolse opvang. Daarom stellen wij hoge eisen:",
        requirements: [
          "Geldig rijbewijs B en een verklaring omtrent gedrag (VOG).",
          "Basistraining in kindveiligheid en pedagogische omgangsvormen.",
          "Instructie over veilig instappen, het voeren van gesprekken en het bewaren van rust tijdens overgangen.",
          "In staat signalen van spanning of vermoeidheid bij kinderen te herkennen.",
        ],
        note: "Onze chauffeurs hebben een rustige, kindvriendelijke houding en bouwen na verloop van tijd herkenning en vertrouwen op met de kinderen.",
      },

      section3: {
        title: "Begeleiding en overdracht ter plaatse",
        arrival: {
          title: "Bij aankomst op school:",
          points: [
            "De chauffeur meldt zich bij de school en wacht op de afgesproken locatie.",
            "Kinderen worden gecontroleerd aan de hand van een lijst.",
            "Er is contact met het schoolpersoneel in geval van bijzondere omstandigheden (bijvoorbeeld als een kind ziek is of er iets is gebeurd).",
          ],
        },
        shelter: {
          title: "Bij aankomst op de opvang:",
          points: [
            "De chauffeur draagt de kinderen persoonlijk over aan een pedagogische medewerker.",
            "De aankomsttijd wordt geregistreerd in het systeem.",
            "Eventuele bijzonderheden tijdens de rit worden direct gedeeld.",
          ],
        },
        note: "Deze overdracht zorgt voor continuïteit, veiligheid en informatieoverdracht tussen school, vervoer en opvang.",
      },

      section4: {
        title: "Realtime communicatie met ouders",
        description:
          "Onze ouderapp houdt ouders op de hoogte van de vervoerstijden. Ouders ontvangen:",
        features: [
          "Pushmeldingen bij vertrek en aankomst.",
          "Mogelijkheid om vragen of afmeldingen via de app te delen.",
          "Direct contact met de opvang bij vertragingen of wijzigingen.",
        ],
        note: "Op deze manier creëren we transparantie en vertrouwen, en kunnen ouders hun kind met een gerust hart laten vervoeren.",
      },

      section5: {
        title: "Flexibele ophaalroutes en planning",
        description: "Onze routes zijn efficiënt en kindvriendelijk:",
        features: [
          "Korte reistijden, met voorkeur voor routes dicht bij de opvanglocatie.",
          "Kleine groepjes per rit, zodat er rust en stilte is in de bus.",
          "Aanpassing aan seizoenen of verkeersdrukte, indien nodig.",
          "Vaste schema’s met duidelijke tijdsblokken per schoollocatie.",
        ],
        note: "Bij inschrijving maken we met ouders afspraken over van welke school het kind wordt opgehaald, op welk tijdstip en op welke dagen.",
      },

      section6: {
        title: "Noodsituaties en achtervang",
        description:
          "Voor uitzonderlijke situaties hanteren we een strikt protocol:",
        features: [
          "Bij vertraging worden de opvang en de ouder direct geïnformeerd.",
          "Als een kind niet verschijnt op de afgesproken locatie, wordt binnen 2 minuten contact opgenomen met de school en de ouders.",
          "De chauffeur staat in contact met de opvang en heeft een lijst met noodnummers.",
        ],
        note: "Onze achtervangdienst zorgt ervoor dat er altijd iemand beschikbaar is om te helpen bij pech, incidenten of onvoorziene omstandigheden.",
      },
    },

    // Gallery page
    gallery: {
      title: "Foto Galerij",
      subtitle: "Kijk door onze dagelijkse activiteiten en gelukkige momenten",
      featuredPhotos: "Belangrijke Foto's",
      carouselSubtitle: "Schuif door onze highlights",
      photoGrid: "Foto Verzameling",
      gridSubtitle: "Blader door onze volledige collectie van herinneringen",
      ctaTitle: "Wil je meer zien?",
      ctaSubtitle:
        "Boek een bezoek om onze warme en welkomme omgeving persoonlijk te ervaren",
      scheduleVisit: "Bezoek plannen",
      contactUs: "Contact Opnemen",
      images: {
        creativePainting: {
          src: "/images/creative-1.jpg",
          title: "Creatief Schilderen",
          alt: "Kinderen schilderen samen",
        },
        artActivities: {
          src: "/images/creative-2.jpg",
          title: "Kunstactiviteiten",
          alt: "Kinderen doen kunstactiviteiten",
        },
        drawingCrafting: {
          src: "/images/creative-3.jpg",
          title: "Tekenen & Knutselen",
          alt: "Kinderen tekenen en knutselen",
        },
        outdoorFun: {
          src: "/images/outdoor-1.jpg",
          title: "Buitenplezier",
          alt: "Kinderen spelen buiten",
        },
        parkPlay: {
          src: "/images/outdoor-2.jpg",
          title: "Spelen in het Park",
          alt: "Kinderen rennen in het park",
        },
        playgroundTime: {
          src: "/images/outdoor-3.jpg",
          title: "Speeltijd",
          alt: "Groep kinderen op de speeltuin",
        },
        healthyMeals: {
          src: "/images/meals-1.jpg",
          title: "Gezonde Maaltijden",
          alt: "Kinderen eten gezonde maaltijden",
        },
        lunchTime: {
          src: "/images/meals-2.jpg",
          title: "Lunchtijd",
          alt: "Kinderen genieten samen van de lunch",
        },
        snackSharing: {
          src: "/images/meals-3.jpg",
          title: "Snack Delen",
          alt: "Kinderen delen snacks",
        },
        napTime: {
          src: "/images/rest-1.jpg",
          title: "Slaaptijd",
          alt: "Kind slaapt rustig",
        },
        restingMats: {
          src: "/images/rest-2.jpg",
          title: "Rustmatten",
          alt: "Kinderen rusten op matten",
        },
        daycareSleep: {
          src: "/images/rest-3.jpg",
          title: "Slaap in de opvang",
          alt: "Kinderen slapen in de opvang",
        },
        groupLearning: {
          src: "/images/learning-1.jpg",
          title: "Groepsleren",
          alt: "Kinderen leren samen",
        },
        readingTime: {
          src: "/images/learning-2.jpg",
          title: "Leestijd",
          alt: "Kinderen lezen boeken",
        },
        teacherHelp: {
          src: "/images/learning-3.jpg",
          title: "Hulp van de Leraar",
          alt: "Leraar helpt leerlingen",
        },
        musicClass: {
          src: "/images/music-1.jpg",
          title: "Muziekles",
          alt: "Kinderen maken muziek",
        },
        instrumentsFun: {
          src: "/images/music-2.jpg",
          title: "Instrumentenplezier",
          alt: "Kinderen met muziekinstrumenten",
        },
        singingGroup: {
          src: "/images/music-3.jpg",
          title: "Zanggroep",
          alt: "Groep zingt samen",
        },
      },
    },

    // Daily Routine page
    dailyRoutine: {
      title: "Dagelijkse routine",
      subtitle: "Een typische dag bij Mini Avonturiers",

      // Ochtend
      morningSchedule: "Ochtendprogramma",
      morningDesc:
        "Kinderen beginnen de dag met gestructureerd spel en leerervaringen.",
      morning: {
        dropOff: {
          time: "7:00 – 9:00",
          title: "Ochtendontvangst & vrij spel",
          description:
            "Kinderen worden in een ontspannen sfeer ontvangen. Ouders krijgen de gelegenheid om informatie te delen met de pedagogische medewerker. Kinderen kiezen hun eigen activiteit of hoek, zodat ze de dag in hun eigen tempo kunnen beginnen. Deze fase bevordert veiligheid en hechting: de overgang van thuis naar opvang verloopt rustig en kindgericht.",
        },
        circle: {
          time: "9:00",
          title: "Fruit, drinken & kring",
          description:
            "We verzamelen ons in een kring voor een leuke en leerzame tijd: zingen, praten over het weer, een boek lezen en bij elkaar inchecken. Dit bevordert taalvaardigheid, teamgeest en sociaal-emotionele ontwikkeling. Eetmomenten worden bewust samen en in rust gehouden.",
        },
        activity: {
          time: "10:00",
          title: "Gerichte activiteit",
          description:
            "Activiteiten verschillen per dag, maar sluiten aan bij het thema en ontwikkelingsgebied (bijv. knutselen, taalspelletjes, reeksen maken, sensorisch materiaal). Ze vinden plaats in kleine groepjes of individueel en worden afgestemd op de ontwikkelingsbehoeften van elk kind.",
        },
        outdoor: {
          time: "11:00",
          title: "Buitenspel of themaspel",
          description:
            "Buiten bewegen is essentieel voor motorische vaardigheden en gezondheid. Buiten is er ruimte om te klimmen, rennen, ontdekken en vrij te spelen. Bij slecht weer vindt binnen een soortgelijk themaspel plaats.",
        },
        lunch: {
          time: "11:30",
          title: "Lunch volgens de schijf van vijf",
          description:
            "Gezond eten is een essentieel onderdeel van onze ontwikkelingsvisie. We lunchen samen en richten ons op gezonde eetgewoonten, zelfstandigheid en gesprek aan tafel. Kinderen worden aangemoedigd om nieuwe smaken te proberen en waar mogelijk betrekken we hen bij de voorbereiding.",
        },
      },

      // Middag
      afternoonSchedule: "Middagprogramma",
      afternoonDesc:
        "In de middag combineren we rust met creatieve en fysieke activiteiten.",
      afternoon: {
        rest: {
          time: "12:30",
          title: "Rusttijd of middagdutje",
          description:
            "Jongere kinderen slapen in een stille, verduisterde kamer onder toezicht. Oudere kinderen die niet meer slapen krijgen een rustmoment: lezen, liggen of een rustig spel spelen. Dit bevordert herstel, concentratie en emotionele regulatie.",
        },
        snack: {
          time: "14:30",
          title: "Fruit- of groentesnack",
          description:
            "Een gezonde snack biedt een korte pauze. We gebruiken dit moment ook om te observeren hoe kinderen zich voelen en wat ze nodig hebben voor de rest van de dag.",
        },
        activity: {
          time: "15:00",
          title: "Activiteit: rollenspel, motoriek, middaggym",
          description:
            "In de middag richten we ons op expressieve en lichamelijke activiteiten. Middaggym, dansen, bouwen, rollenspel of samen een verhaal naspelen. De nadruk ligt op plezier, expressie en teamwork.",
        },
        meal: {
          time: "16:00",
          title: "Brood, soepstengels of warme maaltijd",
          description:
            "Afhankelijk van het kind en de lengte van de opvang bieden we een voedzame snack of een lichte warme maaltijd. Eten blijft een sociaal moment, maar is niet verplicht. Kinderen bepalen hun eetbehoefte binnen de beschikbare opties.",
        },
        pickup: {
          time: "17:00 – 18:30",
          title: "Ophalen, vrij spel & rustige hoekjes",
          description:
            "De dag eindigt in een ontspannen sfeer. Kinderen spelen vrij in een rustige omgeving. We bieden zachte verlichting en boekhoekjes om de overgang naar huis voorspelbaar en vredig te maken. Medewerkers nemen ruim de tijd voor overdracht naar de ouders.",
        },
      },

      // Principes
      principles: "Leidende principes",
      principlesDesc:
        "Onze dagelijkse routine is ontworpen met balans, ritme en welzijn van kinderen in gedachten.",
      principleItems: {
        predictability: {
          title: "Voorspelbaarheid en herhaling",
          description:
            "geven kinderen structuur en maken overgangen tussen activiteiten makkelijker.",
        },
        sharedMoments: {
          title: "Gedeelde momenten",
          description:
            "(kring, eetmomenten) kinderen leren sociale vaardigheden en respect voor anderen.",
        },
        freePlay: {
          title: "Vrije speelmomenten",
          description:
            "stimuleren creativiteit, zelfsturing en sociale interactie.",
        },
        targeted: {
          title: "Gerichte activiteiten",
          description:
            "bieden ontwikkelingsuitdagingen binnen de Zone van Naaste Ontwikkeling (Vygotsky).",
        },
        rest: {
          title: "Rust en slaap",
          description:
            "zijn cruciaal voor het verwerken van indrukken en bevorderen zelfregulatie.",
        },
        attentionSpan: {
          title: "Natuurlijke aandachtsspanne",
          description:
            "Het dagritme is afgestemd op de natuurlijke aandachtsspanne van jonge kinderen, zodat zij optimaal kunnen leren en spelen zonder overprikkeling.",
        },
      },
    },
    // Contact page
    contact: {
      title: "Contact",
      subtitle: "Neem contact op met ons team",
      address: "Adres",
      addressText: "Kinderstraat 123, 3511 AB Utrecht",
      phone: "Telefoonnummer",
      phoneText: "+31 (0)30 123 4567",
      email: "E-mail",
      emailText: "info@mini-avonturiers.nl",
      hours: "Openingstijden",
      hoursText: "Maandag - Vrijdag: 07:00 - 18:30",
      message: "Stuur ons een Bericht",
      name: "Volledige Naam",
      emailField: "E-mailadres",
      subject: "Onderwerp",
      messageField: "Uw bericht",
      send: "Bericht versturen",
      mondayFriday: "Maandag t/m vrijdag",
      saturday: "Zaterdag",
      sunday: "Zondag",
      closed: "Gesloten",
      saturdayOpeningHours: "Afwijkende openingstijden",
      faqItems: {
        q1: "Voor welke leeftijdsgroepen zorgt u?",
        a1: "We zorgen voor kinderen van 0 tot 13 jaar, met leeftijdsgeschikte programma's voor elke groep.",
        q2: "Wat zijn uw openingstijden?",
        a2: "We zijn open van maandag tot vrijdag van 7:00 tot 18:30 uur.",
        q3: "Biedt u maaltijden aan?",
        a3: "Ja, we bieden gezonde ontbijt, lunch en snacks gedurende de dag.",
        q4: "Wat is uw medewerker-kind ratio?",
        a4: "We handhaven een ratio van 1:4 voor baby's, 1:6 voor peuters en 1:8 voor kleuters.",
        q5: "Biedt u flexibele planning aan?",
        a5: "Ja, we bieden flexibele planningsopties om verschillende gezinsbehoeften te accommoderen.",
        q6: "Welke activiteiten biedt u aan?",
        a6: "We bieden verschillende activiteiten aan inclusief knutselen, muziek, buitenspel, educatieve spellen en meer.",
        q7: "Hoe gaat u om met noodsituaties?",
        a7: "We hebben uitgebreide noodsituatieprocedures en alle medewerkers zijn opgeleid in eerste hulp.",
        q8: "Kunnen ouders gedurende de dag bezoeken?",
        a8: "Ja, ouders zijn welkom om te bezoeken tijdens openingstijden, hoewel we om voorafgaande kennisgeving vragen.",
      },
      transport: {
        byCar: "Met de auto",
        byCarDescription:
          "Gratis parkeren op locatie. Gelegen vlakbij de A12, afslag 18.",
        byPublicTransport: "Met het openbaar vervoer",
        byPublicTransportDescription:
          "Buslijnen 2, 8 en 12 stoppen op 5 minuten loopafstand.",
        byBicycle: "Met de fiets",
        byBicycleDescription:
          "Veilige fietsenstalling beschikbaar. Gelegen aan de hoofd fietsroute.",
      },
    },

    // Auth
    auth: {
      welcome: "Welkom terug",
      loginSubtitle: "Log in om toegang te krijgen tot uw account",
      email: "E-mailadres",
      password: "Wachtwoord",
      login: "Inloggen",
      loggingIn: "Inloggen...",
      loginFailed: "Inloggen mislukt. Controleer uw gegevens.",
      noAccount: "Nog geen account?",
      registerHere: "Registreer hier",
      forgotPassword: "Wachtwoord vergeten?",
      registerTitle: "Word lid van onze kindcentrum",
      registerSubtitle: "Maak een account aan om uw kind te registreren",
      fullName: "Volledige naam",
      phone: "Telefoonnummer",
      confirmPassword: "Bevestig wachtwoord",
      parentInfo: "Ouderinformatie",
      children: "Kinderen",
      addChild: "Kind toevoegen",
      removeChild: "Verwijderen",
      childNumber: "Kind",
      name: "Naam van kind",
      dateOfBirth: "Geboortedatum",
      group: "Leeftijdsgroep",
      register: "Registreren",
      registering: "Registreren...",
      registrationFailed: "Registratie mislukt. probeer het opnieuw.",
      passwordMismatch: "Wachtwoorden komen niet overeen",
      passwordTooShort: "Wachtwoord moet minimaal 6 tekens lang zijn",
      haveAccount: "Al een account?",
      loginHere: "Hier inloggen",
      childname: "Naam van kind",
      childBirthDate: "Geboortedatum kind",
      childGroup: "Groep kind",
      selectGroup: "Selecteer groep",
      babies: "Baby's",
      toddlers: "Peuters",
      preschoolers: "Kleuters",
      kindergarten: "Kleuterklas",
      schoolAge: "Schoolleeftijd",
    },

    // Common UI elements
    common: {
      scheduleVisit: "Bezoek plannen",
      contactUs: "Contact opnemen",
      learnMore: "Meer informatie",
      registerNow: "Nu registreren",
      downloadBrochure: "Brochure downloaden",
      readyToStart: "Klaar om te beginnen met de reis van uw kind?",
      joinOurFamily: "Word lid van onze kindcentrum",
      whatParentsSay: "Wat ouders zeggen",
      frequentlyAskedQuestions: "Veelgestelde vragen",
      quickAnswers: "Snelle antwoorden op veelgestelde vragen",
      getInTouch: "Neem contact op",
      sendMessage: "Bericht versturen",
      findUs: "Vind ons",
      byCar: "Per auto",
      byPublicTransport: "Per openbaar vervoer",
      byBicycle: "Per fiets",
      openingHours: "Openingstijden",
      address: "Adres",
      phone: "Telefoonnummer",
      email: "E-mail",
      ctaReady: "Klaar om te beginnen?",
      locationDesc:
        "Bezoek onze locatie en ontdek wat Mini Avonturiers te bieden heeft.",
      ctaDescription:
        "Neem vandaag nog contact met ons op voor meer informatie of om een bezoek te plannen.",
      ctaScheduleVisit: "Bezoek plannen",
      ctaDownloadBrochure: "Brochure downloaden",
      faq: "Veelgestelde vragen",
      faqDescription: "Snelle antwoorden op veelgestelde vragen",
    },

    // Form options
    form: {
      firstname: "Voornaam *",
      lastname: "Achternaam *",
      firstnamePlaceholder: "Jan",
      lastnamePlaceholder: "De Vries",
      selectSubject: "Selecteer een onderwerp",
      generalInquiry: "Algemene Vraag",
      registration: "Registratie",
      scheduleVisit: "Bezoek plannen",
      pricingInfo: "Prijsinformatie",
      other: "Anders",
      fullName: "Volledige Naam *",
      emailAddress: "E-mailadres *",
      phoneNumber: "Telefoonnummer",
      subject: "Onderwerp *",
      message: "Bericht *",
      yourFullName: "Uw volledige naam",
      emailPlaceholder: "uw.email@voorbeeld.nl",
      phonePlaceholder: "+31 (0)6 12345678",
      messagePlaceholder: "Vertel ons hoe we u kunnen helpen...",
      aanmeldenIntro:
        "Je kind aanmelden of een vraag? Vul het formulier hieronder in.",
      rondleidingIntro:
        "Benieuwd naar onze locatie? Vul het contactformulier hieronder in om een afspraak te maken.",
      aanmeldenTitle: "Je kind aanmelden of een vraag?",
      rondleidingTitle: "Rondleiding aanvragen",
      send: "Verzenden",
      sending: "Verzenden",
      sendSuccess:
        "Bedankt! Uw bericht is succesvol verzonden. We nemen zo spoedig mogelijk contact met u op.",
      sendError:
        "Oeps! Er ging iets mis bij het verzenden van uw bericht. Probeer het later opnieuw of neem direct contact met ons op.",
    },

    // FAQ items
    faq: {
      openingHours: "Wat zijn uw openingstijden?",
      openingHoursAnswer:
        "Wij zijn open van maandag tot vrijdag van 7:00 tot 18:30 uur. We bieden ook flexibele schema's aan om de behoeften van uw gezin te voldoen.",
      ageGroups: "Welke leeftijdsgroepen bedienen wij?",
      ageGroupsAnswer:
        "Wij bieden zorg aan voor kinderen van 0 tot 13 jaar oud, met leeftijdsgeschikte programma's en activiteiten voor elke ontwikkelingsfase.",
      meals: "Biedt u maaltijden aan?",
      mealsAnswer:
        "Ja, wij bieden gezonde maaltijden en snacks gedurende de dag aan. We kunnen speciale voedingsbehoeften accommodatie.",
      scheduleVisit: "Hoe kan ik een bezoek plannen?",
      scheduleVisitAnswer:
        "U kunt een bezoek plannen door ons te bellen, een e-mail te sturen of het contactformulier hierboven in te vullen.",
    },

    // Testimonials
    testimonials: {
      title: "Wat Ouders Zeggen",
      subtitle: "Hoor van families die ons vertrouwen met hun kinderen",
      sarah: {
        name: "Sarah Johnson",
        role: "Ouder van Emma (4 jaar)",
        text: "Mini Avonturiers is geweldig geweest voor onze dochter. Ze komt hier elke dag graag en heeft zoveel geleerd. Het personeel is zorgzaam en professioneel.",
      },
      michael: {
        name: "Michael Chen",
        role: "Ouder van Lucas (3 jaar)",
        text: "De dagelijkse routine is perfect voor onze zoon. Hij heeft geweldige sociale vaardigheden ontwikkeld en is altijd enthousiast om ons over zijn dag te vertellen.",
      },
      anna: {
        name: "Anna Rodriguez",
        role: "Ouder van Sofia (5 jaar)",
        text: "We kunnen niet gelukkiger zijn met de zorg die onze dochter krijgt. De activiteiten zijn boeiend en educatief, en het personeel geeft echt om elk kind.",
      },
    },

    // Service Features
    serviceFeatures: {
      safety: "Veiligheid Eerst",
      safetyDesc: "Veilige omgeving met 24/7 monitoring en opgeleid personeel",
      nutrition: "Gezonde Voeding",
      nutritionDesc:
        "Gebalanceerde maaltijden en snacks bereid met verse ingrediënten",
      education: "Vroege Educatie",
      educationDesc: "Leeftijdsgeschikte leeractiviteiten en curriculum",
      flexibility: "Flexibele Planning",
      flexibilityDesc:
        "Aanpasbare zorgopties die passen bij de behoeften van uw gezin",
      communication: "Oudercommunicatie",
      communicationDesc: "Regelmatige updates en open communicatie met ouders",
      activities: "Rijke Activiteiten",
      activitiesDesc:
        "Diverse activiteiten inclusief kunst, muziek, buitenspel en leerspellen",
      qualifiedStaff: "Gekwalificeerde Medewerkers",
      qualifiedStaffDesc:
        "Onze ervaren en gekwalificeerde medewerkers zijn gepassioneerd over vroegkinderlijke ontwikkeling en toegewijd aan het bieden van de beste zorg voor uw kind.",
      safeEnvironment: "Veilige omgeving",
      safeEnvironmentDesc:
        "Veilige omgeving met 24/7 monitoring en opgeleid personeel",
      healthyMeals: "Gezonde Maaltijden",
      healthyMealsDesc:
        "Gebalanceerde maaltijden en snacks bereid met verse ingrediënten",
      learningProgram: "Leerprogramma",
      learningProgramDesc:
        "Een uitgebreid curriculum speciaal ontworpen voor de ontwikkeling van het jonge kind.",
      flexibleHours: "Flexibele uren",
      flexibleHoursDesc:
        "Aanpasbare schema's die passen bij de behoeften van uw gezin",
      parentInvolvement: "Ouderparticipatie",
      parentInvolvementDesc:
        "Regelmatige updates en open communicatie met ouders",
    },

    // Additional Services
    additionalServices: {
      title: "Aanvullende diensten",
      subtitle: "Verrijk de ervaring van uw kind met onze optionele diensten",
      transportation: "Vervoer",
      transportationDesc:
        "Veilig en betrouwbaar vervoer van en naar school of huis",
      extraActivities: "Extra activiteiten",
      extraActivitiesDesc: "Speciale lessen in muziek, kunst, sport of talen",
      specialDiet: "Speciaal dieet",
      specialDietDesc: "Aanpassing voor speciale dieetwensen",
      tutoring: "Bijles",
      tutoringDesc: "Individuele of kleine groep bijlessen",
    },

    // Service Features Lists
    serviceFeaturesList: {
      daycare: [
        "Volledige dagopvang van 7:00 tot 18:30",
        "Leeftijdsgeschikte activiteiten en leren",
        "Gezonde maaltijden en snacks inbegrepen",
        "Regelmatige voortgangsrapporten",
        "Oudercommunicatie-app",
      ],
      afterSchool: [
        "Ophalen na school van lokale scholen",
        "Huiswerkbegeleiding en ondersteuning",
        "Recreatieve activiteiten en spelletjes",
        "Gezonde middagsnack",
        "Flexibele ophaaltijden",
      ],
      holiday: [
        "Volledige dagprogramma's tijdens schoolvakanties",
        "Thema-activiteiten en speciale evenementen",
        "Excursies en uitstapjes",
        "Kunst, knutselen en creatieve projecten",
        "Fysieke activiteiten en sport",
      ],
      flexible: [
        "Aanpasbare schema's",
        "Invalopvang indien nodig",
        "Deelopties beschikbaar",
        "Noodopvang",
        "Weekendopvang (op afspraak)",
      ],
    },

    // Common CTAs and Section Headings
    cta: {
      readyToGetStarted: "Klaar om te beginnen?",
      contactToLearnMore:
        "Neem contact op voor meer informatie over onze diensten of om een bezoek te plannen.",
      registerNow: "Nu registreren",
      ourCoreServices: "Onze kernactiviteiten",
      whyChooseOurServices: "Waarom kiezen voor onze diensten?",
      moreThanChildcare:
        "Wij bieden meer dan alleen kinderopvang – wij creëren een omgeving waarin kinderen kunnen groeien",
      additionalServices: "Aanvullende diensten",
      additionalServicesSubtitle:
        "Verrijk de ervaring van uw kind met onze optionele diensten",
      whatsIncluded: "Wat is inbegrepen:",
      meetOurTeam:
        "Maak kennis met ons toegewijde team van professionals in de kinderopvang die gepassioneerd zijn over de ontwikkeling van uw kind.",
      yearsOfExperience: "Jaren ervaring",
      happyChildren: "Blije kinderen",
      qualifiedStaff: "Gekwalificeerd personeel",
      parentSatisfaction: "Oudertevredenheid",
      parentOf: "Ouder van",
      safeTransport: "Veilig en betrouwbaar vervoer van en naar school of huis",
      extraActivities: "Speciale lessen in muziek, kunst, sport of talen",
      specialDiet: "Aanpassing voor speciale dieetwensen",
      tutoring: "Individuele of kleine groep bijlessen",
      fullDayCare: "Volledige dagopvang van 7:00 tot 18:30",
      ageAppropriate: "Leeftijdsgeschikte activiteiten en leren",
      healthyMealsIncluded: "Gezonde maaltijden en snacks inbegrepen",
      progressReports: "Regelmatige voortgangsrapporten",
      parentApp: "Oudercommunicatie-app",
      afterSchoolPickup: "Ophalen na school van lokale scholen",
      homeworkSupport: "Huiswerkbegeleiding en ondersteuning",
      recreation: "Recreatieve activiteiten en spelletjes",
      afternoonSnack: "Gezonde middagsnack",
      flexiblePickup: "Flexibele ophaaltijden",
      holidayPrograms: "Volledige dagprogramma's tijdens schoolvakanties",
      themedActivities: "Thema-activiteiten en speciale evenementen",
      fieldTrips: "Excursies en uitstapjes",
      artsCrafts: "Kunst, knutselen en creatieve projecten",
      physicalActivities: "Fysieke activiteiten en sport",
      customizableSchedules: "Aanpasbare schema's",
      dropInCare: "Invalopvang indien nodig",
      partTime: "Deelopties beschikbaar",
      emergencyCare: "Noodopvang",
      weekendCare: "Weekendopvang (op afspraak)",
    },

    // Daily Routine CTA and Features
    dailyRoutineCta: {
      joinAdventures: "Klaar om met ons dagelijkse avontuur mee te doen?",
      experienceText:
        "Ervaar ons gestructureerde maar flexibele dagritme dat leert, creatief is en leuk voor uw kind.",
      flexibleScheduleTitle: "Flexibel schema",
      flexibleScheduleDesc:
        "Wij begrijpen dat elk gezin andere behoeften heeft. Met onze flexibele planning kiest u de tijden die het beste bij uw gezin passen.",
      fullDayCare: "Volledige dagopvang (7:00 - 18:30)",
      halfDayOptions: "Halve dagopties beschikbaar",
      dropInCare: "Invalopvang indien nodig",
      specialActivitiesTitle: "Speciale activiteiten",
      specialActivitiesDesc:
        "Naast ons dagelijkse ritme bieden we speciale activiteiten en evenementen gedurende de week.",
      artWorkshops: "Kunstworkshops op dinsdag",
      musicClasses: "Muzieklessen op woensdag",
      gardenTime: "Tijd in de tuin op donderdag",
      fridayFun: "Vrijdag plezierdag activiteiten",
      scheduleVisit: "Bezoek plannen",
      contactUs: "Contact opnemen",
    },

    // Activities page - comprehensive Dutch translation
    activities: {
      hero: {
        title: "Aangeboden Activiteiten",
        subtitle:
          "Leren door te spelen in een natuurlijke, stimulerende omgeving",
      },
      intro: {
        title: "Leren door spel",
        p1: "Bij Mini Avonturiers staat leren door spel centraal. Onze activiteiten zijn zorgvuldig ontworpen om de ontwikkeling van kinderen op een natuurlijke, speelse manier te stimuleren.",
        p2: "De activiteiten zijn afgestemd op de ontwikkelingsfase, interesses en talenten van de kinderen en dragen bij aan groei op het gebied van taal, motoriek, creativiteit, sociaal-emotionele vaardigheden en wereldoriëntatie.",
        ourApproachTitle: "Onze aanpak",
        ourApproachText:
          "Wij bieden een gevarieerd dagprogramma aan, met zowel vrij spel als begeleide activiteiten. Onze aanpak is gebaseerd op Vygotsky's Zone van Naaste Ontwikkeling: we bieden activiteiten aan die net boven het huidige niveau van het kind liggen, maar haalbaar zijn met ondersteuning.",
      },
      sensory: {
        title: "Sensorische activiteiten",
        subtitle:
          "Sensorisch spel, ook wel spelen met materialen die de zintuigen prikkelen genoemd, is essentieel voor jonge kinderen.",
        helpsWithTitle: "Sensorisch spel helpt bij:",
        reggioNote:
          "Pedagogisch medewerkers begeleiden dit soort spel vanuit het Reggio Emilia-perspectief: het kind is de onderzoeker, de ruimte is de derde pedagoog, en wij ondersteunen met open vragen, woorden en observaties.",
      },
      language: {
        title: "Taalontwikkeling",
        subtitle:
          "Taal vormt de basis voor communicatie, expressie en latere schoolvaardigheden.",
        note: "Ons personeel gebruikt taalrijke interacties: zij beschrijven handelingen, stellen open vragen, breiden uitspraken uit en moedigen kinderen aan om te praten. Deze aanpak sluit aan bij de Interactieve Leesmethode en de principes van voor- en vroegschoolse educatie.",
      },
      creative: {
        title: "Creatieve expressie",
        subtitle:
          "Creatieve expressie helpt kinderen zichzelf uit te drukken, gevoelens te verwerken en hun verbeeldingskracht te ontwikkelen.",
        note: "Wij moedigen kinderen aan om hun eigen keuzes te maken en creëren een sfeer waarin experimenteren, fouten maken en fantaseren de norm zijn.",
      },
      thematic: {
        title: "Thematisch werken & ontwikkelingsgericht aanbod",
        subtitle:
          "Wij werken met thema's die aansluiten bij de belevingswereld van het kind, zoals seizoenen, Kindcentrum, dieren, beroepen, emoties, verkeer of feestdagen.",
        note: "Binnen deze thema's koppelen we activiteiten aan meerdere ontwikkelingsgebieden. Ons personeel begeleidt het spel en stimuleert verdieping door gerichte vragen, het uitbreiden van het spel en het integreren van leerdoelen.",
      },
      bso: {
        title: "Buitenschoolse opvang programma's",
        subtitle:
          "Uitdaging en plezier voor oudere kinderen. BSO-kinderen zijn vaak op zoek naar uitdaging, zelfstandigheid en ontspanning na schooltijd.",
        note: "Buitenschoolse opvangactiviteiten worden afgestemd op de eigen wensen en ideeën van de kinderen: kinderen hebben inspraak en kunnen kiezen uit verschillende opties. Dit creëert ruimte voor autonomie, vriendschap en ontspanning.",
      },
      cta: {
        title: "Klaar om onze activiteiten te ontdekken?",
        subtitle:
          "Ervaar ons diverse aanbod van activiteiten die zijn ontworpen om de ontwikkeling van uw kind door spel te stimuleren. Plan een bezoek om onze programma's in actie te zien.",
        button: "Lees meer over onze activiteiten",
      },
      sensoryActivities: {
        naturalMaterials: {
          title: "Natuurlijke materialen (zand, water, klei)",
          description:
            "Kinderen ontdekken texturen, temperaturen en vormen terwijl ze creativiteit ontwikkelen.",
        },
        outdoorElements: {
          title: "Buiten elementen (modderkeuken, tuin)",
          description: "Kansen voor zintuiglijke ervaringen in de natuur.",
        },
        creativeMaterials: {
          title: "Creatieve materialen (verf, klei, stoffen)",
          description:
            "Het stimuleren van verbeeldingskracht en fijne motoriek.",
        },
      },
      sensoryBenefits: {
        exploringTextures: "Ontdekken van verschillende texturen en sensaties",
        developMotorSkills: "Ontwikkelen van fijne en grove motoriek",
        emotionRegulation: "Verbeteren van emotionele regulatie",
        problemSolving: "Stimuleren van probleemoplossend vermogen",
      },
      languageActivities: {
        dailyReading: {
          title: "Dagelijks voorlezen",
          description: "Voorleesmomenten die woordenschat en begrip vergroten.",
        },
        visualLearning: {
          title: "Visueel leren",
          description:
            "Beelden en voorwerpen gebruiken om taalontwikkeling te ondersteunen.",
        },
        musicRhymes: {
          title: "Muziek en rijmpjes",
          description:
            "Zingen van liedjes en rijmpjes om ritme en taal te versterken.",
        },
        rolePlay: {
          title: "Rollenspel",
          description:
            "Kinderen spelen situaties na, wat expressie en creativiteit bevordert.",
        },
      },
      creativeActivities: {
        artsCrafts: {
          title: "Knutselen",
          description:
            "Schilderen, knippen en plakken om creativiteit te uiten.",
        },
        buildingConstruction: {
          title: "Bouwen en constructie",
          description:
            "Met blokken en materialen ruimtelijk inzicht ontwikkelen.",
        },
        musicDance: {
          title: "Muziek en dans",
          description:
            "Bewegen en creëren op muziek, ritme en coördinatie ontwikkelen.",
        },
        drawingColoring: {
          title: "Tekenen en kleuren",
          description: "Kleuren en vormen verkennen met potloden en stiften.",
        },
      },
      thematicAreas: {
        arithmetic: {
          title: "Rekenen",
          description: "Leuke tel- en cijferherkenningsactiviteiten.",
        },
        language: {
          title: "Taal",
          description: "Verhalen vertellen, lezen en woordenschatspelletjes.",
        },
        motorSkills: {
          title: "Motorische vaardigheden",
          description: "Spellen die beweging en coördinatie stimuleren.",
        },
        socialBehavior: {
          title: "Sociaal gedrag",
          description: "Activiteiten die samenwerking en empathie bevorderen.",
        },
      },
      bsoActivities: {
        sportsExercise: {
          category: "Sport en beweging",
          activities: [
            "Voetbal en teamsporten",
            "Buitenspelen en renspelletjes",
          ],
        },
        technologyExperiments: {
          category: "Technologie en experimenten",
          activities: [
            "Eenvoudige proefjes",
            "Bouwen met LEGO en technische speelgoed",
          ],
        },
        cookingBaking: {
          category: "Koken en bakken",
          activities: [
            "Koekjes bakken en eenvoudige recepten",
            "Gezonde snacks maken",
            "Smaken en texturen ontdekken",
          ],
        },
        excursionsTrips: {
          category: "Uitjes en excursies",
          activities: [
            "Natuurwandelingen en parkbezoeken",
            "Bezoek aan lokale bezienswaardigheden",
          ],
        },
      },
    },

    // Educational Staff page translations
    educationalStaff: {
      title: "Rol van pedagogische medewerkers",
      subtitle:
        "Het hart van onze visie en kwaliteit - actieve opvoedingspartners, emotionele ankers en groeicoaches",

      sectionTitle: "Rol van pedagogische medewerkers",
      introText1:
        "Bij Mini Avonturiers staan onze pedagogische medewerkers centraal in onze visie en kwaliteit. Zij zijn de levende vertaling van ons pedagogische beleid in de dagelijkse praktijk. Onze medewerkers zijn niet alleen toezichthouders, maar ook actieve opvoedingspartners, emotionele ankers en groeicoaches.",
      introText2:
        "Wij geloven dat de kwaliteit van de interactie tussen kinderen en volwassenen de kwaliteit van de opvang bepaalt. Daarom zijn onze medewerkers sensitief, responsief, professioneel en reflectief, gepassioneerd over hun werk en aandachtig voor de behoeften van kinderen.",

      coreValues: {
        title: "Onze kernwaarden",
        sensitiveResponsive: {
          title: "Sensitieve en responsieve houding",
          description:
            "Onze medewerkers zijn getraind in sensitieve responsiviteit, een van de vier kernpedagogische doelstellingen van de Wet Kinderopvang. Zij zien het kind echt, herkennen subtiele signalen (bijv. spanning, behoefte aan rust of aandacht) en reageren snel en adequaat.",
          practices: [
            "Kinderen worden bij naam genoemd en persoonlijk begroet",
            "Pedagogisch medewerkers communiceren op ooghoogte, met een rustige stem en open lichaamstaal",
            "Emoties worden benoemd en erkend",
            "Kinderen worden nooit genegeerd, afgewezen of onderbroken in hun ervaring",
          ],
        },
        guidingGroupProcesses: {
          title: "Begeleiden van groepsprocessen",
          description:
            "Naast individuele aandacht speelt het groepsproces een belangrijke rol in de ontwikkeling. Pedagogisch medewerkers begeleiden de groep actief en bevorderen een sfeer van veiligheid, samenwerking en respect.",
          practices: [
            "Kinderen helpen contact te maken en conflicten op te lossen",
            "Delen en samenwerken stimuleren",
            "Empathie en sociale vaardigheden ontwikkelen",
            "Balans tussen ruimte geven en grenzen stellen",
          ],
        },
        providingStructure: {
          title: "Structuur bieden zonder starheid",
          description:
            "Een duidelijke dagstructuur is essentieel voor rust en veiligheid. Onze medewerkers zorgen voor voorspelbaarheid, duidelijke overgangen en een helder ritme, maar zijn ook flexibel genoeg om in te spelen op de behoeften van het moment.",
          practices: [
            "Zorgen voor voorspelbaarheid en duidelijke overgangen",
            "Helderheid bieden over wat er gebeurt, waarom en wanneer",
            "Kinderen helpen leren plannen en inschatten",
            "Aanpassen aan de behoeften van het moment",
          ],
        },
        allowingAutonomy: {
          title: "Ruimte geven voor autonomie",
          description:
            "Onze pedagogische medewerkers stimuleren zelfstandigheid. Ze geven kinderen de ruimte om eigen keuzes te maken, hun grenzen te ontdekken en fouten te maken, alles onder veilige begeleiding.",
          practices: [
            "Kinderen kunnen kiezen wat ze willen spelen of waar ze willen zitten",
            "Ze mogen helpen met dagelijkse routines",
            "Activiteiten zijn open en uitnodigend, geen verplichte uitkomsten",
            "Medewerkers bieden nabijheid en ondersteuning waar nodig",
          ],
        },
        individualDevelopment: {
          title: "Ondersteuning van individuele ontwikkeling",
          description:
            "Elk kind ontwikkelt zich in zijn eigen tempo en op zijn eigen manier. Onze pedagogische medewerkers volgen de ontwikkeling van kinderen systematisch via observaties, gesprekken en oudergesprekken.",
          practices: [
            "Systematische observatie en monitoring",
            "Kijken naar de totale ontwikkeling: taal, motoriek, sociaal-emotioneel, cognitief",
            "Activiteiten afstemmen op de behoeften en interesses van het kind",
            "Gebruik maken van kindvolgsystemen en reflectietools",
          ],
        },
        everyEmployeeMentor: {
          title: "Elke medewerker als mentor",
          description:
            "Elk kind bij Mini Avonturiers heeft een vaste mentor: een pedagogische medewerker die fungeert als aanspreekpunt voor zowel het kind als de ouders.",
          practices: [
            "De ontwikkeling van het kind nauwgezet volgen",
            "Mentorgesprekken voeren met ouders",
            "Een warme, individuele band met het kind opbouwen",
            "Overzicht houden over het welzijn en de groeilijn van het kind",
          ],
        },
      },

      professionalDevelopment: {
        title: "Professionele ontwikkeling van ons team",
        intro:
          "Om kwaliteit te waarborgen investeren we continu in de professionele en persoonlijke ontwikkeling van onze medewerkers. We verwachten dat onze medewerkers niet alleen handelen op routine, maar ook bewuste pedagogische keuzes maken, met respect voor het kind als individu.",
        areas: {
          intervision: {
            title: "Regelmatige intervisie en teamreflectie",
            description:
              "Structurele teamvergaderingen voor professionele groei en gezamenlijk leren",
          },
          education: {
            title: "Vervolgopleidingen en trainingen",
            description:
              "Voortdurend leren op het gebied van emotionele ontwikkeling, spelbegeleiding en communicatie",
          },
          coaching: {
            title: "Coaching op de werkvloer",
            description:
              "Begeleiding en ondersteuning op de werkvloer voor professionele ontwikkeling",
          },
          documentation: {
            title: "Tijd voor documentatie en voorbereiding",
            description: "Gerichte tijd voor planning, documentatie en overleg",
          },
        },
      },

      sensitiveResponsiveness: {
        title: "Sensitieve responsiviteit",
        intro:
          "Deze houding is gebaseerd op de hechtingstheorie van Bowlby en Ainsworth: consequent en warm reageren op het kind bevordert een veilige hechting, die cruciaal is voor een gezonde sociaal-emotionele ontwikkeling.",
        secureAttachment: {
          title: "Veilige hechting",
          description:
            "Consistente en warme reacties bevorderen veilige hechting en vormen een basis voor gezonde sociaal-emotionele ontwikkeling en exploratie.",
        },
        subtleCues: {
          title: "Subtiele signalen",
          description:
            "Medewerkers herkennen en reageren op subtiele signalen zoals spanning, behoefte aan rust of aandacht, zodat elk kind zich gezien en begrepen voelt.",
        },
      },

      teamConsultation: {
        title: "Teamoverleg en verder scholing",
        teamCollaboration: {
          title: "Team samenwerking",
          description:
            "Regelmatige teamoverleggen en consultaties zorgen voor consistente kwaliteit en gedeelde best practices binnen het team.",
          practices: [
            "Regelmatige intervisie en teamreflectie",
            "Gedeeld leren en uitwisseling van best practices",
            "Gezamenlijk problemen oplossen",
          ],
        },
        continuousLearning: {
          title: "Voortdurend leren",
          description:
            "Voortdurende scholing en trainingen zorgen dat ons team up-to-date blijft met de nieuwste pedagogische inzichten en onderzoek.",
          practices: [
            "Vervolgopleidingen en gespecialiseerde trainingen",
            "Coaching- en mentorprogramma’s",
            "Professionele ontwikkelingskansen",
          ],
        },
      },

      staffInAction: {
        title: "Ons team in actie",
        professionalMentors: {
          title: "Professionele mentoren",
          description:
            "Elk kind heeft een vaste mentor die hun ontwikkeling nauw volgt en een vertrouwd aanspreekpunt is voor ouders.",
        },
        emotionalAnchors: {
          title: "Emotionele ankers",
          description:
            "Onze medewerkers bieden emotionele stabiliteit en steun, waardoor kinderen zich veilig en zelfverzekerd voelen in hun omgeving.",
        },
        growthCoaches: {
          title: "Groeicoaches",
          description:
            "Medewerkers begeleiden de ontwikkeling van kinderen via spel, leeractiviteiten en ondersteunende interacties die groei bevorderen.",
        },
      },

      cta: {
        title: "Ontmoet ons professionele team",
        subtitle:
          "Ervaar de toewijding en expertise van onze pedagogische medewerkers die elke dag bijzonder maken voor uw kind",
        scheduleVisit: "Plan een bezoek",
        learnMore: "Lees meer",
      },

      inPractice: "In de praktijk:",
    },
    // Dashboard page translations
    dashboard: {
      welcome: "Welkom",
      subtitle:
        "Beheer de informatie van uw kinderen en blijf op de hoogte van hun activiteiten",
      birthDate: "Geboortedatum",
      group: "Groep",
      notAssigned: "Niet toegewezen",
      registeredSince: "Geregistreerd sinds",
      viewDetails: "Bekijk Details",
      editInfo: "Informatie Bewerken",
      quickActions: "Snelle Acties",
      scheduleVisit: "Bezoek plannen",
      contact: "Contact",
      registration: "Registratie",
      reports: "Rapporten",
      recentUpdates: "Recente Updates",
      newActivity: "Nieuwe activiteiten zijn toegevoegd aan het programma",
      photosAdded: "Nieuwe foto's van de activiteiten van vandaag",
      menuUpdate: "Het menu van deze week is bijgewerkt",
      daysAgo: "dagen geleden",
      weekAgo: "week geleden",
    },

    // Emotional Safety page translations
    emotionalSafety: {
      cta: {
        scheduleVisit: "Plan een bezoek",
        learnMore: "Lees meer",
      },

      title: "Emotionele veiligheid & vertrouwen",
      subtitle:
        "De basis van onze pedagogische visie waar elk kind zich geliefd, gezien en gehoord voelt",
      sectionTitle: "4. Emotionele veiligheid & vertrouwen",
      introText1:
        "Bij Mini Avonturiers is emotionele veiligheid de basis van onze pedagogische visie. Elk kind heeft behoefte om zich geliefd, gezien en gehoord te voelen om optimaal te kunnen ontwikkelen. Wij creëren een omgeving waar kinderen veilige hechtingen kunnen vormen, leren hun emoties te begrijpen en te verwerken, en de zekerheid ervaren dat zij zichzelf mogen zijn.",
      introText2:
        "Onze aanpak is gebaseerd op onder andere de hechtingstheorie van John Bowlby en Mary Ainsworth, die stellen dat kinderen een veilige basis nodig hebben vanwaaruit zij de wereld kunnen verkennen. Ook Emmi Pikler en de Gordon Methode (van Thomas Gordon) zijn belangrijke inspiratiebronnen voor onze respectvolle communicatie en de centraliteit van de relatie.",

      whyEssential: "Waarom emotionele veiligheid essentieel is",
      benefits: {
        dareExplore: {
          title: "Durven verkennen",
          description:
            "Kinderen voelen zich veilig om nieuwe dingen te proberen en risico's te nemen",
        },
        emotionalRegulation: {
          title: "Betere emotieregulatie",
          description:
            "Leren omgaan met emoties, frustratie en teleurstellingen",
        },
        positiveSelf: {
          title: "Positief zelfbeeld",
          description:
            "Sterke sociale vaardigheden en zelfvertrouwen ontwikkelen",
        },
        lastingRelationships: {
          title: "Duurzame relaties",
          description:
            "Veilige hechtingen opbouwen met volwassenen en leeftijdsgenoten",
        },
      },

      inPractice: "In de praktijk",
      practices: {
        familiarFaces: {
          title: "Bekende gezichten en voorspelbaarheid",
          description:
            "We werken met een vast team van kinderopvang professionals voor elke groep. Dit zorgt voor vertrouwdheid en vertrouwen. Kinderen weten wie er voor hen zorgt en hoeven zich niet constant aan te passen.",
          dailyStructure: "Dagstructuur",
          dailyStructureDesc:
            "De dagstructuur is helder en voorspelbaar. Overgangen worden ruim van tevoren aangekondigd en begeleid met rituelen (zoals liedjes of korte uitleg), zodat kinderen zich kunnen voorbereiden en niet overvallen worden. Dit voorkomt onnodige stress.",
        },
        individualAttention: {
          title: "Aandacht voor het individuele kind",
          description:
            "We bieden individuele aandacht, ook binnen de groep. Door goed te observeren herkennen we de behoeften van elk kind. Soms betekent dat een knuffel, soms een moment rust, soms een helpende hand of een luisterend oor.",
          staffApproach: "Aanpak onderwijzend personeel",
          approaches: {
            eyeLevel: "Communiceren op ooghoogte, met open lichaamshouding",
            acknowledgeFeelings: "Actief benoemen en erkennen van gevoelens",
            neverReject: "Emoties nooit wegwuiven of bagatelliseren",
            clearBoundaries:
              "Duidelijke grenzen stellen op respectvolle, liefdevolle manier",
          },
        },
        parentalTrust: {
          title: "Ouderlijk vertrouwen",
          description:
            "Emotionele veiligheid wordt niet alleen binnen de kinderopvang gecreëerd; het begint bij vertrouwen tussen ouders en kinderopvang medewerkers. We investeren bewust in de relatie met ouders.",
          parentPartnership: "Ouder partnerschap",
          partnerships: {
            warmHandover: "Warme overdracht bij breng- en haalmomenten",
            openCommunication: "Open, eerlijke communicatie zonder oordeel",
            takeConcerns: "Zorgen van ouders serieus nemen",
            involveParents: "Ouders betrekken bij de ontwikkeling van hun kind",
          },
        },
      },

      trainingDevelopment:
        "Training en ontwikkeling van onderwijzend personeel",
      trainingIntro:
        "Om dit te bereiken verwachten we van ons onderwijzend personeel dat zij specifieke competenties ontwikkelen en hun vaardigheden in emotionele veiligheidspraktijken continu verbeteren.",
      staffExpectations: {
        selfReflective: {
          title: "Zelfreflectief werken",
          description:
            "Regelmatig reflecteren op eigen handelen en communicatie",
        },
        emotionalTraining: {
          title: "Training emotionele communicatie",
          description:
            "Geschoold in emotionele communicatie met kinderen door coaching en training",
        },
        developmentalKnowledge: {
          title: "Kennis ontwikkelingspsychologie",
          description: "Gedrag en emoties van kinderen begrijpen in context",
        },
        timeToConnect: {
          title: "Tijd nemen voor verbinding",
          description:
            "Ruimte maken om te verbinden, ook tijdens drukke momenten",
        },
        visiblySensitive: {
          title: "Zichtbaar sensitief zijn",
          description: "Kinderen zien, spiegelen en ondersteunen",
        },
      },
      continuousDevelopment: "Continue ontwikkeling",
      continuousDevDesc:
        "Dit faciliteren we door regelmatige pedagogische gesprekken, trainingen en scholing, waarbij dit onderwerp structureel aandacht krijgt. Emotionele veiligheid is geen bijzaak—het is de kern van onze pedagogische kwaliteit.",

      creatingSafeSpaces: "Veilige ruimtes creëren",
      secureEnvironment: {
        title: "Veilige omgeving",
        description:
          "Onze ruimtes zijn ontworpen om aan te voelen als een tweede thuis, met gezellige hoekjes, zachte verlichting en bekende gezichten die een gevoel van verbondenheid en veiligheid creëren voor elk kind.",
      },
      emotionalConnection: {
        title: "Emotionele verbinding",
        description:
          "Elke interactie is een kans om vertrouwen op te bouwen. We luisteren, valideren gevoelens en reageren met empathie, waardoor we kinderen helpen emotionele intelligentie en veerkracht te ontwikkelen.",
      },

      experienceEnvironment: "Ervaar onze zorgzame omgeving",
      experienceSubtitle:
        "Zie hoe we emotionele veiligheid creëren en vertrouwensvolle relaties opbouwen met elk kind",
    },
    footer: {
      connectWith: "Connect met Mini Avonturiers",
      email: "info@mini-avonturiers.nl",
      aboutUs: "Over ons",
      services: "Diensten",
      dailyRoutine: "Dagelijkse routine",
      registerNow: "Registreer nu",
      address: "Adres",
      addressText: "Lindenhof 3,",
      addressText2: "3442 GT Woerden",
      telephone: "Telefoonnummer",
      phoneNumber: "+31 6 16504171",
      copyright: "© 2025 Mini Avonturiers. Alle rechten voorbehouden.",
    },
  },

  ar: {
    // Vision page
    vision: {
      title: "الرؤية التربوية",
      subtitle:
        "نهجنا لرعاية الأطفال يعتمد على الأبحاث العلمية والمبادئ التربوية المثبتة",
      section21: {
        title: "رؤيتنا حول رعاية الأطفال",
        content:
          "في مغامري مينى، نؤمن بأن كل طفل فريد وينمو في بيئة تعزز الفضول واللعب التعليمي والهيكل الواضح. نقدم للأطفال مساحة آمنة حيث يمكنهم النمو ليصبحوا أفرادًا فضوليين واجتماعيين ومستقلين. نركز على القيم الأربعة الرئيسية لريكسن-والرافن: الأمان العاطفي والمهارات الشخصية والاجتماعية والمعايير والقيم.",
      },
      section22: {
        title: "رؤيتنا للأطفال",
        content:
          "في مغامري مينى، الطفل يحتل المركز، بكل فرديته. نحتضن الاعتقاد بأن كل طفل فريد، بشخصيته الخاصة، وتيرته، ومسار تطوره، ودافعهم الداخلي للتعلم. تتماشى هذه الرؤية مع صورة الطفل المتمكن كما هو موضح في نهج ريجيو إيميليا. في هذه الثقافة، لا يُنظر إلى الطفل على أنه وعاء فارغ يجب أن يُملأ، بل كمستكشف نشط وقوي يستكشف العالم بناءً على اهتماماته الخاصة.",
        piaget: {
          title: "نظرية جان بياجيه",
          content:
            "جان بياجيه يدعم هذا المفهوم في نظريته عن التطور المعرفي. حسب قوله، الأطفال يميلون بطبيعتهم لتجربة واختبار الفرضيات وإعطاء معنى للتجارب. لذلك، نوفر للأطفال مساحة ليس فقط للعب ولكن أيضًا لارتكاب الأخطاء وتجربة أشياء جديدة واكتساب رؤى شخصية.",
        },
        bowlby: {
          title: "نظرية التعلق لجون بولبي",
          content:
            "القاعدة الآمنة ضرورية. كما تظهر نظرية التعلق لجون بولبي، فإن التعلق الآمن هو شرط أساسي للتطور الاجتماعي-العاطفي السليم. في مغامري مينى، نوفر بيئة دافئة ومستقرة ومحبّة حيث يشعر الأطفال بأنهم مرئيون ومسموعون ومقدّرون. فقط عندها يمكنهم الانفتاح على العالم من حولهم.",
        },
      },
      section23: {
        title: "رؤيتنا حول التطور",
        content:
          "نرى التطور كعملية شاملة يتطور فيها الطفل في مجالات متعددة بشكل متزامن: اجتماعيًا، عاطفيًا، حركيًا، معرفيًا، وإبداعيًا. هذا المنظور مستوحى من نظرية ليف فيغوتسكي، التي تؤكد على أن التطور يحدث ضمن التفاعل الاجتماعي وأن البيئة، خصوصًا الكبار وزملائهم الأكثر مهارة، يلعبون دورًا حاسمًا.",
        playTool: {
          title: "اللعب كأداة للتطوير",
          content:
            'اللعب هو الأداة التطويرية الأهم التي نستخدمها. ليس كوسيلة للاستجمام، ولكن كوسيلة للتعلم والنمو. يعزز اللعب الإبداعية والمهارات التحليلية وتطوير اللغة والتعاون والمهارات الحركية والمزيد. يدعم هذا الفكرة ماريا مونتيسوري التي أكدت على أن "اللعب هو عمل الطفل." يقود هذا المفهوم نهجنا.',
        },
        thematicApproach: {
          title: "النهج الموضوعي",
          content:
            "داخل نهجنا الموضوعي، القائم على التجارب الحالية، نخلق بيئات تعليمية غنية حيث يمكن للأطفال الاكتشاف والتجربة والإبداع والتأمل بأنفسهم. هذه الموضوعات لا تُعرض بشكل هرمي، بل غالبًا ما تنشأ من داخل الطفل نفسه. يتماشى هذا مع رؤية ريجيو إيميليا، التي ترى الأطفال كمتحدثين بمئة لغة—بمعنى آخر، هناك عدد لا يحصى من الطرق التي يعبر بها الأطفال عن أنفسهم، ويجب أخذها جميعها على محمل الجد.",
        },
        staffRole: {
          title: "دور الطاقم التعليمي",
          content:
            "يقوم طاقمنا التعليمي بدور إرشادي ومتابع: يخلقون الظروف، يطرحون الأسئلة، ينظرون إلى السلوك ويدعمون دون توجيه مباشر. يطبقون مبادئ طريقة جوردون (من قبل توماس جوردون): الاستماع دون حكم، التواصل بوضوح وحل المشاكل من خلال الاحترام المتبادل.",
        },
      },
      section24: {
        title: "التصحيح والتأكيد",
        behaviorCommunication: {
          title: "السلوك كوسيلة للتواصل",
          content:
            "السلوك هو وسيلة للتواصل. تماشياً مع نهج إيمي بيكلر والبيداغوجية غير المباشرة، نعتقد أنه من المهم دائمًا التفكير في ما يحاول الطفل التعبير عنه من خلال سلوكه. السلوك غير المرغوب فيه ليس إشارة إلى القصد أو عدم الرغبة، بل هو غالباً إشارة إلى الإحباط أو العجز أو حاجة لم تُلَبٍّ.",
        },
        respectfulApproach: {
          title: "نهج محترم",
          content:
            "عندما يحدث سلوك تحدي، نتبنى نهجاً يحترم ويدعم. نتحدث مع الطفل، نتناول مشاعرهم ونضع ما قد يمرون به في كلمات. هذا يساعد الأطفال على تطوير التنظيم الذاتي، وهو جانب أساسي من النمو الاجتماعي والعاطفي.",
        },
        positiveReinforcement: {
          title: "التعزيز الإيجابي",
          content:
            "نضع الحدود باستمرار وبحب، مع قواعد واضحة، إيقاع متناسق وتوقعات واضحة. نؤمن بقوة التعزيز الإيجابي، كما تم التأكيد في نظرية ألفي كون. من خلال تحديد وتقدير السلوك المرغوب بشكل حقيقي، نزيد من الدافع الداخلي للطفل ونعزز تقديرهم لذاتهم.",
        },
      },
      section25: {
        title: "المبادئ الأساسية في التطبيق",
        content:
          "تُبنى ممارستنا التربوية اليومية على خمس ركائز قوية، تتفاعل باستمرار مع بعضها البعض. هذه المبادئ الأساسية مستوحاة من رؤى تربوية مستندة إلى العلم، وتنعكس في أفعالنا وتصميم المساحة وتنظيم اليوم.",
        principles: {
          sensitiveResponsiveness: {
            title: "1. الاستجابة الحساسة",
            content:
              "بناء على نظرية التعلق لبولبي والتي طورت أكثر بواسطة ماري آينسورث، تعني الاستجابة الحساسة أن مقدمو الرعاية يدركون إشارات الطفل في الوقت المناسب والدقة وبلطف. نحن متاحون عاطفياً ومستجيبون لاحتياجات الطفل، سواء كان للأمان أو الاهتمام أو الطعام أو الراحة. تشكل هذه الاستجابة الأساس للارتباط الآمن، والذي بدوره يشكل الأساس لتطور أكبر.",
          },
          respectAutonomy: {
            title: "2. احترام الاستقلال الذاتي",
            content:
              "نريد أن نمنح الأطفال الثقة والمساحة للتصرف بشكل مستقل. سواء في تناول الطعام بشكل مستقل، اتخاذ القرارات أو حل النزاعات، نحن ندعم ولا نحكم. هذا لا يعزز الاستقلال الذاتي فقط، ولكنه يعزز أيضاً الشعور بالمسؤولية والثقة بالنفس لدى الأطفال.",
          },
          structureRhythm: {
            title: "3. الهيكل والإيقاع",
            content:
              "ينمو الأطفال في بيئة متوقعة ومنظمة. يساهم الهيكل اليومي المتناسق في الهدوء الداخلي والتوازن. يوفر الإيقاع الاستقرار، وتعمل الطقوس المحفوظة (مثل الأغاني والدوائر والانتقالات) على تعزيز الشعور بالأمان والتماسك. بالتالي، نحافظ على جدول يومي مدروس جيداً مع توازن كافٍ بين النشاط والراحة.",
          },
          activeInvolvement: {
            title: "4. التفاعل الفعال للطاقم التعليمي",
            content:
              "تكمن قوة طاقمنا التعليمي في حضورهم واهتمامهم وتأملاتهم. إنهم ليسوا مجرد متفرجين، بل هم مكتشفون مشاركون. بعقل مفتوح ومراقبة مستمرة، يرشدون عملية تطور الطفل. نستثمر في التطوير المهني والتأمل الذاتي، لضمان أن طاقمنا ينمي مهاراته باستمرار.",
          },
          positiveInteraction: {
            title: "5. التفاعل الإيجابي",
            content:
              "الكلمات مهمة. الطريقة التي نتحدث بها مع الأطفال تشكل كيف يشعرون بأنفسهم والعالم. نستخدم لغة إيجابية وجذابة وملائمة. تتم مناقشة الصراعات، وليس معاقبتها. تركز المجاملات على الجهد والعملية. هذا الشكل من التواصل يعزز علاقة الطفل وكفاءته واستقلاليته—ثلاث احتياجات نفسية أساسية وفقًا لنظرية تحديد الذات لديشي ورايان.",
          },
        },
      },
      section26: {
        title: "التقييم والتحديث",
        content:
          "يتم تقييم خطة سياستنا التربوية سنويًا بالتعاون مع الفريق والمدرب التربوي ولجنة الآباء. نجمع ملاحظات من الميدان، نناقش رؤى جديدة، وتطورات حالية. نعدل السياسة حيثما كان ذلك ضروريًا لضمان بقائها حاليًا وقابلة للتطبيق ومتوافقة مع رؤيتنا.",
      },
      documentsSection: {
        documents: "الوثائق وخطط السياسات",
        kdv: "خطة السياسة التربوية لحضانة الأطفال (KDV)",
        bso: "خطة السياسة التربوية لرعاية ما بعد المدرسة (BSO)",
        safety: "سياسة السلامة والصحة",
      },
    },

    nav: {
      home: "الرئيسية",
      about: "معلومات عنا",
      insideus: "داخل ميني أفونتوريرز",
      vision: "النمو والتطوير",
      whatWeOffer: "ما نقدمه",
      // ما نقدمه
      services: "برامج رعاية الأطفال",
      transportationPolicy: "خدمة النقل والتوصيل",
      nutrition: "وجبات صحية وتغذية",
      ipadSystem: "الأدوات الرقمية والتحديثات اليومية",

      ourDay: "يومنا",
      learningGrowth: "التعلّم والنمو",
      familyConnection: "التواصل العائلي",
      healthSafety: "الصحة والسلامة",

      // المزيد - العمود 1
      dailyRoutine: "الروتين اليومي",
      emotionalSafety: "السلامة العاطفية",
      activities: "الوجبات والأنشطة",

      // المزيد - العمود 2
      horizontalGroups: "هيكل الفئات العمرية",
      educationalStaff: "الطاقم التربوي",
      receivingPolicy: "سياسة التهيئة",

      // المزيد - العمود 3
      parentCollaboration: "التواصل مع الوالدين",
      faq: "الأسئلة الشائعة",

      // المزيد - العمود 4
      safetyHygiene: "النظافة",
      openingClosingChecklist: "قائمة الفتح والإغلاق",

      // أخرى
      contact: "اتصل بنا",
      register: "تسجيل",
      registerChild: "تسجيل طفل",
      registerTour: "تسجيل جولة",
      more: "المزيد",
      login: "تسجيل الدخول",
      dashboard: "لوحة التحكم",
      logout: "تسجيل الخروج",
    },

    openingClosingChecklist: {
      title: "قائمة التحقق من الفتح والإغلاق",
      subtitle: "إجراءات يومية منظمة للسلامة والاستمرارية",
      intro:
        "في Mini Avonturiers، نؤمن بأهمية أن يبدأ كل يوم وينتهي بطريقة آمنة ومنظمة. لذلك نستخدم قائمة تحقق قياسية للفتح والإغلاق يتبعها جميع العاملين في رعاية الأطفال. تضمن هذه القائمة إتمام جميع الفحوصات والتحضيرات اللازمة قبل وصول الأطفال وبعد مغادرة آخر طفل.",
      intro2:
        "من خلال اتباع هذه الإجراءات بشكل منهجي، نضمن السلامة والنظافة والهدوء والاستمرارية في رعايتنا.",

      section1: {
        title: "هدف قائمة التحقق",
        description: "تخدم قائمة التحقق عدة أهداف:",
        purposes: [
          "ضمان السلامة المادية للمكان والمواد.",
          "توفير بيئة صحية لبداية يوم صحية.",
          "بداية متوقعة وهادئة للأطفال والموظفين.",
          "تسليم واضح بين نوبات الصباح والمساء.",
          "أداة للمساءلة أمام المديرين والخدمة الصحية البلدية.",
        ],
        note: "يوقع كل موظف على ما تم إنجازه، لضمان السيطرة والوضوح وإمكانية المتابعة داخل الفريق.",
      },

      section2: {
        title: "قائمة التحقق عند الفتح (الصباح)",
        description:
          "قبل بدء رعاية الأطفال (قبل الساعة 7:00 )، يقوم الموظف بالخطوات التالية:",
        subsections: {
          safety: {
            title: "السلامة العامة",
            points: [
              "التأكد من أن المبنى مغلق بإحكام (لا نوافذ أو أبواب مفتوحة).",
              "تشغيل الإضاءة الداخلية والخارجية حسب الحاجة.",
              "التحقق من نظام الإنذار عند الدخول.",
            ],
          },
          firstAid: {
            title: "الإسعافات الأولية والنظافة",
            points: [
              "التحقق من حقيبة الإسعافات الأولية (وجود المواد وتواريخ صلاحيتها).",
              "التأكد من توفر الحفاضات والمناديل والقفازات بشكل كافٍ.",
              "التحقق من غرفة تغيير الملابس: نظيفة ومرتبة وبها مواد كافية.",
            ],
          },
          cleaning: {
            title: "التنظيف والتهوية",
            points: [
              "تهوية الغرف عند الدخول (فتح النوافذ/الأبواب في الأيام الجافة).",
              "الفحص البصري للمطبخ والحمامات وغرفة الأطفال للتأكد من النظافة.",
              "التحقق من تفريغ صناديق القمامة.",
              "تشغيل نظام تدوير الهواء إن وجد.",
            ],
          },
          materials: {
            title: "مواد اللعب والتجهيزات",
            points: [
              "التأكد من سلامة أماكن اللعب والطاولات (لا أجزاء مفكوكة أو حواف حادة).",
              "التأكد من توفر مواد مناسبة للأعمار المختلفة.",
              "تزويد الألعاب عند الحاجة وإزالة المواد المتسخة.",
            ],
          },
          preparation: {
            title: "التحضير للتسليم",
            points: [
              "تحضير الجهاز اللوحي/الآيباد للتواصل مع أولياء الأمور.",
              "مراجعة قوائم المجموعات وملاحظات اليوم السابق.",
              "تحديد الموجهين لأي نقاط تحتاج اهتمام.",
            ],
          },
        },
      },

      section3: {
        title: "قائمة التحقق عند الإغلاق (نهاية اليوم)",
        description: "بعد مغادرة آخر طفل، يتم إكمال قائمة التحقق التالية:",
        subsections: {
          childControl: {
            title: "مراجعة الأطفال والتسليم",
            points: [
              "التأكد من تسجيل خروج جميع الأطفال عبر النظام. وتم تسجيل أو استكمال التواصل مع أولياء الأمور.",
              "تدوين أي ملاحظات خاصة لليوم التالي.",
            ],
          },
          spacePreparation: {
            title: "تحضير المكان للإغلاق",
            points: [
              "تنظيف الطاولات والأرضيات وترتيب الألعاب.",
              "تنظيف المطبخ وتفريغ صناديق القمامة.",
              "التحقق من الحمامات ومرافق التغيير وتزويدها عند الحاجة.",
            ],
          },
          technology: {
            title: "التقنية والمبنى",
            points: [
              "التأكد من النوافذ: مغلقة ومقفلة.",
              "التحقق من الثلاجة: تغطية المنتجات وفحص درجة الحرارة.",
              "إطفاء الأجهزة (المصابيح، الأجهزة اللوحية، آلة القهوة).",
              "تشغيل نظام الإنذار وقفل الباب الأمامي.",
            ],
          },
        },
        note: "كل موظف يقوم بالإغلاق يوقع ويدون أي ملاحظات (مثل المخزون المتبقي، الأغراض المنسية أو الأعطال الفنية). في نهاية الأسبوع يتم التركيز بشكل خاص على إدارة المخزون والطلبات.",
      },

      section4: {
        title: "المسؤولية والمتابعة",
        points: [
          "كل موظف مسؤول عن إكمال قائمة التحقق بشكل صحيح وكامل.",
          "المدير يتحقق من النتائج بشكل عشوائي (مثل مخزون الإسعافات الأولية).",
          "يتم الإبلاغ فورًا عن أي انحرافات أو أضرار أو مواقف غير آمنة لمدير الموقع.",
          "يتم الاحتفاظ بالقائمة رقميًا أو ورقيًا لكل نصف يوم (بسبب متطلبات الأرشفة الخاصة بالبلدية).",
        ],
      },
    },

    safetyHygiene: {
      hero: {
        title: "السلامة والنظافة والأجواء",
        subtitle: "خلق بيئة آمنة ونظيفة وراعية لكل طفل",
      },
      intro: {
        title: "سلامة طفلك هي أولويتنا",
        text1:
          "في Mini Avonturiers، نلتزم بأعلى معايير السلامة والنظافة والأجواء لضمان أن يشعر كل طفل بالأمان والصحة والسعادة تحت رعايتنا.",
        text2:
          "توفر بروتوكولاتنا الشاملة ومساحاتنا المصممة بعناية بيئة يمكن للأطفال فيها الازدهار والتعلم والنمو بثقة.",
        commitmentTitle: "التزامنا",
        commitments: [
          "بروتوكولات صارمة للسلامة",
          "ممارسات منتظمة للنظافة",
          "بيئة تعليمية هادئة",
          "تدريب احترافي للموظفين",
        ],
      },
      physicalSafety: {
        title: "السلامة الجسدية وإرشادات GGD",
        subtitle:
          "نلتزم بإرشادات صارمة من GGD (خدمة الصحة البلدية) لضمان أعلى معايير السلامة الجسدية لجميع الأطفال.",
        complianceTitle: "الامتثال لـ GGD",
        inspectionsTitle: "عمليات التفتيش المنتظمة",
        inspections: [
          "عمليات تفتيش سنوية للسلامة من GGD",
          "عمليات تدقيق داخلية شهرية",
          "تدريب مستمر للموظفين على السلامة",
          "مراجعة إجراءات الطوارئ",
        ],
        standardsTitle: "معايير السلامة",
        standards: [
          "معدات ومواد مناسبة للعمر",
          "مداخل ومخارج آمنة للمبنى",
          "نسبة مناسبة بين الموظفين والأطفال",
          "موظفون معتمدون في الإسعافات الأولية",
        ],
      },
      safetyGuidelines: [
        {
          title: "سلامة المبنى",
          description:
            "جميع المرافق تلبي معايير السلامة الصارمة لـ GGD مع عمليات تفتيش منتظمة",
          icon: "🏢",
          color: "bg-blue-500",
        },
        {
          title: "سلامة المعدات",
          description: "جميع الألعاب والمعدات مناسبة للعمر ويتم فحصها بانتظام",
          icon: "🧸",
          color: "bg-green-500",
        },
        {
          title: "إجراءات الطوارئ",
          description: "بروتوكولات طوارئ شاملة وطاقم مدرب",
          icon: "🚨",
          color: "bg-red-500",
        },
        {
          title: "الإشراف",
          description: "إشراف مستمر مع نسب مناسبة بين الموظفين والأطفال",
          icon: "👁️",
          color: "bg-purple-500",
        },
      ],
      hygiene: {
        title: "بروتوكولات النظافة",
        subtitle:
          "تضمن ممارسات النظافة الشاملة بيئة صحية لجميع الأطفال والموظفين.",
      },
      hygieneProtocols: [
        {
          title: "التنظيف اليومي",
          description: "تنظيف شامل يوميًا لجميع الأسطح والألعاب والمعدات",
          icon: "🧹",
          details: [
            "تعقيم الأسطح كثيرة الاستخدام",
            "تنظيف وتدوير الألعاب بانتظام",
            "تطهير أماكن تناول الطعام",
          ],
          color: "bg-green-500",
        },
        {
          title: "النظافة الشخصية",
          description: "تعليم والحفاظ على عادات غسل اليدين والنظافة السليمة",
          icon: "🧼",
          details: [
            "روتين غسل اليدين المتكرر",
            "إجراءات تغيير الحفاضات الصحيحة",
            "بروتوكولات الوقاية من الأمراض",
          ],
          color: "bg-blue-500",
        },
        {
          title: "سلامة الطعام",
          description: "معايير صارمة لمناولة الطعام وتحضيره بأمان",
          icon: "🍽️",
          details: [
            "تخزين الطعام وتحضيره بأمان",
            "الوعي وإدارة الحساسية الغذائية",
            "مناطق مطبخ وتناول طعام نظيفة",
          ],
          color: "bg-amber-500",
        },
        {
          title: "إدارة الأمراض",
          description: "بروتوكولات واضحة للتعامل مع الأمراض ومنع انتشارها",
          icon: "🏥",
          details: [
            "سياسات التعامل مع الطفل المريض",
            "التواصل مع أولياء الأمور",
            "إرشادات العودة للرعاية",
          ],
          color: "bg-purple-500",
        },
      ],
      spaces: {
        title: "مساحات تعليمية مجهزة",
        subtitle:
          "تم تصميم مساحاتنا بعناية لدعم التعلم والتطور والسلامة للأطفال من جميع الأعمار.",
      },
      educationalSpaces: [
        {
          title: "تصميم مناسب للعمر",
          description: "مساحات مصممة خصيصًا لمراحل النمو المختلفة",
          icon: "🎯",
          color: "bg-amber-500",
          features: [
            "مناطق لعب آمنة لكل فئة عمرية",
            "أثاث ومعدات مناسبة",
            "مواد وموارد يسهل الوصول إليها",
          ],
        },
        {
          title: "مناطق التعلم",
          description: "مناطق مخصصة لأنواع مختلفة من أنشطة التعلم",
          icon: "📚",
          color: "bg-blue-500",
          features: [
            "أركان قراءة هادئة",
            "محطات فنون إبداعية",
            "مناطق لعب نشطة",
            "مساحات استكشاف حسية",
          ],
        },
        {
          title: "المساحات الخارجية",
          description: "بيئات خارجية آمنة ومحفزة للعب والتعلم",
          icon: "🌳",
          color: "bg-green-500",
          features: [
            "معدات لعب مناسبة للعمر",
            "عناصر لعب طبيعية",
            "مناطق مظللة وحماية من الطقس",
            "سياج آمن وإشراف مستمر",
          ],
        },
        {
          title: "دمج التكنولوجيا",
          description: "استخدام مدروس للتكنولوجيا لتعزيز التعلم",
          icon: "💻",
          color: "bg-purple-500",
          features: [
            "تطبيقات وبرامج تعليمية",
            "أدوات التوثيق الرقمية",
            "منصات التواصل مع أولياء الأمور",
            "إرشادات وقت الشاشة",
          ],
        },
      ],
      atmosphere: {
        title: "الهدوء والنظام في الأجواء",
        subtitle:
          "خلق بيئة هادئة ومنظمة تعزز التعلم والرفاهية والسلوك الإيجابي.",
        calmTitle: "خلق الهدوء",
        calmPoints: [
          "إضاءة ناعمة ومواد طبيعية",
          "مناطق هادئة للراحة والتأمل",
          "انتقالات لطيفة بين الأنشطة",
          "تعزيز السلوك الإيجابي",
        ],
        orderTitle: "الحفاظ على النظام",
        orderPoints: [
          "جداول واضحة وتوقعات منظمة",
          "مساحات لعب وتخزين منظمة",
          "قواعد وحدود ثابتة",
          "نمذجة الموظفين للسلوك الهادئ",
        ],
      },
      atmosphereElements: [
        {
          title: "بيئة هادئة",
          description: "خلق أجواء سلمية وخالية من التوتر للأطفال",
          icon: "😌",
          color: "bg-blue-500",
        },
        {
          title: "جداول منظمة",
          description: "جداول متوقعة توفر الأمان والاستقرار",
          icon: "📅",
          color: "bg-green-500",
        },
        {
          title: "تفاعلات إيجابية",
          description: "علاقات دافئة وداعمة بين الموظفين والأطفال",
          icon: "🤗",
          color: "bg-amber-500",
        },
        {
          title: "مساحات منظمة",
          description: "بيئات منظمة جيدًا تعزز التركيز والهدوء",
          icon: "🗂️",
          color: "bg-purple-500",
        },
      ],
      cta: {
        title: "اختبر بيئتنا الآمنة",
        subtitle:
          "شاهد بنفسك كيف نحافظ على أعلى معايير السلامة والنظافة والأجواء. احجز زيارة لجولة في مرافقنا والتعرف على فريقنا.",
        button: "تعرف على المزيد حول معايير السلامة لدينا",
      },
    },
    parentCollaboration: {
      hero: {
        title: "التعاون مع الوالدين والثقة",
        subtitle: "بناء شراكات قوية من أجل تنمية طفلك",
      },
      intro: {
        title: "رؤيتنا للشراكة",
        text1:
          "في Mini Avonturiers، نرى الوالدين كمربين أول وأهم في حياة الطفل. نحن ندرك أن الوالدين يعرفون أطفالهم أكثر من أي شخص آخر.",
        text2:
          "لذلك، نسعى إلى تعاون مفتوح وشفاف ومتساوٍ، قائم على الثقة المتبادلة والاحترام والحوار. بالنسبة لنا، الوالدين شركاء في تنمية الطفل.",
        supportTitle: "لماذا الشراكة مهمة",
        supportText:
          "التعاون الفعّال بين المربين وأولياء الأمور لا يعزز فقط شعور الطفل بالأمان، بل يضمن أيضًا تنسيقًا أفضل واستمرارية في التربية والإرشاد.",
      },
      pillars: {
        title: "رؤيتنا: تربية الأطفال مسؤولية مشتركة",
        subtitle:
          "يتماشى نهجنا مع مبادئ الشراكة التربوية، كما هو موضح في الأطر التربوية لـ NJi وقانون رعاية الطفل.",
      },
      partnershipPillars: [
        {
          title: "المساواة",
          description: "لكل من الوالدين والمتخصصين خبرتهم الخاصة.",
          icon: "🤝",
          color: "bg-amber-500",
        },
        {
          title: "الثقة المتبادلة",
          description: "الموقف المنفتح هو أساس التعاون البنّاء.",
          icon: "💝",
          color: "bg-rose-500",
        },
        {
          title: "التواصل الشفاف",
          description: "واضح، في الوقت المناسب وبأسلوب مفهوم.",
          icon: "💬",
          color: "bg-blue-500",
        },
        {
          title: "المشاركة والانخراط",
          description: "للآباء تأثير، يقدمون آراءً ويشاركون بنشاط.",
          icon: "👥",
          color: "bg-green-500",
        },
      ],
      practice: {
        title: "تصميم الممارسة",
        subtitle: "أربعة مجالات رئيسية نطبق فيها رؤيتنا للشراكة",
        featuresLabel: "يتضمن هذا:",
      },
      practiceAreas: [
        {
          title: "النقل اليومي ونظام iPad",
          subtitle: "تسليم دافئ وشفافية رقمية",
          icon: "📱",
          description:
            "نحرص يوميًا على تسليم دافئ ومرحّب عند توصيل الأطفال أو اصطحابهم. يتم إبلاغ الوالدين بجدول اليوم، الوجبات، القيلولة، الظروف الخاصة، وسلوك طفلهم.",
          features: [
            "استلام الصور والتحديثات حول الأنشطة",
            "رؤية أوقات النوم والطعام",
            "إمكانية تبادل الرسائل مع المربي",
            "الوصول إلى الجدول والملاحظات والتفاصيل",
          ],
        },
        {
          title: "محادثات الوالد-المُرشد",
          subtitle: "تتبع شخصي للتنمية",
          icon: "👨‍👩‍👧‍👦",
          description:
            "لكل طفل مُرشد مخصص: متخصص مسؤول عن تنمية الطفل ورفاهيته. تُعقد اجتماعات المُرشد مرتين على الأقل سنويًا (أو أكثر عند الحاجة).",
          features: [
            "مناقشة التطور على المستوى الاجتماعي والحركي والمعرفي والعاطفي",
            "مشاركة الملاحظات",
            "مساحة للأسئلة أو المخاوف أو رغبات الوالدين",
            "مناقشة الإجراءات اللاحقة عند الحاجة",
          ],
        },
        {
          title: "التواصل السهل",
          subtitle: "سياسة الباب المفتوح ودعم متاح",
          icon: "🚪",
          description:
            "لدينا سياسة الباب المفتوح. الآباء دائمًا مرحب بهم لطرح الأسئلة أو المحادثات القصيرة أو لمشاركة أمر ما.",
          features: [
            "سياسة الباب المفتوح للوالدين",
            "طاقم مدرّب على التواصل السهل",
            "الاستماع الفعّال ونهج يركز على الحلول",
            "أشخاص اتصال مخصصون في الموقع",
          ],
        },
        {
          title: "اجتماعات الوالدين والعصف الذهني",
          subtitle: "مشاركة منتظمة وتعلم مشترك",
          icon: "📅",
          description:
            "ننظم اجتماعات أولياء الأمور مرتين على الأقل في السنة. يمكن أن تكون هذه الاجتماعات تعليمية أو اجتماعية.",
          features: [
            "أمسيات موضوعية حول التربية والتنمية",
            "أمسيات مشاهدة في المجموعة مع شرح",
            "جلسات عصف ذهني حول السياسات والأساليب",
            "فعاليات اجتماعية لبناء المجتمع",
          ],
        },
      ],
      feedback: {
        title: "ثقافة التغذية الراجعة",
        subtitle:
          "نحن في تطور مستمر ونرحب بالتغذية الراجعة. يمكن للوالدين مشاركة الاقتراحات أو المخاوف أو المجاملات بطرق مختلفة:",
        note: "لا تُعتبر التغذية الراجعة نقدًا، بل مدخلات قيّمة لتحسين الجودة باستمرار.",
      },
      feedbackMethods: [
        {
          title: "محادثة شخصية",
          description: "تواصل مباشر مع المربي",
          icon: "💭",
        },
        {
          title: "بوابة الوالدين الرقمية",
          description: "تقديم الملاحظات عبر نظامنا الرقمي",
          icon: "💻",
        },
        {
          title: "لحظات التقييم",
          description: "تغذية راجعة منظمة خلال المحادثات",
          icon: "📊",
        },
        {
          title: "استبيانات الوالدين",
          description: "استطلاعات رضا واستبيانات منتظمة",
          icon: "📝",
        },
      ],
      committee: {
        title: "دور لجنة أولياء الأمور",
        subtitle:
          "تلعب لجنة أولياء الأمور لدينا دورًا نشطًا داخل مؤسسة رعاية الطفل",
        activeRole: "دور استشاري نشط",
        text1:
          "تعمل اللجنة كصوت للوالدين ولوحة استشارية، نسعى معها معًا للجودة والمشاركة.",
        text2:
          "يقدمون المشورة بشأن المجالات الرئيسية التي تؤثر مباشرة على تجربة طفلك واحتياجات أسرتك.",
        areasTitle: "مجالات الاستشارة:",
      },
      parentCommitteeAreas: [
        "السياسة التربوية",
        "الغذاء، السلامة والصحة",
        "ساعات العمل والأسعار",
        "إجراءات الشكاوى وتواصل الوالدين",
      ],
      cta: {
        title: "هل أنت مستعد لبدء شراكتنا؟",
        subtitle:
          "انضم إلى مجتمعنا من الآباء والمربين الذين يعملون معًا من أجل التطور الأمثل لطفلك. حدد موعدًا للتعرف على نهجنا التعاوني.",
        button: "حدد موعدًا",
      },
    },

    // ARABIC
    receivingPolicy: {
      hero: {
        title: "السياسة: استقبال وتأقلم الأطفال الجدد",
        subtitle: "تأسيس قاعدة دافئة وآمنة لرحلة كل طفل",
      },
      intro: {
        title: "ترحيب يركز على الطفل",
        text1:
          "تجارب الطفل الأولى في الحضانة لها تأثير كبير على شعوره بالأمان والثقة والارتباط. لذلك في Mini Avonturiers نولي اهتمامًا ورعاية إضافية عند استقبال الأطفال الجدد ومساعدتهم على التأقلم.",
        text2:
          "نحن نؤمن أن كل طفل فريد ويجب أن يكون قادرًا على التكيف مع بيئة جديدة بطريقته الخاصة وبوتيرته الخاصة. لهذا السبب نستخدم نهجًا فرديًا، مصممًا وفقًا لاحتياجات الطفل والوالدين.",
        supportTitle: "لماذا هذا مهم",
        supportText:
          "البداية الجيدة تخلق شعورًا بالهدوء لكل من الطفل والوالدين، وتضع الأساس لعلاقة إيجابية مع طاقم الرعاية والمجموعة.",
      },
      warmIntro: {
        title: "مقدمة دافئة: من أول تواصل",
        subtitle:
          "يبدأ التأقلم حتى قبل يوم الرعاية الأول. نبني الثقة مع الوالدين ونتأكد من أن الطفل يمكنه التعرف على البيئة الجديدة في جو هادئ ومحب.",
        items: [
          {
            title: "التعريف ومقابلة الاستقبال",
            description:
              "قبل يوم الرعاية الأول، يتم عقد مقابلة استقبال مع الوالدين.",
            features: [
              "يتحدث الوالدان عن الطفل: الشخصية، نمط النوم، التغذية",
              "الألعاب المفضلة، التفضيلات، اللغة، الحساسية، شخصيات التعلق",
              "الخصائص الفريدة والمميزات الخاصة",
              "نناقش التوقعات المتبادلة وطرق عملنا",
            ],
            icon: "👨‍👩‍👧‍👦",
          },
          {
            title: "جولة تعريفية",
            description: "خلال الجولة نعرض عليكم الغرف والمجموعة والموظفين.",
            features: [
              "عرض الغرف والمجموعة والموظفين",
              "يمكن للوالدين والطفل تجربة الأجواء",
              "التعرف على الموظفين التربويين والمُعلمة الرئيسية",
              "يجعل المكان أكثر ألفة",
            ],
            icon: "🏠",
          },
        ],
      },
      phases: {
        title: "التأقلم على مراحل: الطفل في المركز",
        subtitle:
          "يحصل الطفل الجديد لدينا على فرصة للتأقلم تدريجيًا. اعتمادًا على العمر والشخصية والوضع الأسري والخبرات السابقة، نضع خطة تأقلم.",
        items: [
          {
            day: "اليوم 1",
            duration: "قصير",
            description: "مع الوالدين، بحد أقصى ساعة واحدة في المجموعة",
            activities: "المراقبة بهدوء والتعرف",
            icon: "👀",
          },
          {
            day: "اليوم 2",
            duration: "أطول",
            description: "يبقى الوالد لفترة قصيرة، الطفل يبقى وحده",
            activities: "حوالي 1.5–2 ساعة بدون الوالد",
            icon: "🤗",
          },
          {
            day: "اليوم 3",
            duration: "نصف يوم",
            description: "يبقى الطفل وحده، مع نشاط",
            activities: "قد يشمل وقت الطعام أو أنشطة منظمة",
            icon: "🎨",
          },
          {
            day: "اليوم 4+",
            duration: "يوم كامل",
            description: "بناء نحو يوم كامل",
            activities: "حسب احتياجات الطفل واستجابته",
            icon: "🌟",
          },
        ],
        note: "هذه المراحل ليست إلزامية أو صارمة. بعض الأطفال يحتاجون إلى وقت أطول، والبعض الآخر يشعر بالراحة بسرعة. ننسق هذا بمرونة مع الوالدين ونركز على الهدوء والأمان والرفاهية العاطفية.",
      },
      mentor: {
        title: "دور المربية: القرب والثقة",
        subtitle:
          "منذ البداية، يُعين لكل طفل مُربية دائمة. توفر هذه المربية حضورًا مخصصًا يتيح للطفل فرصة تكوين علاقة آمنة مع شخص بالغ.",
        roles: [
          {
            title: "نقطة الاتصال الأولى",
            description:
              "الموظفة التي تراقب بنشاط، وتُسلي، وتوجه، وتغرس الثقة في الطفل.",
          },
          {
            title: "بناء علاقة آمنة",
            description: "مسؤولة عن بناء علاقة آمنة مع الطفل.",
          },
          {
            title: "مُراقبة العملية",
            description:
              "الشخص الذي يراقب عملية التأقلم بعناية ويتواصل مع الفريق والوالدين.",
          },
          {
            title: "مزيد من القرب",
            description: "تُقدم للطفل قربًا إضافيًا خلال فترة التأقلم.",
          },
        ],
        activitiesTitle: "تشمل أنشطة المربية:",
        activities: [
          "البقاء بالقرب أثناء اللعب",
          "التسلية في الحضن",
          "القيام بالأنشطة معًا",
          "تكرار الروتين المألوف",
          "استخدام الأشياء المألوفة",
        ],
        note: "هذا الحضور المخصص يمنح الطفل فرصة لتكوين ارتباط مع شخص بالغ، وهو أمر أساسي للتطور وفقًا لنظرية التعلق لبولبي.",
      },
      parents: {
        title: "الوالدان كشركاء في فترة التأقلم",
        subtitle:
          "نحن نؤمن أن مشاركة الوالدين ضرورية للانتقال. نشجع الوالدين على الثقة، مع أخذ مشاعرهم الخاصة على محمل الجد.",
        items: [
          {
            title: "مراقبة نشطة",
            description:
              "يُدعى الوالدان للمراقبة والمشاركة بملاحظاتهم خلال لحظات التأقلم.",
          },
          {
            title: "تواصل سهل",
            description:
              "اتصال سهل عبر نظام iPad أو لفظيًا عند الاستقبال والتسليم.",
          },
          {
            title: "مناقشة مفتوحة",
            description:
              "نناقش بشكل مفتوح كيف حال الطفل، والإشارات التي نلتقطها وكيفية التعديل إذا لزم الأمر.",
          },
          {
            title: "متاح دائمًا",
            description:
              "المربية دائمًا متاحة للاستشارة، حتى خارج الاجتماعات المخططة.",
          },
        ],
        note: "عندما يشعر الوالدان بالأمان، يشعر الأطفال أيضًا بالأمان.",
      },
      aftercare: {
        title: "الرعاية اللاحقة والتقييم",
        subtitle:
          "الأسابيع الأولى حاسمة. يركز كل شيء على ضمان شعور الطفل بالأمان والارتباط والراحة في Mini Avonturiers.",
        items: [
          {
            title: "مراقبة مكثفة",
            description:
              "تواصل المربية متابعة الطفل عن كثب خلال الأسابيع الأولى.",
          },
          {
            title: "تقييمات غير رسمية",
            description:
              "لحظات تقييم قصيرة وغير رسمية (لفظيًا أو عبر التطبيق).",
          },
          {
            title: "تقييم بعد 6 أسابيع",
            description:
              "بعد حوالي 6 أسابيع، يتم إجراء تقييم قصير واجتماع مع الوالدين.",
          },
          {
            title: "دعم إضافي",
            description: "عند الحاجة، نوفر دعمًا إضافيًا أو نمدد فترة التأقلم.",
          },
        ],
        note: "بعد حوالي 6 أسابيع، يُعقد تقييم قصير واجتماع مع الوالدين لمناقشة فترة التأقلم والتطور والرفاهية.",
      },
      cta: {
        title: "هل أنتم مستعدون لبدء رحلة طفلكم؟",
        subtitle:
          "اختبروا نهجنا الدافئ والمتمركز حول الطفل في استقبال العائلات الجديدة. احجزوا مقابلة استقبال لبدء رحلة التأقلم الخاصة بطفلكم.",
        button: "تعرفوا أكثر على عملية التأقلم",
      },
    },

    // ARABIC (AR)
    ipadSystem: {
      hero: {
        title: "نظام الآيباد والتواصل مع الأهل",
        subtitle: "ابقَ على اتصال بمغامرات طفلك اليومية",
      },
      intro: {
        title: "اتصال رقمي لراحة البال",
        text1:
          "في ميني أفونتورييرز، نُقدّر التواصل المفتوح والشفاف والمُشترك مع الأهل. نحن نفهم أن ترك الطفل ليس بالأمر السهل دائمًا على الأهل.",
        text2:
          "لذلك نستخدم نظام آيباد حديث وآمن وسهل الاستخدام مع تطبيق للأهل، يبقيهم على اطلاع بحالة طفلهم في الوقت الفعلي — حتى عن بُعد.",
        supportTitle: "دعم رقمي، وليس بديلاً",
        supportText:
          "هذا الدعم الرقمي لا يحل محل التواصل الشخصي، بل هو إضافة قيّمة تعزز الثقة والمشاركة والتنسيق بين المنزل والحضانة.",
      },
      appFeatures: {
        title: "ماذا يمكن أن يتوقع الأهل من التطبيق؟",
        subtitle:
          "يوفر تطبيق الأهل وصولاً يوميًا إلى معلومات شاملة حول يوم طفلهم.",
        whatYouSee: "ستشاهدون:",
        items: [
          {
            title: "الصور والانطباعات",
            description:
              "يتلقى الأهل بانتظام صورًا لطفلهم أثناء اللعب أو القيام بالأعمال اليدوية أو قضاء الوقت في الخارج أو المشاركة في الأنشطة.",
            icon: "📸",
            color: "bg-pink-500",
            benefits: [
              "رؤية بصرية لحياة المجموعة",
              "يعزز الشعور بالمشاركة",
              "يوثق اللحظات الخاصة",
              "يعرض الأنشطة اليومية والمواضيع",
            ],
          },
          {
            title: "التقارير اليومية",
            description:
              "في نهاية اليوم، يكتب المربون تقريرًا قصيرًا يصف الأنشطة والأجواء واللحظات المميزة.",
            icon: "📝",
            color: "bg-indigo-500",
            benefits: [
              "ما فعله الأطفال (أنشطة، موضوع)",
              "الأجواء داخل المجموعة",
              "لحظات أو ملاحظات مميزة",
              "يساعد الأهل على التحدث مع طفلهم عن يومه",
            ],
          },
          {
            title: "أوقات الوجبات",
            description:
              "إطلاع الأهل على ما أكله الطفل وكمية الطعام، بما في ذلك الظروف الخاصة.",
            icon: "🍽️",
            color: "bg-green-500",
            benefits: [
              "ما أكله الطفل وكمية الطعام",
              "أي ظروف خاصة (فقدان شهية، حساسية)",
              "تفضيلات غذائية وقيود",
              "متابعة التغذية والتنسيق",
            ],
          },
          {
            title: "جداول النوم",
            description:
              "يمكن للأهل معرفة متى نام طفلهم، ومدة النوم وكيف استيقظ.",
            icon: "😴",
            color: "bg-blue-400",
            benefits: [
              "متى نام الطفل",
              "مدة النوم",
              "كيف استيقظ",
              "مفيد لتنسيق الروتين المنزلي",
            ],
          },
          {
            title: "وظيفة الرسائل",
            description:
              "يمكن للأهل إرسال رسائل إلى المجموعة والعكس صحيح للأسئلة أو التحديثات السريعة.",
            icon: "💬",
            color: "bg-yellow-500",
            benefits: [
              "تواصل مباشر مع المربين",
              "أسئلة وتحديثات سريعة",
              "ليس بديلاً عن الحوار المباشر",
              "إضافة مفيدة للأمور الصغيرة",
            ],
          },
        ],
      },
      whyChoose: {
        title: "لماذا اخترنا هذا النظام؟",
        subtitle: "تم تصميم نظامنا الرقمي وفق أربعة مبادئ رئيسية",
        items: [
          {
            title: "الشفافية والثقة",
            description:
              "لن يضطر الأهل للتخمين حول يوم طفلهم. يتم إعلامهم باستمرار مما يعزز الثقة وراحة البال أثناء ساعات العمل.",
            icon: "🛡️",
            color: "bg-amber-500",
          },
          {
            title: "الإشعارات والتنسيق الفوري",
            description:
              "يمكننا إعلام الأهل فورًا بأي تغييرات في السلوك أو الشهية أو المزاج. كما يمكن للأهل مشاركة المعلومات الهامة عبر التطبيق.",
            icon: "⚡",
            color: "bg-blue-500",
          },
          {
            title: "تعزيز المشاركة",
            description:
              "يساعد التطبيق الأهل على البقاء مشاركين في نمو طفلهم من خلال المتابعة والملاحظة والتفاعل مع أنشطة اليوم.",
            icon: "🤝",
            color: "bg-green-500",
          },
          {
            title: "التوثيق وإمكانية الرجوع",
            description:
              "جميع التقارير والملاحظات والرسائل متاحة للمراجعة. هذا يضمن الاستمرارية ويوفر مدخلات قيّمة للاجتماعات.",
            icon: "📊",
            color: "bg-purple-500",
          },
        ],
      },
      privacy: {
        title: "الخصوصية والأمان",
        subtitle:
          "نعمل فقط مع أنظمة تفي بأعلى معايير الخصوصية والأمان وحماية البيانات (متوافقة مع GDPR).",
        items: [
          {
            title: "متوافق مع GDPR",
            description:
              "نستخدم أنظمة تفي بأعلى معايير الخصوصية والأمان وحماية البيانات.",
            icon: "🔒",
            color: "bg-red-500",
          },
          {
            title: "وصول آمن",
            description: "فقط والدا الطفل المعني يمكنهما الوصول إلى المعلومات.",
            icon: "👤",
            color: "bg-blue-600",
          },
          {
            title: "عدم المشاركة علنًا",
            description:
              "لا تتم مشاركة الصور والبيانات علنًا ويتم الاحتفاظ بها فقط للفترة القانونية المطلوبة.",
            icon: "🚫",
            color: "bg-gray-700",
          },
          {
            title: "طاقم مدرب",
            description:
              "يتم تدريب موظفينا على إعداد التقارير بعناية والتواصل الرقمي باحترام وحياد واحترافية.",
            icon: "👨‍🏫",
            color: "bg-teal-500",
          },
        ],
        note: "يمكن فقط لوالدي الطفل الوصول للمعلومات. لا تتم مشاركة الصور والبيانات علنًا ويتم الاحتفاظ بها فقط للفترة القانونية المطلوبة.",
      },
      technical: {
        title: "التدريب والمعايير المهنية",
        text1:
          "يتم تدريب موظفينا على إعداد التقارير بعناية والتواصل الرقمي باحترام وحياد واحترافية.",
        text2:
          "جميع التقارير والملاحظات والرسائل متاحة للمراجعة، مما يضمن الاستمرارية ويوفر مدخلات قيمة للاجتماعات والتقييمات.",
        benefitsTitle: "الفوائد الرئيسية:",
        benefits: [
          "تحديثات فورية وشفافية",
          "منصة آمنة متوافقة مع GDPR",
          "تدريب احترافي للموظفين",
          "توثيق شامل",
        ],
      },
      cta: {
        title: "هل أنت جاهز للبقاء على اتصال؟",
        subtitle:
          "اختبر راحة البال التي تأتي مع التحديثات الفورية حول مغامرات طفلك اليومية. تعرف أكثر على نظام التواصل الرقمي لدينا.",
        button: "اعرف المزيد عن التطبيق",
      },
    },

    // ar.ts
    nutrition: {
      hero: {
        title: "التغذية وفقًا لدائرة الخمس",
        subtitle: "تغذية صحية ومتنوعة وواعية للعقول والأجسام النامية",
      },
      intro: {
        title: "أكثر من مجرد طعام",
        text1:
          "في ميني أفونتوريرز، نُقدّر التغذية الصحية والمتنوعة والواعية. فالتغذية ليست ضرورية فقط للنمو الجسدي للأطفال الصغار، بل أيضًا لتركيزهم ومزاجهم وتوازن طاقاتهم وتطوير عادات غذائية صحية للمستقبل.",
        text2:
          "لهذا السبب، نعتمد في سياستنا الغذائية على إرشادات مركز التغذية الهولندي ونلتزم بمبادئ دائرة الخمس.",
        supportTitle: "نهجنا",
        supportText:
          "نسعى إلى نظام غذائي متوازن، مع التركيز على الخضروات والفواكه والحبوب الكاملة والبروتينات ومنتجات الألبان والدهون الصحية والقدر الكافي من السوائل.",
      },
      wheelOfFive: {
        title: "دائرة الخمس",
        subtitle: "أساسنا الغذائي مبني على إرشادات مركز التغذية الهولندي",
        items: [
          {
            category: "الخضروات والفواكه",
            description: "خضروات وفواكه طازجة وملونة",
            icon: "🥬",
            color: "bg-green-500",
          },
          {
            category: "الحبوب الكاملة",
            description: "خبز القمح الكامل، المعكرونة، والأرز البني",
            icon: "🌾",
            color: "bg-amber-500",
          },
          {
            category: "البروتينات",
            description: "البقوليات، البيض، السمك، الدجاج والبدائل",
            icon: "🥚",
            color: "bg-red-500",
          },
          {
            category: "منتجات الألبان",
            description: "الحليب، الجبن، ومنتجات الألبان",
            icon: "🥛",
            color: "bg-blue-500",
          },
          {
            category: "الدهون الصحية",
            description: "المكسرات، البذور، والزيوت الصحية",
            icon: "🥑",
            color: "bg-purple-500",
          },
        ],
      },
      mealtime: {
        title: "الطعام أكثر من تغذية: لحظة اجتماعية",
        subtitle:
          "في ميني أفونتوريرز، الوجبات ليست مجرد تناول طعام: إنها أيضًا فرص تعليمية واجتماعية وعاطفية.",
        note: "يأكل طاقمنا التربوي مع الأطفال على الطاولة ويكونون قدوة. يشجعون الأطفال على تجربة نكهات جديدة بطريقة إيجابية وغير إلزامية، دون ضغط أو مكافأة.",
        items: [
          {
            title: "المهارات الاجتماعية",
            description: "الأكل معًا، المشاركة، وانتظار الآخرين",
            icon: "🤝",
          },
          {
            title: "آداب المائدة",
            description: 'تعلم قول "من فضلك" و"شكرًا"',
            icon: "🍽️",
          },
          {
            title: "الاستقلالية",
            description: "تعلم الاختيار أو الخدمة بنفسك",
            icon: "👶",
          },
          {
            title: "التثقيف الغذائي",
            description: "التحدث عن الطعام: الألوان، النكهات، والأصول",
            icon: "🌍",
          },
        ],
      },
      baby: {
        title: "الرضع: تغذية حسب الاحتياجات",
        subtitle:
          "بالنسبة للرضع، تُخصص التغذية بالكامل حسب احتياجاتهم الفردية، وبالتشاور الوثيق مع الوالدين.",
        note: "ندعم الآباء بنشاط خلال الانتقال إلى الطعام الصلب، مع الاهتمام بالحساسية الغذائية والتفضيلات والمتطلبات الغذائية.",
        items: [
          {
            title: "اتباع الإيقاع",
            description: "اتباع إيقاع الطفل الخاص (لا مواعيد طعام ثابتة)",
            icon: "⏰",
          },
          {
            title: "التوجيه بمحبة",
            description:
              "توجيه هادئ ومحب أثناء الرضاعة (على الحضن، مع التواصل البصري)",
            icon: "💝",
          },
          {
            title: "التغذية الآمنة",
            description: "الإطعام في وضعية جلوس آمنة، مع مراعاة إشارات الشبع",
            icon: "🛡️",
          },
          {
            title: "التواصل الدافئ",
            description:
              "التواصل الدافئ حول لحظات الانتقال (مثل أول وجبة صلبة)",
            icon: "💬",
          },
        ],
      },
      toddler: {
        title: "الأطفال الصغار ومرحلة ما قبل المدرسة: صحي ومتوازن ومستقل",
        subtitle:
          "بالنسبة للأطفال الأكبر سنًا (1.5–4 سنوات)، نلتزم بجدول يومي مع مواعيد طعام ثابتة.",
        hotMealsTitle: "تشمل الوجبات الساخنة:",
        items: [
          {
            title: "أوقات الفاكهة",
            description: "أنواع متعددة يوميًا، مقطعة ومقدمة بشكل جذاب",
            icon: "🍎",
            color: "bg-red-500",
          },
          {
            title: "خيارات الحبوب الكاملة",
            description:
              "ساندويتشات القمح الكامل أو وجبة غداء ساخنة (حسب الوقت)",
            icon: "🥪",
            color: "bg-amber-500",
          },
          {
            title: "وجبات خفيفة صحية",
            description:
              "أعواد الخبز، الخيار، الطماطم الكرزية، البسكويت الكامل",
            icon: "🥒",
            color: "bg-green-500",
          },
          {
            title: "مشروبات صحية",
            description:
              "الماء والشاي الفاتر بشكل أساسي، العصير فقط في المناسبات الخاصة",
            icon: "💧",
            color: "bg-blue-500",
          },
        ],
        hotMealFeatures: [
          "تنوع في الخضروات (على الأقل نوعان في كل وجبة)",
          "معكرونة كاملة، أرز بني أو بطاطس",
          "مصدر بروتين (بقوليات، بيض، سمك، دجاج أو نباتي)",
          "بدون سكريات مضافة أو منكهات صناعية",
          "كمية ملح قليلة (بحد أقصى 1 غرام سنويًا للأطفال الصغار)",
          "كمية كافية من الألياف والبروتينات والدهون الصحية",
        ],
      },
      independence: {
        title: "افعلها بنفسك، جربها بنفسك",
        subtitle:
          "نشجع الأطفال – بطريقة مناسبة للعمر – على الأكل والاختيار والتذوق والتنظيف بأنفسهم.",
        note: "نجعل من الأكل لحظة للاهتمام والراحة والارتباط.",
        items: [
          {
            title: "الإمساك والسكب",
            description: "إمساك الكوب وصبّه بنفسه (مع المساعدة)",
            icon: "🥤",
          },
          {
            title: "اختيار الفاكهة",
            description: "اختيار قطعة فاكهة من الطبق بنفسه",
            icon: "🍓",
          },
          {
            title: "التنظيف",
            description: "تعلم ترتيب أطباقك الخاصة",
            icon: "🧹",
          },
        ],
      },
      cultural: {
        title: "حساسية ثقافية وشمولية",
        subtitle:
          "ندرك أن العادات الغذائية والطقوس قد تختلف من أسرة إلى أخرى ومن ثقافة إلى أخرى.",
        items: [
          {
            title: "تفضيلات دينية",
            description: "احترام التفضيلات النباتية أو الغذائية الدينية",
            icon: "🕊️",
          },
          {
            title: "طقوس ثقافية",
            description:
              "الاهتمام بالمواعيد أو الطقوس الرمزية للوجبات في المنزل",
            icon: "🏠",
          },
          {
            title: "دون ضغط",
            description:
              "لا يوجد إلزام عام على الأكل: يمكن للأطفال الإشارة عند الشعور بالاكتفاء",
            icon: "🤗",
          },
        ],
      },
      educational: {
        title: "القيمة التعليمية للتغذية",
        subtitle: "التغذية هي أيضًا وسيلة لجعل الأطفال يدركون مصدر الطعام.",
        note: "بهذا نشجع على الفهم والمشاركة والسلوك الصحي.",
        items: [
          {
            title: "تحضير الطعام",
            description: "تقطيع الفواكه أو الخضروات (تحت إشراف)",
            icon: "🔪",
          },
          {
            title: "تعلم الأرقام",
            description: "عد القطع أو تسمية الألوان",
            icon: "🔢",
          },
          {
            title: "خيارات صحية",
            description: 'محادثات عن الخيارات الصحية: "ماذا تحب؟ ما هو الصحي؟"',
            icon: "💭",
          },
          {
            title: "أنشطة الحديقة",
            description: "أحيانًا الزرع والحصاد في حديقة صغيرة",
            icon: "🌱",
          },
        ],
      },
      cta: {
        title: "هل أنت مستعد لدعم نمو طفلك؟",
        subtitle:
          "اختبر نهجنا الشامل للتغذية الصحية والتعليم أثناء الوجبات. حدد موعدًا لزيارة برنامجنا الغذائي عمليًا.",
        button: "اعرف المزيد عن تغذيتنا",
      },
    },

    horizontalGroups: {
      hero: {
        title: "المجموعات الأفقية",
        subtitle: "رعاية مناسبة للعمر من أجل نمو أمثل",
        ageRange: "(0–4 سنوات)",
      },

      intro: {
        title: "نهجنا تجاه الفئات العمرية",
        text1:
          "في ميني أفونتوريرز، اخترنا عمداً العمل مع مجموعات أفقية. هذا يعني أن الأطفال في نفس المرحلة العمرية والتطور يجتمعون معًا في مجموعة واحدة.",
        text2:
          "بدلاً من مجموعة عمودية (حيث يتم دمج الأطفال من عمر 0 إلى 4 سنوات)، نقسم الأطفال إلى ثلاث مراحل لضمان أن كل طفل يتطور بشكل أفضل عندما تكون البيئة مصممة خصيصًا لاحتياجاته ومرحلته.",
        threeGroups: "مجموعاتنا الثلاث",
      },

      groupsDivisionTitle: "تقسيم مجموعاتنا",
      groupsDivisionSubtitle:
        "تم تصميم كل مجموعة بعناية لتلبية الاحتياجات الخاصة",
      whatChildrenReceive: "ما يحصل عليه الأطفال:",
      ourApproach: "نهجنا:",

      groups: {
        baby: {
          title: "مجموعة الرُضّع",
          subtitle: "± 0–1.5 سنة",
          description:
            "الثقة والرعاية والتعلق هي الأساس. نحن نتبع الإيقاع الطبيعي لكل طفل.",
          features: [
            "اهتمام فردي وهدوء",
            "الكثير من التواصل الجسدي والتفاعل الحساس",
            "مساحة حرة للحركة (الاستلقاء، التدحرج، الزحف)",
            "مواد حسية وموسيقى مهدئة",
          ],
          approach:
            "طاقمنا التربوي مدرب على الاستجابة الحساسة ورؤية إيمي بيكلر، حيث يكون احترام الطفل والحركة المستقلة هما الأساس.",
        },
        toddler: {
          title: "مجموعة الأطفال الصغار",
          subtitle: "± 1.5–2.5 سنة",
          description:
            "الاكتشاف وتطوير الاستقلالية. الأطفال يريدون أن يفعلوا كل شيء بأنفسهم.",
          features: [
            "تعلم التعبير عن المشاعر وتنظيمها",
            "تطوير المهارات الحركية (التسلق، الدفع، البناء)",
            "البدء باستخدام اللغة والتواصل البسيط",
            "ممارسة مفاهيم مثل الانتظار والمشاركة و قول ’لا‘",
          ],
          approach:
            "تركز توجيهاتنا على التأكيد، التوجيه، ووضع الحدود. نقدم إرشادات واضحة، نساعد في حل النزاعات، ونوفر فرصًا كثيرة للتكرار والتعلم المستقل.",
        },
        preschool: {
          title: "مجموعة ما قبل المدرسة",
          subtitle: "± 2.5–4 سنوات",
          description:
            "انفجار لغوي، تطور اجتماعي، ولعب رمزي. الأطفال يبحثون عن التحدي والتفاعل.",
          features: [
            "اللعب معًا، تعلم المشاركة، التشاور ولعب الأدوار",
            "اكتشاف علاقات السبب والنتيجة والمنطق",
            "تقديم أنشطة ضمن موضوعات",
            "الاستعداد للانتقال إلى المدرسة الابتدائية",
          ],
          approach:
            "نستخدم التفاعل الغني لغويًا، وقت الحلقة، التعليم المنظم للعب، ولعب الأدوار. يشجع طاقمنا الاستقلالية، تنمية المفردات، مهارات حل المشكلات، والثقة بالنفس.",
        },
      },

      why: {
        title: "لماذا المجموعات الأفقية؟",
        subtitle: "يعتمد نهجنا على مبادئ تربوية راسخة وعلم نفس النمو",
        items: {
          safety: {
            title: "الأمان والتعرف",
            description:
              "الأطفال في نفس المرحلة التطويرية لديهم إيقاعات واحتياجات وقدرات متشابهة. يمكننا تصميم البيئة بالكامل لتناسب تلك المرحلة.",
          },
          targeted: {
            title: "التوجيه الموجّه",
            description:
              "للرُضّع والأطفال الصغار وما قبل المدرسة أهداف تطويرية خاصة بهم. يمكن للمربين تقديم دعم أكثر تحديدًا.",
          },
          natural: {
            title: "التعلم الطبيعي",
            description:
              "وفقًا لجان بياجيه، يحدث التطور المعرفي على مراحل. نحن نُكيّف اللعب والدعم مع هذه العمليات الطبيعية للتعلم.",
          },
        },
      },

      benefits: {
        title: "فوائد نهجنا",
        subtitle: "توفر المجموعات الأفقية فوائد لكل من الأطفال والموظفين",
        children: {
          title: "فوائد للأطفال",
          subtitle: "نمو أمثل في بيئات مناسبة للعمر",
          items: [
            "المزيد من الهدوء وقلة المثيرات بسبب غياب الفروق العمرية الكبيرة",
            "الأنشطة مصممة بشكل أفضل لمستوى التطور",
            "يشعر الأطفال بالكفاءة والثقة بشكل أسرع",
            "مساحة لتطوير الاستقلالية بما يتناسب مع المرحلة",
            "تحسين الملاحظة والتوجيه في السلوك واللعب والتطور العاطفي",
          ],
        },
        staff: {
          title: "فوائد للموظفين",
          subtitle: "خبرة مركزة وتطور مهني",
          items: [
            "التركيز على مرحلة نمو واحدة يؤدي إلى عمق تربوي أكبر",
            "تسهيل تنظيم البرنامج اليومي",
            "جودة أعلى في توجيه اللعب والملاحظة",
            "المزيد من الهدوء والوضوح في ديناميات المجموعة",
            "تحسين التعاون بين الفريق من خلال تقسيم الأدوار بوضوح",
          ],
        },
      },

      transitions: {
        title: "الاتساق والانتقالات",
        subtitle:
          "على الرغم من أن الأطفال في مجموعات أفقية، فإننا لا نفقد استمرارية التطور. يتم التحضير بعناية للانتقال إلى المجموعة التالية:",
        items: [
          "يتم تسليم المعلومات بحرارة بين المربين",
          "يتم إشراك الآباء في الانتقال",
          "يتعود الأطفال تدريجيًا على المجموعة الجديدة",
          "يبقى المربي المرافق مشاركًا طوال فترة الانتقال",
        ],
        conclusion:
          "بهذه الطريقة نضمن أن ينتقل الطفل إلى المرحلة التالية بثقة وبدون توتر.",
      },

      cta: {
        title: "هل أنت مستعد لتتعرف أكثر؟",
        subtitle:
          "اكتشف كيف يمكن لنهجنا المناسب للعمر أن يفيد نمو طفلك. احجز جولة لترى مجموعاتنا الأفقية على أرض الواقع.",
        button: "تعرف أكثر على مجموعاتنا",
      },
    },

    // Brand
    brand: {
      name: "مغامري مينى",
      tagline: "المغامرة تبدأ بالحب والتعلم",
    },

    // Educational Staff page translations
    educationalStaff: {
      title: "دور الكادر التربوي",
      subtitle:
        "قلب رؤيتنا وجودتنا - شركاء فاعلون للوالدين، ودعامات عاطفية، ومدربو نمو",

      sectionTitle: "دور الكادر التربوي",
      introText1:
        "في ميني أفونتوريرز، يمثل كادرنا التربوي جوهر رؤيتنا وجودتنا. إنهم التجسيد الحي لسياستنا التربوية في الممارسة اليومية. موظفونا ليسوا مجرد مشرفين، بل شركاء نشطين للوالدين، دعامات عاطفية، ومدربو نمو.",
      introText2:
        "نؤمن أن جودة التفاعل بين الأطفال والبالغين تحدد جودة الرعاية. لذلك فإن موظفينا حسّاسون، متجاوبون، محترفون، وعاكسون، شغوفون بعملهم ويولون اهتماماً لاحتياجات الأطفال.",

      coreValues: {
        title: "قيمنا الأساسية",
        sensitiveResponsive: {
          title: "موقف حساس ومتجاوب",
          description:
            "موظفونا مدربون على الاستجابة الحساسة، وهي واحدة من الأهداف التربوية الأساسية الأربعة في قانون رعاية الأطفال. إنهم يرون الطفل حقًا، ويدركون الإشارات الدقيقة (مثل التوتر، الحاجة إلى الراحة أو الانتباه)، ويستجيبون بسرعة وبشكل مناسب.",
          practices: [
            "يتم مناداة الأطفال بأسمائهم والترحيب بهم شخصياً",
            "يتواصل الكادر التربوي على مستوى العين، بصوت هادئ ولغة جسد منفتحة",
            "يتم تسمية العواطف والاعتراف بها",
            "لا يتم تجاهل الأطفال أو رفضهم أو مقاطعتهم في تجربتهم",
          ],
        },
        guidingGroupProcesses: {
          title: "توجيه العمليات الجماعية",
          description:
            "بالإضافة إلى الاهتمام الفردي، يلعب العمل الجماعي دورًا مهمًا في التنمية. يوجه الكادر التربوي المجموعة بنشاط ويعزز جوًا من الأمان والتعاون والاحترام.",
          practices: [
            "مساعدة الأطفال على التواصل وحل النزاعات",
            "تعزيز المشاركة والتعاون",
            "تنمية التعاطف والمهارات الاجتماعية",
            "الموازنة بين منح الحرية ووضع الحدود",
          ],
        },
        providingStructure: {
          title: "توفير الهيكل دون صرامة",
          description:
            "الهيكل اليومي الواضح ضروري للسلامة والهدوء. يضمن موظفونا التوقع الواضح، والتحولات السلسة، والإيقاع المنتظم، مع المرونة للتكيف مع احتياجات اللحظة.",
          practices: [
            "ضمان التوقعات الواضحة والتحولات السلسة",
            "توضيح ما يحدث، ولماذا، ومتى",
            "مساعدة الأطفال على التعلم في التخطيط والتقييم",
            "التكيف مع احتياجات اللحظة",
          ],
        },
        allowingAutonomy: {
          title: "إتاحة مساحة للاستقلالية",
          description:
            "يشجع كادرنا التربوي الاستقلالية. يسمحون للأطفال باتخاذ خياراتهم الخاصة، واكتشاف حدودهم، وارتكاب الأخطاء، تحت إشراف آمن.",
          practices: [
            "يمكن للأطفال اختيار ما يريدون اللعب به أو مكان الجلوس",
            "يُسمح لهم بالمساعدة في الروتين اليومي",
            "الأنشطة مفتوحة وداعية، دون نتائج إلزامية",
            "يقدم الموظفون القرب والدعم عند الحاجة",
          ],
        },
        individualDevelopment: {
          title: "دعم التطور الفردي",
          description:
            "يتطور كل طفل وفقًا لسرعته الخاصة وبطريقته الخاصة. يراقب موظفونا التطور بشكل منهجي من خلال الملاحظات والمحادثات واجتماعات أولياء الأمور والمعلمين.",
          practices: [
            "المتابعة المنهجية من خلال الملاحظات",
            "النظر إلى التطور الكلي: اللغة، الحركية، الاجتماعي-العاطفي، المعرفي",
            "تكييف الأنشطة حسب احتياجات الطفل واهتماماته",
            "استخدام أنظمة متابعة الطفل وأدوات التفكير",
          ],
        },
        everyEmployeeMentor: {
          title: "كل موظف كمرشد",
          description:
            "كل طفل في ميني أفونتوريرز لديه مرشد مخصص: محترف رعاية أطفال يكون نقطة اتصال موثوقة للطفل والوالدين.",
          practices: [
            "المتابعة الدقيقة لتطور الطفل",
            "إجراء محادثات إرشادية مع الوالدين",
            "ضمان علاقة دافئة وفردية مع الطفل",
            "الحفاظ على نظرة شاملة لرفاهية الطفل ومسار نموه",
          ],
        },
      },

      professionalDevelopment: {
        title: "التطوير المهني لفريقنا",
        intro:
          "لضمان الجودة، نستثمر باستمرار في التطوير المهني والشخصي لموظفينا. نتوقع منهم ألا يعملوا فقط بروتين، بل أن يتخذوا خيارات تربوية واعية، مع احترام الطفل كفرد.",
        areas: {
          intervision: {
            title: "الإشراف المنتظم وانعكاس الفريق",
            description: "اجتماعات فريق منظمة للنمو المهني والتعلم المشترك",
          },
          education: {
            title: "مزيد من التعليم والتدريب",
            description: "تعلم مستمر في التطور العاطفي، وتوجيه اللعب، والتواصل",
          },
          coaching: {
            title: "الإشراف في مكان العمل",
            description: "إرشاد ودعم عملي للتطوير المهني",
          },
          documentation: {
            title: "وقت للتوثيق والتحضير",
            description: "تخصيص وقت للتخطيط والتوثيق والمشاورات",
          },
        },
      },

      sensitiveResponsiveness: {
        title: "الاستجابة الحساسة",
        intro:
          "هذا النهج يستند إلى نظرية التعلق لبولبي وآينسورث: الاستجابة الدافئة والمتسقة للطفل تعزز التعلق الآمن، وهو أمر حاسم للتطور الاجتماعي والعاطفي الصحي.",
        secureAttachment: {
          title: "التعلق الآمن",
          description:
            "الاستجابات الدافئة والمتسقة تعزز التعلق الآمن، مما يخلق أساسًا للتطور الاجتماعي والعاطفي الصحي والاستكشاف.",
        },
        subtleCues: {
          title: "الإشارات الدقيقة",
          description:
            "يتعرف الموظفون على الإشارات الدقيقة مثل التوتر أو الحاجة إلى الراحة أو الانتباه ويستجيبون لها، مما يضمن أن كل طفل يشعر بأنه مرئي ومفهوم.",
        },
      },

      teamConsultation: {
        title: "تشاور الفريق والتدريب المستمر",
        teamCollaboration: {
          title: "تعاون الفريق",
          description:
            "الاجتماعات والمشاورات المنتظمة تضمن جودة ثابتة وأفضل الممارسات المشتركة بين جميع الموظفين.",
          practices: [
            "الإشراف المنتظم وانعكاس الفريق",
            "التعلم المشترك وأفضل الممارسات",
            "حل المشكلات بشكل تعاوني",
          ],
        },
        continuousLearning: {
          title: "التعلم المستمر",
          description:
            "التعليم والتدريب المستمر يضمن أن يظل موظفونا على اطلاع بأحدث المناهج التربوية والبحوث.",
          practices: [
            "التعليم المستمر والتدريب المتخصص",
            "برامج التدريب والإرشاد",
            "فرص التطوير المهني",
          ],
        },
      },

      staffInAction: {
        title: "فريقنا في العمل",
        professionalMentors: {
          title: "مرشدون محترفون",
          description:
            "لكل طفل مرشد مخصص يتابع تطوره عن كثب ويكون نقطة اتصال موثوقة للوالدين.",
        },
        emotionalAnchors: {
          title: "دعامات عاطفية",
          description:
            "يقدم موظفونا استقراراً عاطفياً ودعماً، مما يساعد الأطفال على الشعور بالأمان والثقة في بيئتهم.",
        },
        growthCoaches: {
          title: "مدربو النمو",
          description:
            "يرشد الموظفون نمو الأطفال من خلال اللعب والأنشطة التعليمية والتفاعلات الداعمة التي تعزز النمو.",
        },
      },

      cta: {
        title: "تعرف على فريقنا المحترف",
        subtitle:
          "اختبر التزام وخبرة موظفينا التربويين الذين يجعلون كل يوم مميزًا لطفلك",
        scheduleVisit: "حدد موعداً لزيارة",
        learnMore: "اعرف المزيد",
      },

      inPractice: "في الممارسة:",
    },

    // Dashboard page translations
    dashboard: {
      welcome: "مرحباً",
      subtitle: "إدارة معلومات أطفالك والبقاء على اطلاع بأنشطتهم",
      birthDate: "تاريخ الميلاد",
      group: "المجموعة",
      notAssigned: "غير مُعين",
      registeredSince: "مُسجل منذ",
      viewDetails: "عرض التفاصيل",
      editInfo: "تحرير المعلومات",
      quickActions: "إجراءات سريعة",
      scheduleVisit: "جدولة زيارة",
      contact: "اتصال",
      registration: "التسجيل",
      reports: "التقارير",
      recentUpdates: "التحديثات الأخيرة",
      newActivity: "تم إضافة أنشطة جديدة إلى البرنامج",
      photosAdded: "تم إضافة صور جديدة من أنشطة اليوم",
      menuUpdate: "تم تحديث قائمة الطعام لهذا الأسبوع",
      daysAgo: "منذ أيام",
      weekAgo: "منذ أسبوع",
    },

    // Emotional Safety page translations
    emotionalSafety: {
      cta: {
        scheduleVisit: "حدد موعد زيارة",
        learnMore: "اعرف المزيد",
      },

      title: "الأمان العاطفي والثقة",
      subtitle: "أساس رؤيتنا التربوية حيث يشعر كل طفل بالحب والرؤية والاستماع",
      sectionTitle: "4. الأمان العاطفي والثقة",
      introText1:
        "في مغامري مينى، الأمان العاطفي هو أساس رؤيتنا التربوية. كل طفل يحتاج للشعور بالحب والرؤية والاستماع إليه للنمو بشكل مثالي. نحن نخلق بيئة حيث يمكن للأطفال تكوين روابط آمنة، وتعلم فهم ومعالجة عواطفهم، وتجربة الثقة أنه يمكنهم أن يكونوا أنفسهم.",
      introText2:
        "نهجنا يقوم على، من بين أمور أخرى، نظرية التعلق لجون بولبي وماري آينسورث، الذين يؤكدون أن الأطفال يحتاجون إلى قاعدة آمنة لاستكشاف العالم منها. إيمي بيكلر وطريقة غوردون (من قبل توماس غوردون) هم أيضاً مصادر إلهام مهمة لتواصلنا المحترم ومركزية العلاقة.",

      whyEssential: "لماذا الأمان العاطفي ضروري",
      benefits: {
        dareExplore: {
          title: "الجرأة على الاستكشاف",
          description: "الأطفال يشعرون بالأمان لتجربة أشياء جديدة وأخذ المخاطر",
        },
        emotionalRegulation: {
          title: "تنظيم عاطفي أفضل",
          description: "تعلم التعامل مع العواطف والإحباط والخيبات",
        },
        positiveSelf: {
          title: "صورة ذاتية إيجابية",
          description: "تطوير مهارات اجتماعية قوية وثقة بالنفس",
        },
        lastingRelationships: {
          title: "علاقات دائمة",
          description: "بناء روابط آمنة مع البالغين والأقران",
        },
      },

      inPractice: "في الممارسة",
      practices: {
        familiarFaces: {
          title: "وجوه مألوفة وقابلية للتنبؤ",
          description:
            "نحن نعمل مع فريق دائم من محترفي رعاية الأطفال لكل مجموعة. هذا يخلق الألفة والثقة. الأطفال يعرفون من يعتني بهم ولا يحتاجون للتكيف باستمرار.",
          dailyStructure: "الهيكل اليومي",
          dailyStructureDesc:
            "الهيكل اليومي واضح وقابل للتنبؤ. يتم الإعلان عن الانتقالات مسبقاً ومرافقتها بطقوس (مثل الأغاني أو التفسيرات القصيرة)، حتى يتمكن الأطفال من الاستعداد وعدم التفاجؤ. هذا يمنع الضغط غير الضروري.",
        },
        individualAttention: {
          title: "الاهتمام بالطفل الفردي",
          description:
            "نحن نقدم اهتماماً فردياً، بما في ذلك داخل المجموعة. من خلال الملاحظة الدقيقة، نحدد احتياجات كل طفل. أحياناً يعني ذلك عناقاً، أحياناً لحظة راحة، أحياناً يداً مساعدة أو أذناً مستمعة.",
          staffApproach: "نهج الطاقم التعليمي",
          approaches: {
            eyeLevel: "التواصل على مستوى العين، مع لغة جسد مفتوحة",
            acknowledgeFeelings: "تسمية والاعتراف بالمشاعر بنشاط",
            neverReject: "عدم رفض أو تقليل العواطف أبداً",
            clearBoundaries: "وضع حدود واضحة بطريقة محترمة ومحبة",
          },
        },
        parentalTrust: {
          title: "ثقة الوالدين",
          description:
            "الأمان العاطفي لا يُخلق فقط داخل رعاية الأطفال؛ يبدأ بالثقة بين الآباء وموظفي رعاية الأطفال. نحن نستثمر بوعي في العلاقة مع الآباء.",
          parentPartnership: "شراكة الوالدين",
          partnerships: {
            warmHandover: "تسليم دافئ في أوقات الإيصال والاستلام",
            openCommunication: "تواصل مفتوح وصادق بدون حكم",
            takeConcerns: "أخذ مخاوف الآباء على محمل الجد",
            involveParents: "إشراك الآباء في تطوير طفلهم",
          },
        },
      },

      trainingDevelopment: "تدريب وتطوير الطاقم التعليمي",
      trainingIntro:
        "لتحقيق هذا، نتوقع من طاقمنا التعليمي تطوير كفاءات محددة وتحسين مهاراتهم في ممارسات الأمان العاطفي بشكل مستمر.",
      staffExpectations: {
        selfReflective: {
          title: "العمل التأملي الذاتي",
          description: "التفكير بانتظام في أفعالك وتواصلك الخاص",
        },
        emotionalTraining: {
          title: "تدريب التواصل العاطفي",
          description:
            "مدربون في التواصل العاطفي مع الأطفال من خلال التوجيه والتدريب",
        },
        developmentalKnowledge: {
          title: "معرفة علم النفس التطويري",
          description: "فهم سلوك وعواطف الأطفال في السياق",
        },
        timeToConnect: {
          title: "أخذ وقت للتواصل",
          description: "إفساح المجال للتواصل، حتى خلال الأوقات المزدحمة",
        },
        visiblySensitive: {
          title: "كونك حساساً بوضوح",
          description: "رؤية وانعكاس ودعم الأطفال",
        },
      },
      continuousDevelopment: "التطوير المستمر",
      continuousDevDesc:
        "نحن نسهل هذا من خلال الاستشارات التربوية المنتظمة وجلسات التدريب والتعليم الإضافي، حيث يحصل هذا الموضوع على اهتمام هيكلي. الأمان العاطفي ليس فكرة لاحقة—إنه جوهر جودتنا التربوية.",

      creatingSafeSpaces: "خلق مساحات آمنة",
      secureEnvironment: {
        title: "بيئة آمنة",
        description:
          "مساحاتنا مصممة لتشعر كبيت ثاني، مع زوايا مريحة وإضاءة ناعمة ووجوه مألوفة تخلق شعوراً بالانتماء والأمان لكل طفل.",
      },
      emotionalConnection: {
        title: "الاتصال العاطفي",
        description:
          "كل تفاعل هو فرصة لبناء الثقة. نحن نستمع ونؤكد المشاعر ونستجيب بتعاطف، مما يساعد الأطفال على تطوير الذكاء العاطفي والمرونة.",
      },

      experienceEnvironment: "تجربة بيئتنا الراعية",
      experienceSubtitle:
        "انظر كيف نخلق الأمان العاطفي ونبني علاقات ثقة مع كل طفل",
    },

    // Home page
    home: {
      welcome: "مرحبًا بكم في مغامري مينى!",
      subtitle:
        "في ميني أفونتوريرز، يدور كل شيء حول الاكتشاف، والنمو، والاستمتاع. نحن نوفر مكانًا آمنًا وموثوقًا حيث يشعر الأطفال بالراحة ويمكنهم التطور وفقًا لوتيرتهم الخاصة. للصغار من عمر 0 إلى 4 سنوات لدينا الحضانة، حيث يسير اللعب والتعلم جنبًا إلى جنب. أما للأطفال من 4 إلى 13 سنة فنوفر رعاية ما بعد المدرسة، وهي بيئة مريحة للاسترخاء بعد الدوام، واكتشاف أشياء جديدة، والاستمتاع معًا. فريقنا حاضر يوميًا بالاهتمام والدفء والنظام، لنضمن أن يحصل كل طفل على المساحة لاكتشاف مواهبه والنمو بثقة ومتعة.",
      registerChild: "تسجيل الطفل",
      registerTour: "تسجيل جولة",

      aboutUs: {
        title: "معلومات عنا",
        welcomeCenter: {
          title: "مرحبًا بكم في مركزنا",
          description:
            "في مغامري مينى، مهمتنا هي توفير بيئة آمنة ومغذية وملهمة حيث يُرى كل طفل ويُسمع ويُدعم في تطويره الفريد. من خلال اللعب والفضول والتواصل مع الطبيعة، نمكن الأطفال من النمو إلى أفراد واثقين وسعداء.",
        },
        growingImproving: {
          title: "النمو والتطور",
          description:
            "نحن نؤمن أن كل طفل يتطور بطريقته الخاصة وفي وقته الخاص. رؤيتنا هي توجيه هذا النمو من خلال الأمان العاطفي، الهيكل، وحرية التعبير. من خلال إنشاء مساحة مليئة بالاكتشاف والدفء والارتباط، نضمن أن يشعر الأطفال بالأمان لاستكشاف العالم من حولهم، واتخاذ القرارات، وأن يصبحوا ما هم المفترض أن يكونوا.",
        },
      },

      services: {
        title: "خدماتنا",
        subtitle:
          "اكتشف مجموعتنا الشاملة من خدمات رعاية الأطفال المصممة لدعم نمو وتطور طفلك.",
        items: {
          childcarePrograms: {
            title: "برامج رعاية الأطفال",
            features: ["رعاية يومية", "أنشطة منظمة", "مناطق التنمية"],
          },
          transportPickup: {
            title: "خدمات النقل والاصطحاب",
            features: ["مسارات آمنة", "متابعة GPS", "جدول زمني موثوق"],
          },
          healthyMeals: {
            title: "الوجبات الصحية والتغذية",
            features: [
              "نظام غذائي متوازن",
              "قائمة قابلة للتخصيص",
              "باشراف اختصاصيي تغذية",
            ],
          },
          digitalTools: {
            title: "الأدوات الرقمية والتحديثات اليومية",
            features: [
              "تحديثات في الوقت الحقيقي",
              "بوابة الوالدين",
              "اتصال سلس",
            ],
          },
          dailyRoutines: {
            title: "الجداول اليومية",
            features: ["جدول منظم", "توازن وقت اللعب", "فترات الراحة"],
          },
          emotionalSafety: {
            title: "السلامة العاطفية",
            features: ["بيئة آمنة", "إرشاد مهني", "طاقم دعم"],
          },
          mealtimesActivities: {
            title: "وجبات الأنشطة",
            features: ["أنشطة جذابة", "تنوع في الوجبات", "تعليم التغذية"],
          },
          ageGroupStructure: {
            title: "هيكل الفئة العمرية",
            features: ["رعاية مخصصة", "أنشطة مناسبة للعمر", "موظفين متخصصين"],
          },
          transitions: {
            title: "الانتقالات",
            features: ["طاقم مدرب", "انتقالات داعمة", "تغذية راجعة مستمرة"],
          },
          settlingPolicy: {
            title: "سياسة الدمج",
            features: ["بيئة ترحيبية", "مساعدة الاندماج", "دعم الوالدين"],
          },
          parentCommunication: {
            title: "اتصال الآباء",
            features: ["اتصال ذو اتجاهين", "تحديثات منتظمة", "بناء المجتمع"],
          },
          hygiene: {
            title: "النظافة",
            features: ["مرافق نظيفة", "فحوصات منتظمة", "بروتوكولات صحية"],
          },
          openingClosing: {
            title: "الافتتاح/الإغلاق",
            features: ["جدول منظم", "فحوصات السلامة", "عمليات فعالة"],
          },
        },
      },

      contactUs: "اتصل بنا",
      learnMore: "أعرف أكثر",
      whyChooseUs: "لماذا تختارنا؟",
      whyChooseUsSubtitle:
        "نحن نقدم بيئة مغذية حيث يمكن للأطفال النمو والتعلم والازدهار",
      activities: "أنشطتنا",
      activitiesSubtitle: "أنشطة جذابة مصممة لتطوير مهارات وإبداع طفلك",
      viewDailyRoutine: "عرض الجدول اليومي",
      parentTestimonials: "ماذا يقول الآباء",
      testimonialsSubtitle: "استمع إلى العائلات التي تثق بنا مع أطفالها",
      readyToStart: "مستعد لبدء رحلة طفلك؟",
      ctaSubtitle: "انضم إلى عائلتنا وامنح طفلك أفضل بداية في الحياة",
      registerNow: "سجل الآن",
      scheduleVisit: "جدولة زيارة",
      ctaReady: "مستعد للبدء؟",
      ctaDescription:
        "اتصل بنا اليوم لمعرفة المزيد عن برامجنا وجدولة زيارة إلى منشأتنا.",
      ctaScheduleVisit: "جدولة زيارة",
      ctaDownloadBrochure: "تحميل الكتيب",
    },

    // About page
    about: {
      title: "عن مغامري مينى",
      subtitle: "قصتنا ومهمتنا والفريق وراء رعاية طفلك",
      mission: "مهمتنا",
      missionText:
        "توفير بيئة آمنة وداعمة ومحفزة حيث يمكن لكل طفل تطوير إمكانياته الكاملة من خلال اللعب والتعلم والتفاعل الاجتماعي.",
      vision: "رؤيتنا",
      visionText:
        "أن نكون المزود الرائد لرعاية الأطفال في أوترخت، معروفين بالتميز في تعليم ورعاية الطفولة المبكرة.",
      values: "قيمنا",
      valuesList: {
        safety: "السلامة أولاً",
        learning: "التعلم من خلال اللعب",
        respect: "احترام كل طفل",
        community: "بناء المجتمع",
      },
      team: "فريقنا",
      teamText:
        "موظفونا ذوو الخبرة والمؤهلون متحمسون لتطوير الطفولة المبكرة وملتزمون بتقديم أفضل رعاية لطفلك.",
      experience: "سنوات الخبرة",
      children: "أطفال سعداء",
      staff: "موظفون مؤهلون",
      satisfaction: "رضا الوالدين",
      ourImpact: "تأثيرنا",
      principlesThatGuide: "المبادئ التي توجه كل ما نقوم به",
      valuesDescriptions: {
        safety: "نحن نعطي الأولوية لسلامة ورفاهية كل طفل تحت رعايتنا",
        learning:
          "الأطفال يتعلمون بشكل أفضل من خلال اللعب والاستكشاف والتجارب العملية",
        respect: "كل طفل فريد ويستحق الاحترام والحب والاهتمام الفردي",
        community: "نحن نبني شراكات قوية مع العائلات والمجتمع المحلي",
      },
      teamMembers: {
        sarahJohnson: {
          name: "سارة جونسون",
          role: "مديرة ومؤسسة",
          experience: "20+ سنة في تعليم الطفولة المبكرة",
          description:
            "متحمسة لخلق بيئات داعمة حيث يمكن للأطفال النمو والازدهار.",
        },
        mariaRodriguez: {
          name: "ماريا رودريغيز",
          role: "معلمة رئيسية",
          experience: "15+ سنة خبرة تدريس",
          description:
            "متخصصة في الفنون الإبداعية وتطوير اللغة للأطفال الصغار.",
        },
        davidChen: {
          name: "ديفيد تشين",
          role: "أخصائي التربية البدنية",
          experience: "12+ سنة في تطوير الطفل",
          description:
            "يركز على تطوير المهارات الحركية والأنشطة البدنية الصحية.",
        },
        lisaThompson: {
          name: "ليزا تومسون",
          role: "منسقة التغذية",
          experience: "10+ سنوات في تغذية الأطفال",
          description: "تضمن وجبات صحية ومتوازنة تدعم نمو وتطور الأطفال.",
        },
      },
      parentTestimonials: {
        emmaAndTom: {
          names: "إيما وتوم ويلسون",
          child: "لوكاس (4 سنوات)",
          text: "كان مغامري مينى تجربة رائعة لابننا. الموظفون يهتمون حقاً بتطور كل طفل ويخلقون بيئة دافئة ومحبة.",
        },
        annaAndMark: {
          names: "آنا ومارك ديفيس",
          child: "صوفي (3 سنوات)",
          text: "نحب كيف يركز المركز على التعلم واللعب معاً. نمت صوفي كثيراً في الثقة والمهارات منذ انضمامها.",
        },
        lisaAndJames: {
          names: "ليزا وجيمس براون",
          child: "أوليفر (5 سنوات)",
          text: "التواصل مع الآباء ممتاز، ونشعر دائماً بأننا مطلعون على تقدم طفلنا وأنشطته اليومية.",
        },
      },
      ctaJoinFamily: "انضم إلى عائلتنا",
      ctaExperience:
        "اختبر الفرق الذي يمكن أن يحدثه فريقنا المتخصص ونهجنا المثبت لتطوير طفلك.",
      meetTeam: "تعرف على فريقنا المتخصص من المهنيين في الطفولة المبكرة",
      whatParentsSayTitle: "ماذا يقول الآباء",
      whatParentsSaySubtitle: "استمع من العائلات التي تثق بنا مع أطفالها",
      scheduleVisit: "جدولة زيارة",
      contactUs: "اتصل بنا",
    },

    // Services page
    services: {
      title: "خدماتنا",
      subtitle: "حلول شاملة لرعاية الأطفال لكل عائلة",
      daycare: "الرعاية النهارية الكاملة",
      daycareDesc: "رعاية كاملة للأطفال من الصباح إلى المساء مع أنشطة تعليمية",
      afterSchool: "الرعاية بعد المدرسة",
      afterSchoolDesc: "رعاية آمنة ومثيرة للأطفال في سن المدرسة",
      holiday: "رعاية العطلات",
      holidayDesc: "برامج خاصة خلال عطلات المدرسة",
      flexible: "ساعات مرنة",
      flexibleDesc: "جداول قابلة للتخصيص تناسب احتياجات عائلتك",
      learnMore: "اعرف المزيد",
      contactUs: "اتصل بنا",
    },

    // Gallery page
    gallery: {
      title: "معرض الصور",
      subtitle: "أنظر إلى أنشطتنا اليومية واللحظات السعيدة",
      featuredPhotos: "الصور المميزة",
      carouselSubtitle: "قم بالتمرير خلال التلفزيونات الخاصة بنا",
      photoGrid: "جمع الصور",
      gridSubtitle: "تصفح مجموعة كاملة من ذكرياتنا",
      ctaTitle: "هل تريد أن ترى المزيد؟",
      ctaSubtitle: "قم بتخطيط زيارة لتجربة جو الوطنية والسعيدة في الأفراد",
      scheduleVisit: "تخطيط زيارة",
      contactUs: "اتصل بنا",
      images: {
        creativePainting: {
          src: "/images/creative-1.jpg",
          title: "الرسم الإبداعي",
          alt: "الأطفال يرسمون معًا",
        },
        artActivities: {
          src: "/images/creative-2.jpg",
          title: "أنشطة فنية",
          alt: "الأطفال يقومون بأنشطة فنية",
        },
        drawingCrafting: {
          src: "/images/creative-3.jpg",
          title: "الرسم والأشغال اليدوية",
          alt: "الأطفال يرسمون ويصنعون الأعمال اليدوية",
        },
        outdoorFun: {
          src: "/images/outdoor-1.jpg",
          title: "مرح في الخارج",
          alt: "الأطفال يلعبون في الخارج",
        },
        parkPlay: {
          src: "/images/outdoor-2.jpg",
          title: "اللعب في الحديقة",
          alt: "الأطفال يركضون في الحديقة",
        },
        playgroundTime: {
          src: "/images/outdoor-3.jpg",
          title: "وقت الملعب",
          alt: "مجموعة من الأطفال في الملعب",
        },
        healthyMeals: {
          src: "/images/meals-1.jpg",
          title: "وجبات صحية",
          alt: "الأطفال يأكلون وجبات صحية",
        },
        lunchTime: {
          src: "/images/meals-2.jpg",
          title: "وقت الغداء",
          alt: "الأطفال يستمتعون بالغداء معًا",
        },
        snackSharing: {
          src: "/images/meals-3.jpg",
          title: "مشاركة الوجبات الخفيفة",
          alt: "الأطفال يشاركون الوجبات الخفيفة",
        },
        napTime: {
          src: "/images/rest-1.jpg",
          title: "وقت القيلولة",
          alt: "طفل ينام بسلام",
        },
        restingMats: {
          src: "/images/rest-2.jpg",
          title: "حصائر الراحة",
          alt: "الأطفال يستريحون على الحصائر",
        },
        daycareSleep: {
          src: "/images/rest-3.jpg",
          title: "النوم في الحضانة",
          alt: "الأطفال ينامون في الحضانة",
        },
        groupLearning: {
          src: "/images/learning-1.jpg",
          title: "التعلم الجماعي",
          alt: "الأطفال يتعلمون معًا",
        },
        readingTime: {
          src: "/images/learning-2.jpg",
          title: "وقت القراءة",
          alt: "الأطفال يقرؤون الكتب",
        },
        teacherHelp: {
          src: "/images/learning-3.jpg",
          title: "مساعدة المعلم",
          alt: "المعلم يساعد الطلاب",
        },
        musicClass: {
          src: "/images/music-1.jpg",
          title: "حصة موسيقى",
          alt: "الأطفال يعزفون الموسيقى",
        },
        instrumentsFun: {
          src: "/images/music-2.jpg",
          title: "متعة الآلات الموسيقية",
          alt: "الأطفال مع الآلات الموسيقية",
        },
        singingGroup: {
          src: "/images/music-3.jpg",
          title: "مجموعة الغناء",
          alt: "مجموعة تغني معًا",
        },
      },
    },

    // Daily Routine page
    dailyRoutine: {
      title: "الروتين اليومي",
      subtitle: "يوم نموذجي في Mini Avonturiers",

      // الصباح
      morningSchedule: "برنامج الصباح",
      morningDesc: "يبدأ الأطفال يومهم بأنشطة منظمة وتجارب تعليمية.",
      morning: {
        dropOff: {
          time: "7:00 – 9:00 ",
          title: "الوصول الصباحي واللعب الحر",
          description:
            "يُستقبل الأطفال في أجواء مريحة. يُمنح الوالدان فرصة لمشاركة المعلومات مع المربي. يختار الأطفال نشاطهم أو ركنهم المفضل، مما يسمح لهم ببدء يومهم بوتيرتهم الخاصة. هذه المرحلة تعزز الشعور بالأمان والارتباط: الانتقال من المنزل إلى الحضانة يكون هادئًا ومتمحورًا حول الطفل.",
        },
        circle: {
          time: "9:00 ",
          title: "فاكهة، مشروبات ووقت الدائرة",
          description:
            "نجتمع في دائرة لوقت ممتع وتعليمي: الغناء، الحديث عن الطقس، قراءة كتاب، والتواصل مع الآخرين. يعزز هذا مهارات اللغة وروح الفريق والتطور الاجتماعي العاطفي. يتم الحفاظ على أوقات الطعام بشكل جماعي وهادئ.",
        },
        activity: {
          time: "10:00 ",
          title: "نشاط موجه",
          description:
            "تختلف الأنشطة يوميًا، لكنها تتماشى مع الموضوع ومجال التطور (مثل: الأشغال اليدوية، ألعاب اللغة، التسلسل، المواد الحسية). تُقام الأنشطة في مجموعات صغيرة أو بشكل فردي وتُكيف وفق احتياجات كل طفل.",
        },
        outdoor: {
          time: "11:00 ",
          title: "اللعب في الهواء الطلق أو اللعب الموضوعي",
          description:
            "الحركة في الهواء الطلق ضرورية للمهارات الحركية والصحة. هناك مساحة للتسلق والجري والاستكشاف واللعب الحر. في حالة الطقس السيئ، يتم تقديم جلسة لعب مماثلة بموضوع محدد في الداخل.",
        },
        lunch: {
          time: "11:30 ",
          title: "الغداء وفقًا لدليل التغذية الصحي",
          description:
            "يُعد الأكل الصحي جزءًا أساسيًا من رؤيتنا التربوية. نتناول الغداء معًا مع التركيز على العادات الصحية، الاستقلالية، والمحادثة على المائدة. نشجع الأطفال على تجربة نكهات جديدة، ونشركهم في التحضير متى أمكن.",
        },
      },

      // بعد الظهر
      afternoonSchedule: "برنامج بعد الظهر",
      afternoonDesc:
        "في فترة بعد الظهر نوازن بين الراحة والأنشطة الإبداعية والبدنية.",
      afternoon: {
        rest: {
          time: "12:30",
          title: "وقت الراحة أو القيلولة",
          description:
            "ينام الأطفال الأصغر سنًا في غرفة هادئة ومظلمة تحت إشراف. أما الأطفال الأكبر الذين لا ينامون، فيُمنحون وقت راحة: قراءة، استلقاء أو لعب هادئ. هذا يعزز التعافي والتركيز والتنظيم العاطفي.",
        },
        snack: {
          time: "2:30",
          title: "وجبة خفيفة من الفاكهة أو الخضار",
          description:
            "توفر الوجبة الصحية استراحة قصيرة. كما نستخدم هذا الوقت لملاحظة كيف يشعر الأطفال وما يحتاجونه لبقية اليوم.",
        },
        activity: {
          time: "3:00",
          title: "نشاط: لعب أدوار، مهارات حركية، تمارين بعد الظهر",
          description:
            "في فترة بعد الظهر نركز على الأنشطة التعبيرية والبدنية: تمارين، رقص، بناء، لعب أدوار أو تمثيل قصة معًا. التركيز على المرح، التعبير، والعمل الجماعي.",
        },
        meal: {
          time: "4:00",
          title: "خبز، أعواد خبز أو وجبة ساخنة",
          description:
            "اعتمادًا على الطفل ومدة الرعاية، نقدم وجبة خفيفة مغذية أو وجبة ساخنة خفيفة. يظل الأكل مناسبة اجتماعية، لكنه ليس إلزاميًا. يحدد الأطفال احتياجاتهم الغذائية ضمن الخيارات المتاحة.",
        },
        pickup: {
          time: "5:00 – 6:30",
          title: "الاستلام، اللعب الحر والأركان الهادئة",
          description:
            "ينتهي اليوم في أجواء مريحة. يلعب الأطفال بحرية في بيئة هادئة. نوفر إضاءة خافتة وأركان للكتب لجعل الانتقال إلى المنزل متوقعًا وهادئًا. يأخذ الموظفون وقتًا كافيًا لتسليم الأطفال إلى أولياء الأمور.",
        },
      },

      // المبادئ
      principles: "المبادئ التوجيهية",
      principlesDesc:
        "تم تصميم روتيننا اليومي مع مراعاة التوازن والإيقاع ورفاهية الأطفال.",
      principleItems: {
        predictability: {
          title: "التكرار والتوقع",
          description: "يوفران للأطفال الهيكل ويسهلان الانتقال بين الأنشطة.",
        },
        sharedMoments: {
          title: "اللحظات المشتركة",
          description:
            "(وقت الدائرة، أوقات الوجبات) يتعلم الأطفال المهارات الاجتماعية واحترام الآخرين.",
        },
        freePlay: {
          title: "لحظات اللعب الحر",
          description: "تحفز الإبداع، التوجيه الذاتي والتفاعل الاجتماعي.",
        },
        targeted: {
          title: "أنشطة موجهة",
          description: "توفر تحديات تنموية ضمن منطقة النمو القريبة (فيجوتسكي).",
        },
        rest: {
          title: "الراحة والنوم",
          description: "ضرورية لمعالجة الانطباعات وتعزز التنظيم الذاتي.",
        },
        attentionSpan: {
          title: "مدى الانتباه الطبيعي",
          description:
            "تم تصميم الروتين اليومي ليتناسب مع مدى انتباه الأطفال الطبيعي، حتى يتمكنوا من التعلم واللعب بشكل مثالي دون فرط التحفيز.",
        },
      },
    },
    // Contact page
    contact: {
      title: "اتصل بنا",
      subtitle: "تواصل مع فريقنا",
      address: "العنوان",
      addressText: "كيندرسترات 123، 3511 أ ب أوترخت",
      phone: "الهاتف",
      phoneText: "+31 (0)30 123 4567",
      email: "البريد الإلكتروني",
      emailText: "info@mini-avonturiers.nl",
      hours: "ساعات العمل",
      hoursText: "الاثنين - الجمعة: 07:00 - 18:30",
      message: "أرسل لنا رسالة",
      name: "الاسم الكامل",
      emailField: "عنوان البريد الإلكتروني",
      subject: "الموضوع",
      messageField: "رسالتك",
      send: "إرسال الرسالة",
      mondayFriday: "من الاثنين إلى الجمعة",
      saturday: "السبت",
      sunday: "الأحد",
      closed: "مغلق",
      saturdayOpeningHours: "ساعات العمل مختلفة",
      faq: "الأسئلة الشائعة",
      faqSubtitle: "اعثر على إجابات للأسئلة الشائعة حول خدمات رعاية الأطفال",
      faqItems: {
        q1: "ما هي الفئات العمرية التي تعتنون بها؟",
        a1: "نحن نعتني بالأطفال من 0 إلى 12 سنة، مع برامج مناسبة للعمر لكل مجموعة.",
        q2: "ما هي ساعات العمل؟",
        a2: "نحن مفتوحون من الاثنين إلى الجمعة من 7:00 صباحاً إلى 6:30.",
        q3: "هل تقدمون وجبات؟",
        a3: "نعم، نقدم وجبة إفطار وغداء ووجبات خفيفة صحية طوال اليوم.",
        q4: "ما هي نسبة الموظفين إلى الأطفال؟",
        a4: "نحافظ على نسبة 1:4 للرضع، 1:6 للصغار، و1:8 لمرحلة ما قبل المدرسة.",
        q5: "هل تقدمون جدولة مرنة؟",
        a5: "نعم، نقدم خيارات جدولة مرنة لاستيعاب احتياجات العائلات المختلفة.",
        q6: "ما الأنشطة التي تقدمونها؟",
        a6: "نقدم مجموعة متنوعة من الأنشطة تشمل الفنون والحرف، الموسيقى، اللعب في الهواء الطلق، الألعاب التعليمية، والمزيد.",
        q7: "كيف تتعاملون مع الطوارئ؟",
        a7: "لدينا إجراءات طوارئ شاملة وجميع الموظفين مدربون على الإسعافات الأولية.",
        q8: "هل يمكن للآباء الزيارة خلال النهار؟",
        a8: "نعم، الآباء مرحب بهم للزيارة خلال ساعات العمل، رغم أننا نطلب إشعاراً مسبقاً.",
      },
      transport: {
        byCar: "بالسيارة",
        byCarDescription:
          "موقف سيارات مجاني متوفر في الموقع. يقع بالقرب من الطريق السريع A12، مخرج 18.",
        byPublicTransport: "بوسائل النقل العام",
        byPublicTransportDescription:
          "تتوقف خطوط الحافلات 2 و8 و12 على بعد 5 دقائق سيرًا على الأقدام.",
        byBicycle: "بالدراجة",
        byBicycleDescription:
          "موقف دراجات آمن متوفر. يقع على الطريق الرئيسي للدراجات.",
      },
    },

    // Auth
    auth: {
      welcome: "مرحباً بعودتك",
      loginSubtitle: "سجل الدخول للوصول إلى حسابك",
      email: "عنوان البريد الإلكتروني",
      password: "كلمة المرور",
      login: "تسجيل الدخول",
      loggingIn: "جاري تسجيل الدخول...",
      loginFailed: "فشل تسجيل الدخول. يرجى التحقق من بياناتك.",
      noAccount: "ليس لديك حساب؟",
      registerHere: "سجل هنا",
      forgotPassword: "نسيت كلمة المرور؟",
      registerTitle: "انضم إلى عائلتنا",
      registerSubtitle: "أنشئ حساباً لتسجيل طفلك",
      fullName: "الاسم الكامل",
      phone: "رقم الهاتف",
      confirmPassword: "تأكيد كلمة المرور",
      parentInfo: "معلومات الوالدين",
      children: "الأطفال",
      addChild: "إضافة طفل",
      removeChild: "إزالة",
      childNumber: "الطفل",
      name: "اسم الطفل",
      dateOfBirth: "تاريخ الميلاد",
      group: "الفئة العمرية",
      register: "التسجيل",
      registering: "جاري التسجيل...",
      registrationFailed: "فشل التسجيل. يرجى المحاولة مرة أخرى.",
      passwordMismatch: "كلمات المرور غير متطابقة",
      passwordTooShort: "يجب أن تكون كلمة المرور 6 أحرف على الأقل",
      haveAccount: "هل لديك حساب؟",
      loginHere: "تسجيل الدخول هنا",
      childname: "اسم الطفل",
      childBirthDate: "تاريخ ميلاد الطفل",
      childGroup: "مجموعة الطفل",
      selectGroup: "اختر المجموعة",
      babies: "رضع",
      toddlers: "أطفال صغار",
      preschoolers: "أطفال ما قبل المدرسة",
      kindergarten: "روضة أطفال",
      schoolAge: "سن المدرسة",
    },

    // Common UI elements
    common: {
      scheduleVisit: "تخطيط زيارة",
      contactUs: "اتصل بنا",
      learnMore: "اعرف المزيد",
      registerNow: "سجل الآن",
      downloadBrochure: "تحميل الكتاب",
      readyToStart: "مستعدون لبدء رحلة طفلك؟",
      joinOurFamily: "انضم إلى عائلتنا",
      whatParentsSay: "ماذا يقول الآباء",
      frequentlyAskedQuestions: "الأسئلة الشائعة",
      quickAnswers: "إجابات سريعة للأسئلة الشائعة",
      getInTouch: "تواصل معنا",
      sendMessage: "إرسال الرسالة",
      findUs: "ابحث عننا",
      byCar: "بالسيارة",
      byPublicTransport: "بالنقل العام",
      byBicycle: "بالدراجات الهوائية",
      openingHours: "ساعات العمل",
      address: "العنوان",
      phone: "الهاتف",
      email: "البريد الإلكتروني",
      faq: "الأسئلة الشائعة",
      faqDescription: "إجابات سريعة على الأسئلة الشائعة",
      ctaReady: "نص عربي افتراضي لـ ",
      locationDesc:
        "نص عربي افتراضي لـقم بزيارة موقعنا واكتشف ما يقدمه Mini Adventurers.",
      ctaDescription: "نص عربي افتراضي لـ",
      ctaScheduleVisit: "نص عربي افتراضي لـ",
      ctaDownloadBrochure: "نص عربي افتراضي لـ",
    },

    // Form options
    form: {
      firstname: "الاسم الأول *",
      lastname: "الاسم الأخير *",
      firstnamePlaceholder: "جون",
      lastnamePlaceholder: "دو",
      selectSubject: "اختر موضوع",
      generalInquiry: "استفسار عام",
      registration: "التسجيل",
      scheduleVisit: "تخطيط زيارة",
      pricingInfo: "معلومات الأسعار",
      other: "آخر",
      fullName: "الاسم الكامل *",
      emailAddress: "عنوان البريد الإلكتروني *",
      phoneNumber: "رقم الهاتف",
      subject: "الموضوع *",
      message: "الرسالة *",
      yourFullName: "اسمك الكامل",
      emailPlaceholder: "البريد الإلكتروني@المثال.com",
      phonePlaceholder: "+31 (0)6 12345678",
      messagePlaceholder: "أخبرنا كيف يمكننا مساعدتك...",
      aanmeldenIntro:
        "هل ترغب في تسجيل طفلك أو لديك سؤال؟ يرجى ملء النموذج أدناه.",
      rondleidingIntro:
        "هل ترغب في زيارة موقعنا؟ يرجى ملء نموذج الاتصال أدناه لتحديد موعد.",
      aanmeldenTitle: "تسجيل الطفل أو طرح سؤال",
      rondleidingTitle: "طلب جولة",
      send: "إرسال",
      sending: "جاري الإرسال...",
      sendSuccess: "شكرًا لك! تم إرسال رسالتك بنجاح. سنتواصل معك قريبًا.",
      sendError:
        "عذرًا! حدث خطأ أثناء إرسال رسالتك. يرجى المحاولة مرة أخرى لاحقًا أو الاتصال بنا مباشرة.",
    },

    // FAQ items
    faq: {
      openingHours: "ما هي ساعات العمل؟",
      openingHoursAnswer:
        "نحن مفتوحون من الاثنين إلى الجمعة من 7:00 صباحاً إلى 6:30. نقدم أيضاً خيارات جدولة مرنة لتلبية احتياجات عائلتك.",
      ageGroups: "ما هي الفئات العمرية التي نخدمها؟",
      ageGroupsAnswer:
        "نقدم خدمة للأطفال من 0 إلى 12 سنة، مع برامج وأنشطة مناسبة لكل مرحلة تطويرية.",
      meals: "هل تقدمون وجبات؟",
      mealsAnswer:
        "نعم، نقدم وجبات صحية ومغذية ووجبات خفيفة طوال اليوم. يمكننا توافر متطلبات غذائية معينة.",
      scheduleVisit: "كيف يمكنني تخطيط زيارة؟",
      scheduleVisitAnswer:
        "يمكنك التخطيط للزيارة عن طريق الاتصال بنا، أو مراسلتنا عبر البريد الإلكتروني، أو ملء نموذج الاتصال أعلاه.",
    },

    // Activities
    activities: {
      hero: {
        title: "الأنشطة المقدمة",
        subtitle: "التعلم من خلال اللعب في بيئة طبيعية ومحفزة",
      },
      intro: {
        title: "التعلم من خلال اللعب",
        p1: "في ميني أفونتوريرز، يعتبر التعلم من خلال اللعب محورياً. تم تصميم أنشطتنا بعناية لتحفيز نمو الأطفال بطريقة طبيعية ومرحة.",
        p2: "تُصمم الأنشطة بما يتناسب مع مرحلة نمو الأطفال واهتماماتهم ومواهبهم، وتساهم في تطوير اللغة والمهارات الحركية والإبداعية والاجتماعية والعاطفية، بالإضافة إلى الوعي بالعالم.",
        ourApproachTitle: "نهجنا",
        ourApproachText:
          "نقدم برنامجاً يومياً متنوعاً يجمع بين اللعب الحر والأنشطة الموجهة. يعتمد نهجنا على نظرية منطقة النمو القريبة لفيجوتسكي: نقدم أنشطة أعلى قليلاً من مستوى الطفل الحالي، ولكن يمكن تحقيقها بالدعم المناسب.",
      },
      sensory: {
        title: "الأنشطة الحسية",
        subtitle:
          "اللعب الحسي — المعروف أيضاً باللعب بمواد تحفز الحواس — أساسي للأطفال الصغار.",
        helpsWithTitle: "يساعد اللعب الحسي في:",
        reggioNote:
          "يقوم الطاقم التربوي بتوجيه هذا النوع من اللعب من منظور ريجيو إيميليا: الطفل هو الباحث، والبيئة هي المربي الثالث، ونحن ندعم من خلال الأسئلة المفتوحة والكلمات والملاحظات.",
      },
      language: {
        title: "تطوير اللغة",
        subtitle: "اللغة تشكل الأساس للتواصل والتعبير ومهارات المدرسة لاحقاً.",
        note: "يستخدم طاقمنا تفاعلات غنية باللغة: يصفون الأفعال، يطرحون أسئلة مفتوحة، يوسعون العبارات، ويشجعون الأطفال على التحدث. يتماشى هذا النهج مع أسلوب القراءة التفاعلية ومبادئ التعليم المبكر.",
      },
      creative: {
        title: "التعبير الإبداعي",
        subtitle:
          "يساعد التعبير الإبداعي الأطفال على التعبير عن أنفسهم، ومعالجة المشاعر، وتطوير خيالهم.",
        note: "نشجع الأطفال على اتخاذ قراراتهم بأنفسهم ونخلق جواً يكون فيه التجريب وارتكاب الأخطاء والتخيل أمراً طبيعياً.",
      },
      thematic: {
        title: "العمل الموضوعي والعرض الموجه للنمو",
        subtitle:
          "نعمل على مواضيع مرتبطة بعالم الطفل، مثل الفصول، الأسرة، الحيوانات، المهن، العواطف، المرور أو الأعياد.",
        note: "ضمن هذه المواضيع، نربط الأنشطة بمناطق نمو متعددة. يقوم طاقمنا بتوجيه اللعب وتشجيع التعمق من خلال أسئلة موجهة، وتوسيع اللعب، ودمج الأهداف التعليمية.",
      },
      bso: {
        title: "برنامج ما بعد المدرسة",
        subtitle:
          "التحدي والمتعة للأطفال الأكبر سناً. غالباً ما يبحث أطفال BSO عن التحدي والاستقلالية والاسترخاء بعد المدرسة.",
        note: "تتم تهيئة أنشطة ما بعد المدرسة وفقاً لرغبات وأفكار الأطفال: للأطفال صوت ويمكنهم الاختيار من بين عدة خيارات. وهذا يخلق مساحة للاستقلالية والصداقة والاسترخاء.",
      },
      cta: {
        title: "هل أنتم مستعدون لاكتشاف أنشطتنا؟",
        subtitle:
          "اختبروا مجموعتنا المتنوعة من الأنشطة المصممة لتحفيز نمو طفلكم من خلال اللعب. احجزوا زيارة لرؤية برامجنا أثناء العمل.",
        button: "اعرف المزيد عن أنشطتنا",
      },
      sensoryActivities: {
        naturalMaterials: {
          title: "المواد الطبيعية (الرمل، الماء، الطين)",
          description:
            "يستكشف الأطفال الملمس ودرجات الحرارة والأشكال أثناء تنمية الإبداع.",
        },
        outdoorElements: {
          title: "العناصر الخارجية (مطبخ الطين، الحديقة)",
          description: "فرص لتجارب حسية غنية في الطبيعة.",
        },
        creativeMaterials: {
          title: "المواد الإبداعية (الدهانات، العجين، الأقمشة)",
          description: "تشجيع الخيال وتطوير المهارات الحركية الدقيقة.",
        },
      },
      sensoryBenefits: {
        exploringTextures: "استكشاف القوام والإحساسات المختلفة",
        developMotorSkills: "تطوير المهارات الحركية الدقيقة والكبيرة",
        emotionRegulation: "تحسين التنظيم العاطفي",
        problemSolving: "تشجيع مهارات حل المشكلات",
      },
      languageActivities: {
        dailyReading: {
          title: "القراءة اليومية",
          description: "وقت القصة الذي يوسع المفردات والفهم.",
        },
        visualLearning: {
          title: "التعلم البصري",
          description: "استخدام الصور والأشياء لدعم تطوير اللغة.",
        },
        musicRhymes: {
          title: "الموسيقى والقوافي",
          description: "غناء الأغاني والقوافي لتعزيز الإيقاع واللغة.",
        },
        rolePlay: {
          title: "اللعب التمثيلي",
          description: "يقوم الأطفال بتمثيل مواقف، مما يعزز التعبير والإبداع.",
        },
      },
      creativeActivities: {
        artsCrafts: {
          title: "الفنون والحرف اليدوية",
          description: "التلوين والقص واللصق للتعبير عن الإبداع.",
        },
        buildingConstruction: {
          title: "البناء والإنشاء",
          description: "استخدام المكعبات والمواد لتطوير المهارات المكانية.",
        },
        musicDance: {
          title: "الموسيقى والرقص",
          description: "الحركة والإبداع على الموسيقى، تطوير الإيقاع والتنسيق.",
        },
        drawingColoring: {
          title: "الرسم والتلوين",
          description: "استكشاف الألوان والأشكال باستخدام الأقلام والألوان.",
        },
      },
      thematicAreas: {
        arithmetic: {
          title: "الحساب",
          description: "أنشطة ممتعة للعد والتعرف على الأرقام.",
        },
        language: {
          title: "اللغة",
          description: "سرد القصص، القراءة، وألعاب المفردات.",
        },
        motorSkills: {
          title: "المهارات الحركية",
          description: "ألعاب تعزز الحركة والتنسيق.",
        },
        socialBehavior: {
          title: "السلوك الاجتماعي",
          description: "أنشطة تشجع التعاون والتعاطف.",
        },
      },
      bsoActivities: {
        sportsExercise: {
          category: "الرياضة والتمارين",
          activities: [
            "كرة القدم والألعاب الجماعية",
            "اللعب في الهواء الطلق وتحديات الجري",
          ],
        },
        technologyExperiments: {
          category: "التكنولوجيا والتجارب",
          activities: [
            "تجارب علمية بسيطة",
            "البناء باستخدام الليغو والألعاب التقنية",
          ],
        },
        cookingBaking: {
          category: "الطهي والخبز",
          activities: [
            "خبز البسكويت والوصفات البسيطة",
            "تحضير وجبات خفيفة صحية",
            "استكشاف النكهات والقوام",
          ],
        },
        excursionsTrips: {
          category: "الرحلات والجولات",
          activities: [
            "المشي في الطبيعة وزيارة الحدائق",
            "زيارة المعالم المحلية",
          ],
        },
      },
    },

    // Testimonials
    testimonials: {
      title: "ماذا يقول الآباء",
      subtitle: "اسمع من العائلات التي تثق بنا مع أطفالها",
      sarah: {
        name: "سارة جونسون",
        role: "أب الإيما (4 سنوات)",
        text: "رعاية الأطفال أوترخت كانت رائعة لوالدتنا. تحب التواجد هنا كل يوم وتعلمت الكثير. الموظفون حذرون ومهنيون.",
      },
      michael: {
        name: "مايكل شين",
        role: "أب لوكاس (3 سنوات)",
        text: "الروتين اليومي مثالي لابني. قام بتطوير مهارات جيدة في التواصل الاجتماعي وكان دائماً متحمساً لتقديم لنا عن رحلته.",
      },
      anna: {
        name: "آنا رودريغوز",
        role: "أم سوفيا (5 سنوات)",
        text: "لم نكن أفضل من الرعاية التي تتلقاها ابنتنا. الأنشطة تفاعلية وتعليمية، والموظفون يهتمون بكل طفل.",
      },
    },

    // Service Features
    serviceFeatures: {
      safety: "السلامة أولاً",
      safetyDesc: "بيئة آمنة مع مراقبة 24/7 وفريق مهني منظم",
      nutrition: "التغذية الصحية",
      nutritionDesc: "وجبات متوازنة ووجبات خفيفة تحضيرت بالمكونات الطازجة",
      education: "التعليم المبكر",
      educationDesc: "أنشطة تعليمية ومنهج مناسب للعمر",
      flexibility: "الجدولة المرنة",
      flexibilityDesc: "خيارات صيانة مخصصة لتلبية احتياجات العائلات المختلفة",
      communication: "التواصل مع الوالدين",
      communicationDesc: "تحديثات دورية وتواصل مفتوح مع الوالدين",
      activities: "الأنشطة الغنية",
      activitiesDesc:
        "أنشطة عديدة متضمنة الفنون والحرف، الموسيقى، اللعب في الهواء الطلق، الألعاب التعليمية، والمزيد.",
      qualifiedStaff: "موظفون مؤهلون",
      qualifiedStaffDesc:
        "موظفونا ذوو الخبرة والمؤهلون متحمسون لتطوير الطفولة المبكرة وملتزمون بتقديم أفضل رعاية لطفلك.",
      safeEnvironment: "بيئة آمنة",
      safeEnvironmentDesc: "بيئة آمنة مع مراقبة 24/7 وفريق مهني منظم",
      healthyMeals: "وجبات صحية",
      healthyMealsDesc: "وجبات متوازنة ووجبات خفيفة تحضيرت بالمكونات الطازجة",
      learningProgram: "برنامج تعليمي",
      learningProgramDesc: "برنامج تعليمي شامل يصمم لتطوير الطفولة المبكرة",
      flexibleHours: "ساعات مرنة",
      flexibleHoursDesc: "جداول قابلة للتخصيص تناسب احتياجات العائلات المختلفة",
      parentInvolvement: "تشارك الوالدين",
      parentInvolvementDesc: "تحديثات دورية وتواصل مفتوح مع الوالدين",
    },

    // Additional Services
    additionalServices: {
      title: "خدمات إضافية",
      subtitle: "عزز تجربة طفلك مع خدماتنا الاختيارية",
      transportation: "النقل",
      transportationDesc: "نقل آمن وموثوق من وإلى المدرسة أو المنزل",
      extraActivities: "أنشطة إضافية",
      extraActivitiesDesc:
        "دروس متخصصة في الموسيقى أو الفن أو الرياضة أو اللغات",
      specialDiet: "نظام غذائي خاص",
      specialDietDesc: "تلبية المتطلبات الغذائية الخاصة",
      tutoring: "دروس خصوصية",
      tutoringDesc: "دروس خصوصية فردية أو جماعية صغيرة",
    },

    // Service Features Lists
    serviceFeaturesList: {
      daycare: [
        "رعاية نهارية كاملة من الساعة 7:00 صباحاً حتى 6:30",
        "أنشطة وتعلم مناسب للعمر",
        "وجبات خفيفة وصحية مشمولة",
        "تقارير تقدم منتظمة",
        "تطبيق تواصل مع أولياء الأمور",
      ],
      afterSchool: [
        "استلام بعد المدرسة من المدارس المحلية",
        "مساعدة ودعم في الواجبات المنزلية",
        "أنشطة ترفيهية وألعاب",
        "وجبة خفيفة بعد الظهر",
        "أوقات استلام مرنة",
      ],
      holiday: [
        "برامج كاملة خلال العطل المدرسية",
        "أنشطة ذات مواضيع وفعاليات خاصة",
        "رحلات وزيارات ميدانية",
        "فن، أشغال يدوية ومشاريع إبداعية",
        "أنشطة بدنية ورياضة",
      ],
      flexible: [
        "جداول مرنة وقابلة للتخصيص",
        "رعاية عند الحاجة",
        "خيارات دوام جزئي متاحة",
        "رعاية طارئة",
        "رعاية في عطلة نهاية الأسبوع (حسب الترتيب)",
      ],
    },

    // Common CTAs and Section Headings
    cta: {
      readyToGetStarted: "هل أنت مستعد للبدء؟",
      contactToLearnMore:
        "تواصل معنا لمعرفة المزيد عن خدماتنا أو لتحديد موعد زيارة.",
      registerNow: "سجل الآن",
      ourCoreServices: "خدماتنا الأساسية",
      whyChooseOurServices: "لماذا تختار خدماتنا؟",
      moreThanChildcare:
        "نحن نقدم أكثر من مجرد رعاية أطفال – نحن نخلق بيئة ينمو فيها الأطفال",
      additionalServices: "خدمات إضافية",
      additionalServicesSubtitle: "عزز تجربة طفلك مع خدماتنا الاختيارية",
      whatsIncluded: "ما الذي يتضمنه:",
      meetOurTeam:
        "تعرف على فريقنا المتخصص في رعاية الطفولة المبكرة والمتحمس لتنمية طفلك.",
      yearsOfExperience: "سنوات الخبرة",
      happyChildren: "أطفال سعداء",
      qualifiedStaff: "موظفون مؤهلون",
      parentSatisfaction: "رضا الوالدين",
      parentOf: "ولي أمر",
      safeTransport: "نقل آمن وموثوق من وإلى المدرسة أو المنزل",
      extraActivities: "دروس متخصصة في الموسيقى أو الفن أو الرياضة أو اللغات",
      specialDiet: "تلبية المتطلبات الغذائية الخاصة",
      tutoring: "دروس خصوصية فردية أو جماعية صغيرة",
      fullDayCare: "رعاية نهارية كاملة من 7:00 صباحاً حتى 6:30",
      ageAppropriate: "أنشطة وتعلم مناسب للعمر",
      healthyMealsIncluded: "وجبات خفيفة وصحية مشمولة",
      progressReports: "تقارير تقدم منتظمة",
      parentApp: "تطبيق تواصل مع أولياء الأمور",
      afterSchoolPickup: "استلام بعد المدرسة من المدارس المحلية",
      homeworkSupport: "مساعدة ودعم في الواجبات المنزلية",
      recreation: "أنشطة ترفيهية وألعاب",
      afternoonSnack: "وجبة خفيفة بعد الظهر",
      flexiblePickup: "أوقات استلام مرنة",
      holidayPrograms: "برامج كاملة خلال العطل المدرسية",
      themedActivities: "أنشطة ذات مواضيع وفعاليات خاصة",
      fieldTrips: "رحلات وزيارات ميدانية",
      artsCrafts: "فن، أشغال يدوية ومشاريع إبداعية",
      physicalActivities: "أنشطة بدنية ورياضة",
      customizableSchedules: "جداول مرنة وقابلة للتخصيص",
      dropInCare: "رعاية عند الحاجة",
      partTime: "خيارات دوام جزئي متاحة",
      emergencyCare: "رعاية طارئة",
      weekendCare: "رعاية في عطلة نهاية الأسبوع (حسب الترتيب)",
    },

    // Daily Routine CTA and Features
    dailyRoutineCta: {
      joinAdventures: "هل أنت مستعد للانضمام إلى مغامراتنا اليومية؟",
      experienceText:
        "اختبر روتيننا اليومي المنظم والمرن الذي يعزز التعلم والإبداع والمرح لطفلك.",
      flexibleScheduleTitle: "جدول مرن",
      flexibleScheduleDesc:
        "ندرك أن لكل عائلة احتياجات مختلفة. يتيح لك جدولنا المرن اختيار الأوقات الأنسب لعائلتك.",
      fullDayCare: "رعاية نهارية كاملة (7:00 - 18:30)",
      halfDayOptions: "خيارات نصف يوم متاحة",
      dropInCare: "رعاية عند الحاجة",
      specialActivitiesTitle: "أنشطة خاصة",
      specialActivitiesDesc:
        "بالإضافة إلى روتيننا اليومي، نقدم أنشطة وفعاليات خاصة طوال الأسبوع.",
      artWorkshops: "ورش عمل فنية أيام الثلاثاء",
      musicClasses: "دروس موسيقى أيام الأربعاء",
      gardenTime: "وقت الحديقة أيام الخميس",
      fridayFun: "أنشطة يوم المرح يوم الجمعة",
      scheduleVisit: "تخطيط زيارة",
      contactUs: "اتصل بنا",
    },

    // Transportation Policy page
    transportationPolicy: {
      title: "سياسة النقل: خدمة التوصيل بالحافلة",
      subtitle: "خدمة نقل آمنة وموثوقة للأطفال في سن المدرسة",
      intro:
        "في Mini Avonturiers، نقدم خدمة توصيل آمنة وموثوقة وصديقة للأطفال للأطفال في سن المدرسة باستخدام سياراتنا المميزة. هذه الخدمة متاحة للأطفال المشاركين في برنامج الرعاية بعد المدرسة (BSO) وتعزز استمرارية الرعاية وتوفر الراحة للآباء.",
      intro2:
        "يتطلب نقل الأطفال الصغار إعدادًا دقيقًا، واتفاقيات واضحة، وانتباهًا دائمًا للسلامة والراحة والمشاركة. لهذا السبب وضعنا سياسة نقل واضحة تلبي جميع المتطلبات القانونية وتركز على رفاهية الطفل أثناء النقل.",

      section1: {
        title: "حافلة Mini Adventurer: آمنة ومعروفة ومألوفة",
        description: "تم تجهيز سياراتنا بـ:",
        features: [
          "هوية تجارية مميزة (شعار واسم Mini Adventurers)، حتى يعرف الأطفال والمدارس من سيقوم بالتوصيل.",
          "مقاعد أمان وأحزمة تفي بأحدث متطلبات السلامة.",
          "مركبات معتمدة تخضع لفحص فني سنوي.",
          "أنظمة ملاحة ومساعدة السائق لضمان نقل آمن وفعال.",
        ],
        note: "أثناء النقل، يتم دائمًا الالتزام باللوائح المعمول بها لنقل الأطفال (وفقًا لقانون نقل الركاب لعام 2000 وإرشادات GGD).",
      },

      section2: {
        title: "سائقون مؤهلون بنهج يركز على الطفل",
        description:
          "سائقونا ليسوا مجرد ناقلين – إنهم أول نقطة اتصال بعد المدرسة ويلعبون دورًا مهمًا في الانتقال من المدرسة إلى رعاية ما بعد المدرسة. لهذا السبب نضع معايير عالية:",
        requirements: [
          "رخصة قيادة سارية المفعول من الفئة B وشهادة حسن السيرة والسلوك (VOG).",
          "تدريب أساسي في سلامة الأطفال وآداب التعامل التربوي.",
          "تعليمات حول الصعود الآمن، وإجراء المحادثات، والحفاظ على الهدوء أثناء الانتقال.",
          "القدرة على التعرف على علامات التوتر أو التعب لدى الأطفال.",
        ],
        note: "يتمتع سائقونا بسلوك هادئ وصديق للأطفال، ويبنون بمرور الوقت علاقة ثقة وتعرف مع الأطفال.",
      },

      section3: {
        title: "الإرشاد والتسليم في الموقع",
        arrival: {
          title: "عند الوصول إلى المدرسة:",
          points: [
            "يبلغ السائق المدرسة وينتظر في المكان المتفق عليه.",
            "يتم التحقق من الأطفال مقابل القائمة.",
            "يتم التواصل مع موظفي المدرسة في حال وجود ظروف خاصة (مثل مرض طفل أو حدوث شيء ما).",
          ],
        },
        shelter: {
          title: "عند الوصول إلى مركز الرعاية:",
          points: [
            "يسلم السائق الأطفال شخصيًا إلى أحد موظفي الرعاية.",
            "يتم تسجيل وقت الوصول في النظام.",
            "يتم مشاركة أي ملاحظات خاصة خلال الرحلة على الفور.",
          ],
        },
        note: "يضمن هذا التسليم الاستمرارية والسلامة ونقل المعلومات بين المدرسة والنقل والرعاية.",
      },

      section4: {
        title: "الاتصال الفوري مع الوالدين",
        description:
          "يتم إعلام الوالدين بمواعيد النقل من خلال تطبيق خاص. حيث يتلقون:",
        features: [
          "إشعارات فورية عند المغادرة والوصول.",
          "إمكانية إرسال الأسئلة أو الإلغاءات عبر التطبيق.",
          "الاتصال المباشر مع مركز الرعاية في حال حدوث تأخيرات أو تغييرات.",
        ],
        note: "بهذه الطريقة نخلق الشفافية والثقة، ويمكن للوالدين أن يطمئنوا على نقل أطفالهم.",
      },

      section5: {
        title: "مسارات وخطط جمع مرنة",
        description: "مساراتنا فعالة وصديقة للأطفال:",
        features: [
          "أوقات سفر قصيرة، مع تفضيل الطرق القريبة من موقع الاستقبال.",
          "مجموعات صغيرة في كل رحلة، لضمان الهدوء داخل الحافلة.",
          "تعديلات حسب المواسم أو الازدحام المروري إذا لزم الأمر.",
          "جداول ثابتة مع فترات زمنية واضحة لكل موقع مدرسي.",
        ],
        note: "عند التسجيل، نتفق مع الوالدين على المدرسة التي سيتم استلام الطفل منها، وفي أي وقت وأي أيام.",
      },

      section6: {
        title: "الطوارئ والخطط البديلة",
        description: "نطبق بروتوكولًا صارمًا في الحالات الاستثنائية:",
        features: [
          "في حالة التأخير، يتم إبلاغ مركز الرعاية والوالدين على الفور.",
          "إذا لم يظهر الطفل في الموقع المتفق عليه، يتم الاتصال بالمدرسة والوالدين خلال دقيقتين.",
          "السائق على اتصال دائم بمركز الرعاية ويحمل قائمة بأرقام الطوارئ.",
        ],
        note: "تضمن خدمة الطوارئ لدينا وجود شخص متاح دائمًا للمساعدة في حالة حدوث عطل أو حادث أو ظرف غير متوقع.",
      },
    },
    footer: {
      connectWith: "تواصل مع ميني آفونتوريرس",
      email: "info@mini-avonturiers.nl",
      aboutUs: "معلومات عنا",
      services: "الخدمات",
      dailyRoutine: "الروتين اليومي",
      registerNow: "سجل الآن",
      address: "العنوان",
      addressText: "ليندهوف 3،",
      addressText2: "3442 جي تي ووردن",
      telephone: "الهاتف",
      phoneNumber: "+31 6 16504171",
      copyright: "© 2025 ميني آفونتوريرس. جميع الحقوق محفوظة.",
    },
  },
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [language, setLanguage] = useState<Language>("nl"); // Default to Dutch

  // Helper to resolve nested keys like 'home.welcome'
  const resolveKey = (obj: any, path: string[]): any => {
    return path.reduce(
      (acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined),
      obj
    );
  };

  const t = <T = any,>(key: string): T => {
    const path = key.split(".");
    const value = resolveKey(translations[language], path);
    return (value !== undefined ? value : key) as T;
  };

  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
